import React, { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Listbox, Transition } from "@headlessui/react";
import {
  APIProvider,
  Map as GoogleMap,
  AdvancedMarker,
  InfoWindow,
  useMap,
} from "@vis.gl/react-google-maps";
import UserService from "rc-app/services/UserService";
import { guidedTourEnabled } from "rc-app/utils/featureFlags";
import {
  getAgencyShareLinks,
  getAgencyShareLinkClicks,
  getMyShareLinkStats,
  getUserRoleInAgency,
} from "rc-app/api/shareLinkActionsApi";
import { getFullProjectData } from "rc-app/api/projectActionsApi";
import { getAgentProfile } from "rc-app/api/agentActionsApi";
import {
  getClientAdminByEmailId,
  getClientManagerByEmailId,
} from "rc-app/api/systemBusinessAdminActionApi";
import { getMetaModels } from "rc-app/api/metaModelsActionsApi";
import { getMatterports } from "rc-app/api/matterportActionsApi";
import { getVrSessionList } from "rc-app/api/vrSessionActionsApi.js";
import { getAllAgent } from "rc-app/api/agentActionsApi";
import {
  getBusinessAdminList,
  getSystemAdminList,
} from "rc-app/api/systemBusinessAdminActionApi";
import { getCustomerList } from "rc-app/api/customerActionsApi";
import { listEmbedKeys } from "rc-app/api/embedActionsApi";
import { summarizeEmbeds, embedSectionState, type EmbedKeyInfo } from "rc-app/utils/embedAnalytics";

const joinName = (first?: string, last?: string): string | undefined => {
  const s = [first, last].filter(Boolean).join(" ").trim();
  return s || undefined;
};

const pickMobile = (rec: any): string | undefined => {
  const m =
    rec?.mobile_number ??
    rec?.mobile ??
    rec?.phone ??
    rec?.phone_number ??
    undefined;
  return m ? String(m) : undefined;
};

type DateRangeKey = "lifetime" | "7d" | "15d" | "30d" | "60d";

const DATE_RANGE_OPTIONS: { key: DateRangeKey; label: string }[] = [
  { key: "lifetime", label: "Lifetime" },
  { key: "7d", label: "Last 7 days" },
  { key: "15d", label: "Last 15 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "60d", label: "Last 60 days" },
];

const computeDateRange = (
  key: DateRangeKey,
): { startDate: string | null; endDate: string | null } => {
  if (key === "lifetime") return { startDate: null, endDate: null };
  const days = parseInt(key, 10);
  const now = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  const toIso = (d: Date) => d.toISOString().split("T")[0]; // YYYY-MM-DD
  return { startDate: toIso(start), endDate: toIso(now) };
};

const SESSIONS_PAGE_SIZE = 10;

const DateRangePicker: React.FC<{
  value: DateRangeKey;
  onChange: (next: DateRangeKey) => void;
}> = ({ value, onChange }) => (
  <div className="inline-flex rounded-lg bg-[#15333D] border border-[#2C4D5A] p-1 gap-0.5">
    {DATE_RANGE_OPTIONS.map((opt) => {
      const active = value === opt.key;
      return (
        <button
          key={opt.key}
          type="button"
          onClick={() => onChange(opt.key)}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            active
              ? "bg-[#008564] text-white shadow-sm"
              : "text-[#889DB2] hover:text-white hover:bg-white/5"
          }`}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

type KpiCardProps = {
  icon: React.ReactNode;
  iconBg: string;
  accent: string;
  value?: React.ReactNode;
  label: string;
  share?: { percent: number; color: string; label?: string };
  subtext?: { text: React.ReactNode; color: string };
  emptyState?: { title: string; subtitle: string };
  // "up" = green pulsing arrow next to the value, gentle ambient glow on
  // hover → conveys "live / moving". "down" = static rose arrow, slight
  // ambient red tint → conveys "stagnant / negative". Used for the
  // Active / Dormant Agents & Leads cards.
  trend?: "up" | "down";
};

// ── By User Profile / By City panels (Project Analytics) ──────────────
type Contact = { name: string; mobile?: string };

// Max users shown when a role row is expanded. Beyond this, a footer
// "+ N more · View all" link offers access to the full list (modal TBD).
const USER_EXPAND_LIMIT = 20;

const ROLE_DEFS: { key: string; label: string; color: string }[] = [
  { key: "business_admin", label: "Business Admin", color: "#C4B5FD" },
  { key: "system_admin", label: "System Admin", color: "#FB7185" },
  { key: "agent", label: "Agent", color: "#38BDF8" },
  { key: "customer", label: "Lead", color: "#34D399" },
  { key: "raw_visitor", label: "Raw Visitor", color: "#9CA3AF" },
];

const roleKey = (s: any): string => {
  if (s?.aba_id?.length) return "business_admin";
  if (s?.asa_id?.length) return "system_admin";
  if (s?.agent_id?.length) return "agent";
  if (s?.customer_id?.length) return "customer";
  return "raw_visitor";
};

// Dot colour for the Platform scope chips (mirrors ROLE_DEFS + Share chips).
const platformChipDot = (k: string): string =>
  k === "you"
    ? "#C4B5FD"
    : k === "system_admin"
      ? "#FB7185"
      : k === "agent"
        ? "#38BDF8"
        : k === "customer"
          ? "#34D399"
          : "#9CA3AF"; // everyone / fallback

// Normalize an IP for visitor de-duplication.
// IPv4 → returned as-is (one public IP per network — already stable).
// IPv6 → collapsed to its /64 prefix (first 4 hextets). Mobile carriers
// (Jio etc.) rotate the lower 64 host bits via privacy extensions
// (RFC 4941), so the SAME device/network presents many full addresses
// over a day; the /64 prefix stays constant per network, so grouping on
// it counts one visitor per network instead of over-counting rotations.
// Different networks still get different /64s → distinct visitors.
const normalizeIpForDedup = (raw: string): string => {
  const ip = String(raw || "").trim();
  if (!ip || !ip.includes(":")) return ip; // empty or IPv4 → as-is
  const hextets = ip.split(":").filter((h) => h !== ""); // handles "::"
  return hextets.slice(0, 4).join(":"); // /64 prefix
};

// Distinct-visitor key for "Visitors" counts. Returns a role-prefixed
// id so the same string id can't collide across roles, OR null for
// raw visitors (anonymous — not counted toward visitor totals). Used
// by Session Metrics, City breakdown, etc. so all surfaces sum to the
// same number as the By User Profile panel.
const identifiedVisitorKey = (s: any): string | null => {
  if (s?.aba_id?.length) return `ba:${String(s.aba_id[0])}`;
  if (s?.asa_id?.length) return `sa:${String(s.asa_id[0])}`;
  if (s?.agent_id?.length) return `ag:${String(s.agent_id[0])}`;
  if (s?.customer_id) return `c:${String(s.customer_id)}`;
  return null;
};

const visitorKey = (s: any, role: string): string => {
  if (role === "business_admin") return String(s?.aba_id?.[0] || s?.user_id || s?._id);
  if (role === "system_admin") return String(s?.asa_id?.[0] || s?.user_id || s?._id);
  if (role === "agent") return String(s?.agent_id?.[0] || s?.user_id || s?._id);
  if (role === "customer") return String(s?.customer_id?.[0] || s?.user_id || s?._id);
  return String(s?.user_id || s?._id);
};

const ByUserProfilePanel: React.FC<{
  sessions: any[];
  contactsByRole: Record<string, Map<string, Contact>>;
  viewerRole: "aba" | "asa";
  selfId: string;
  // Active platform scope chip — when a specific role/You is selected, the
  // panel shows ONLY that row (not the full breakdown). "everyone"/undefined
  // = full breakdown.
  scope?: string;
}> = ({ sessions, contactsByRole, viewerRole, selfId, scope }) => {
  // Only one role row expanded at a time — opening a new one auto-
  // collapses any other.
  const [expanded, setExpanded] = React.useState<string | null>(null);

  type UserStat = { id: string; visits: number; timeSpent: number };
  type Group = {
    key: string;
    label: string;
    color: string;
    visitors: number;
    views: number;
    timeSpent: number;
    users: Map<string, UserStat>;
  };

  const groups = React.useMemo<Group[]>(() => {
    // Tier rows visible per viewer role (locked matrix):
    //   ABA → System Admin · Agents · Lead · Raw Visitor   (+ My Views)
    //   ASA → Agents · Lead                                (+ My Views)
    // "Business Admin" folds into My Views; ASA sees no System Admin / Raw
    // Visitor rows.
    const visibleTierKeys =
      viewerRole === "asa"
        ? ["agent", "customer"]
        : ["system_admin", "agent", "customer"];

    // "My Views" = the viewer's own sessions (their own role id == selfId).
    const mine: Group = {
      key: "mine",
      label: "My Views",
      // My Views takes the viewer's own role color (their own role tier is
      // hidden in their panel, so there's no collision): ASA → System Admin
      // rose, ABA → Business Admin violet.
      color: viewerRole === "asa" ? "#FB7185" : "#C4B5FD",
      visitors: 0,
      views: 0,
      timeSpent: 0,
      users: new Map(),
    };

    const buckets: Record<string, Group> = {};
    for (const def of ROLE_DEFS) {
      if (!visibleTierKeys.includes(def.key)) continue;
      buckets[def.key] = {
        key: def.key,
        label: def.label,
        color: def.color,
        visitors: 0,
        views: 0,
        timeSpent: 0,
        users: new Map(),
      };
    }

    for (const s of sessions) {
      const dur = typeof s?.total_duration === "number" ? s.total_duration : 0;
      // My Views first — matched on the viewer's own role id. Disjoint from the
      // tier buckets below: a self session's roleKey is business_admin (ABA) or
      // system_admin (ASA), neither of which is a visible tier for that viewer.
      const selfArr = viewerRole === "asa" ? s?.asa_id : s?.aba_id;
      const isMine = selfId
        ? Array.isArray(selfArr)
          ? selfArr.some((x: any) => String(x) === selfId)
          : String(selfArr) === selfId
        : false;
      if (isMine) {
        mine.views += 1;
        mine.timeSpent += dur;
        continue;
      }
      const rk = roleKey(s);
      const g = buckets[rk];
      if (!g) continue;
      g.views += 1;
      g.timeSpent += dur;
      // Raw visitors are anonymous — don't synthesize a "visitor" id
      // from user_id/_id, otherwise every session looks like a unique
      // person. Leave the visitors column blank for this row.
      if (rk === "raw_visitor") continue;
      const vk = visitorKey(s, rk);
      const existing = g.users.get(vk) || { id: vk, visits: 0, timeSpent: 0 };
      existing.visits += 1;
      existing.timeSpent += dur;
      g.users.set(vk, existing);
    }
    for (const g of Object.values(buckets)) g.visitors = g.users.size;
    mine.visitors = mine.views > 0 ? 1 : 0;
    // My Views first, then the visible tiers in ROLE_DEFS order — shown even
    // with 0 data so the panel structure stays consistent.
    const full = [
      mine,
      ...ROLE_DEFS.filter((d) => buckets[d.key]).map((d) => buckets[d.key]),
    ];
    // When a specific scope chip is active, collapse the panel to just that
    // entity: "you" → My Views; a role → that role's row only.
    if (!scope || scope === "everyone") return full;
    if (scope === "you") return full.filter((g) => g.key === "mine");
    return full.filter((g) => g.key === scope);
  }, [sessions, viewerRole, selfId, scope]);

  const toggle = (key: string) =>
    setExpanded((prev) => (prev === key ? null : key));

  return (
    <div className="rounded-xl bg-[#15333D] border border-[#2C4D5A] p-4 h-full flex flex-col">
      <div className="flex items-baseline justify-between mb-3 gap-2">
        <h3 className="text-[10px] font-semibold text-[#889DB2] uppercase tracking-widest">
          By User Profile
        </h3>
        <span className="text-[10px] text-[#5A7C8C]">
          {sessions.length.toLocaleString()} views
        </span>
      </div>
      {groups.length === 0 ? (
        <p className="text-[#889DB2] text-sm text-center py-6">No data</p>
      ) : (
        <table className="w-full text-[12px] border-separate border-spacing-0">
          <thead>
            <tr className="text-[#889DB2] text-[10px] uppercase tracking-wider">
              <th className="text-left py-2 px-2 font-medium">Role</th>
              <th className="text-right py-2 px-2 font-medium">Visitors</th>
              <th className="text-right py-2 px-2 font-medium">Views</th>
              <th className="text-right py-2 px-2 font-medium">Time Spent</th>
              <th className="text-right py-2 px-2 font-medium">Avg</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => {
              const isExpandable = g.key !== "raw_visitor" && g.key !== "mine";
              const isOpen = isExpandable && expanded === g.key;
              const userList = Array.from(g.users.values()).sort(
                (a, b) => b.visits - a.visits,
              );
              const contactMap = contactsByRole[g.key];
              return (
                <Fragment key={g.key}>
                  <tr
                    onClick={() => isExpandable && toggle(g.key)}
                    className={
                      isExpandable
                        ? "cursor-pointer hover:bg-white/[0.02]"
                        : "cursor-default"
                    }
                  >
                    <td className="py-2 px-2 border-t border-[#2C4D5A]">
                      <span className="inline-flex items-center gap-2">
                        {isExpandable ? (
                          <svg
                            className={`w-3 h-3 text-[#889DB2] transition-transform ${
                              isOpen ? "rotate-90" : ""
                            }`}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                        ) : (
                          <span className="w-3 h-3 inline-block" />
                        )}
                        <Pill color={g.color}>{g.label}</Pill>
                      </span>
                    </td>
                    <td className="py-2 px-2 border-t border-[#2C4D5A] text-right text-white font-medium">
                      {g.key === "raw_visitor" ? (
                        <span className="text-[#5A7C8C]">—</span>
                      ) : (
                        g.visitors.toLocaleString()
                      )}
                    </td>
                    <td className="py-2 px-2 border-t border-[#2C4D5A] text-right text-white font-medium">
                      {g.views.toLocaleString()}
                    </td>
                    <td className="py-2 px-2 border-t border-[#2C4D5A] text-right text-white font-medium whitespace-nowrap">
                      {formatDuration(g.timeSpent)}
                    </td>
                    <td className="py-2 px-2 border-t border-[#2C4D5A] text-right text-white font-medium whitespace-nowrap">
                      {g.views > 0
                        ? formatDuration(Math.round(g.timeSpent / g.views))
                        : "—"}
                    </td>
                  </tr>
                  {isOpen &&
                    userList.slice(0, USER_EXPAND_LIMIT).map((u) => {
                      const contact = contactMap?.get(u.id);
                      const name =
                        contact?.name ||
                        (g.key === "raw_visitor" && u.id.includes("@")
                          ? u.id
                          : "Anonymous");
                      const mobile = contact?.mobile;
                      return (
                        <tr key={u.id} className="bg-[#0E2530]/40">
                          <td className="py-2 pl-10 pr-2 border-t border-[#2C4D5A]/60">
                            <div className="flex flex-col">
                              <span className="text-[#E5E7EB] truncate max-w-[220px]">{name}</span>
                              {mobile && (
                                <span className="text-[10px] text-[#5A7C8C]">{mobile}</span>
                              )}
                            </div>
                          </td>
                          <td className="border-t border-[#2C4D5A]/60" />
                          <td className="py-2 px-2 border-t border-[#2C4D5A]/60 text-right text-[#E5E7EB]">
                            {u.visits.toLocaleString()}
                          </td>
                          <td className="py-2 px-2 border-t border-[#2C4D5A]/60 text-right text-[#E5E7EB] whitespace-nowrap">
                            {formatDuration(u.timeSpent)}
                          </td>
                          <td className="py-2 px-2 border-t border-[#2C4D5A]/60 text-right text-[#E5E7EB] whitespace-nowrap">
                            {u.visits > 0
                              ? formatDuration(Math.round(u.timeSpent / u.visits))
                              : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  {isOpen && userList.length > USER_EXPAND_LIMIT && (
                    <tr className="bg-[#0E2530]/40">
                      <td
                        colSpan={5}
                        className="py-2 pl-10 pr-2 border-t border-[#2C4D5A]/60"
                      >
                        <button
                          type="button"
                          onClick={() => {
                            // TODO: open dedicated <ViewAllVisitorsModal />
                            // with sortable + searchable + paginated table.
                            console.log(
                              "View all clicked for role:",
                              g.key,
                              "userList length:",
                              userList.length,
                            );
                          }}
                          className="text-[11px] font-medium text-[#34D399] hover:underline"
                        >
                          + {(userList.length - USER_EXPAND_LIMIT).toLocaleString()} more · View all {g.label.toLowerCase()}s
                        </button>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

// A single plottable point on the Locations map.
// - source="gps": exactly one session with verified lat/lng. Pinned at the
//   exact GPS coords; popup shows session-level detail.
// - source="city": N legacy / GPS-denied sessions aggregated to a city
//   center via forward-geocoding the city name. Preserves backward
//   compatibility for sessions captured before lat/lng was wired up, and
//   for visitors who declined the GPS prompt.
type SessionPoint = {
  key: string;
  pos: { lat: number; lng: number };
  source: "gps" | "city";
  city: string;
  region: string | null;
  country: string | null;
  sessions: any[];
};

// Module-level cache for lazy neighborhood lookups. Rounded to 4 decimals
// (~11m) so multiple sessions inside the same building share one entry.
const NEIGHBORHOOD_CACHE = new Map<string, string | null>();
const neighborhoodCacheKey = (lat: number, lng: number) =>
  `${lat.toFixed(4)},${lng.toFixed(4)}`;

const fetchNeighborhood = async (
  lat: number,
  lng: number,
  apiKey: string,
): Promise<string | null> => {
  const cacheKey = neighborhoodCacheKey(lat, lng);
  if (NEIGHBORHOOD_CACHE.has(cacheKey)) return NEIGHBORHOOD_CACHE.get(cacheKey)!;
  try {
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`;
    const res = await fetch(url);
    const data = await res.json();
    if (data?.status !== "OK") {
      NEIGHBORHOOD_CACHE.set(cacheKey, null);
      return null;
    }
    // Prefer sublocality_level_1 ("Manikonda"); fall back to neighborhood
    // or sublocality_level_2 if level_1 isn't present.
    const wanted = ["sublocality_level_1", "neighborhood", "sublocality_level_2"];
    for (const want of wanted) {
      for (const result of data.results || []) {
        for (const comp of result.address_components || []) {
          if (comp.types?.includes(want)) {
            const val = comp.long_name || null;
            if (val) {
              NEIGHBORHOOD_CACHE.set(cacheKey, val);
              return val;
            }
          }
        }
      }
    }
    NEIGHBORHOOD_CACHE.set(cacheKey, null);
    return null;
  } catch {
    NEIGHBORHOOD_CACHE.set(cacheKey, null);
    return null;
  }
};

// Pure side-effect component: when an active marker is set, smoothly
// pan to it and zoom in to city level. Sits inside <GoogleMap> so it
// has access to the map instance via useMap().
const MapZoomController: React.FC<{
  activePos: { lat: number; lng: number } | null;
}> = ({ activePos }) => {
  const map = useMap();
  React.useEffect(() => {
    if (!map || !activePos) return;
    map.panTo(activePos);
    // Only zoom IN if the user is too zoomed out to see the pin in context.
    // Never zoom out — they may have intentionally drilled in to street level
    // on a GPS pin and clicking shouldn't fight them.
    const currentZoom = map.getZoom();
    if (typeof currentZoom !== "number" || currentZoom < 11) {
      map.setZoom(11);
    }
  }, [map, activePos]);
  return null;
};

// Reports the live zoom level back to the parent so we can scale the
// proximity-clustering threshold with zoom.
const ZoomTracker: React.FC<{ onZoom: (z: number) => void }> = ({ onZoom }) => {
  const map = useMap();
  React.useEffect(() => {
    if (!map) return undefined;
    const update = () => {
      const z = map.getZoom();
      if (typeof z === "number") onZoom(z);
    };
    update();
    const listener = map.addListener("zoom_changed", update);
    return () => (listener as any)?.remove?.();
  }, [map, onZoom]);
  return null;
};

// Cluster-click handler: when a multi-city pin is tapped, smoothly zoom
// into the cluster's center so it breaks apart on the next render pass.
const ClusterZoomController: React.FC<{
  target: { lat: number; lng: number; zoom: number } | null;
  onConsumed: () => void;
}> = ({ target, onConsumed }) => {
  const map = useMap();
  React.useEffect(() => {
    if (!map || !target) return;
    map.panTo({ lat: target.lat, lng: target.lng });
    map.setZoom(target.zoom);
    onConsumed();
  }, [map, target, onConsumed]);
  return null;
};

// Auto-fit the viewport to wherever the data actually lives. India-only
// data stays India-zoomed; data spanning India + Europe + US + UAE
// auto-zooms out to a world view. Re-fires only when the underlying city
// set changes — never fights the user's manual zoom/pan.
const BoundsFitter: React.FC<{
  markers: Array<{ pos: { lat: number; lng: number } }>;
}> = ({ markers }) => {
  const map = useMap();
  const lastSigRef = React.useRef<string>("");
  React.useEffect(() => {
    if (!map || !markers.length) return;
    const sig = markers
      .map((m) => `${m.pos.lat.toFixed(3)},${m.pos.lng.toFixed(3)}`)
      .sort()
      .join("|");
    if (sig === lastSigRef.current) return;
    lastSigRef.current = sig;
    if (markers.length === 1) {
      map.setCenter(markers[0].pos);
      map.setZoom(10);
      return;
    }
    const g = (window as any).google?.maps;
    if (!g?.LatLngBounds) return;
    const bounds = new g.LatLngBounds();
    for (const m of markers) bounds.extend(m.pos);
    map.fitBounds(bounds, 60);
  }, [map, markers]);
  return null;
};

const LocationsMap: React.FC<{
  sessions: any[];
  totalViews: number;
  projectNameById: Map<string, string>;
  businessAdminById: Map<string, string>;
  systemAdminById: Map<string, string>;
  agentNameById: Map<string, string>;
}> = ({
  sessions,
  totalViews,
  projectNameById,
  businessAdminById,
  systemAdminById,
  agentNameById,
}) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [cityCoords, setCityCoords] = React.useState<
    Map<string, { lat: number; lng: number }>
  >(new Map());
  const [activeKey, setActiveKey] = React.useState<string | null>(null);
  const [neighborhood, setNeighborhood] = React.useState<
    { cacheKey: string; value: string | null; loading: boolean } | null
  >(null);

  const resolveUserName = React.useCallback(
    (s: any): string => {
      if (s?.aba_id?.length) {
        const name = businessAdminById.get(String(s.aba_id[0]));
        if (name) return name;
      }
      if (s?.asa_id?.length) {
        const name = systemAdminById.get(String(s.asa_id[0]));
        if (name) return name;
      }
      if (s?.agent_id?.length) {
        const name = agentNameById.get(String(s.agent_id[0]));
        if (name) return name;
      }
      return s?.user_id || "Anonymous";
    },
    [businessAdminById, systemAdminById, agentNameById],
  );

  // Split sessions: those with a valid (lat,lng) plot individually;
  // the rest fall back to city-level aggregation (preserves the old
  // behavior for legacy/GPS-denied sessions).
  const { gpsSessions, cityBuckets } = React.useMemo(() => {
    const gps: any[] = [];
    const buckets = new Map<string, any[]>();
    for (const s of sessions) {
      const lat = Number(s?.latitude);
      const lng = Number(s?.longitude);
      // Treat (0, 0) as "missing" — backend default placeholder, not a real
      // location (Gulf of Guinea is not where our visitors are).
      if (
        Number.isFinite(lat) &&
        Number.isFinite(lng) &&
        (lat !== 0 || lng !== 0)
      ) {
        gps.push(s);
        continue;
      }
      const city = String(s?.city || "").trim();
      if (!city || city === "Unknown") continue;
      const arr = buckets.get(city) || [];
      arr.push(s);
      buckets.set(city, arr);
    }
    return { gpsSessions: gps, cityBuckets: buckets };
  }, [sessions]);

  // Forward-geocode any city that doesn't yet have coords. Only fires for
  // cities that appear in the city-fallback bucket.
  React.useEffect(() => {
    if (!apiKey) return undefined;
    let cancelled = false;
    (async () => {
      const next = new Map(cityCoords);
      let changed = false;
      for (const city of cityBuckets.keys()) {
        if (next.has(city)) continue;
        try {
          const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
            city,
          )}&key=${apiKey}`;
          const res = await fetch(url);
          const data = await res.json();
          if (cancelled) return;
          const loc = data?.results?.[0]?.geometry?.location;
          if (data?.status === "OK" && loc) {
            next.set(city, { lat: loc.lat, lng: loc.lng });
            changed = true;
          }
        } catch {
          // ignore individual failures
        }
      }
      if (changed && !cancelled) setCityCoords(next);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityBuckets, apiKey]);

  // Merge GPS points + city-fallback points into a single flat list.
  const points = React.useMemo<SessionPoint[]>(() => {
    const out: SessionPoint[] = [];
    // Group GPS sessions by rounded coordinate (~4 decimals ≈ 11m) so repeat
    // views from the SAME spot collapse into ONE pin (holding N sessions)
    // instead of stacking invisibly on the same pixel. The pin then shows the
    // count as its badge and its popup aggregates views + cumulative time.
    const gpsGroups = new Map<string, any[]>();
    for (const s of gpsSessions) {
      const lat = Number(s.latitude);
      const lng = Number(s.longitude);
      const gkey = `${lat.toFixed(4)},${lng.toFixed(4)}`;
      const arr = gpsGroups.get(gkey);
      if (arr) arr.push(s);
      else gpsGroups.set(gkey, [s]);
    }
    for (const [gkey, sList] of gpsGroups.entries()) {
      const first = sList[0];
      out.push({
        key: `gps-${gkey}`,
        pos: { lat: Number(first.latitude), lng: Number(first.longitude) },
        source: "gps",
        city: first?.city || "",
        region: first?.region || null,
        country: first?.country || null,
        sessions: sList,
      });
    }
    for (const [city, sList] of cityBuckets.entries()) {
      const pos = cityCoords.get(city);
      if (!pos) continue;
      const first = sList[0];
      out.push({
        key: `city-${city}`,
        pos,
        source: "city",
        city,
        region: first?.region || null,
        country: first?.country || null,
        sessions: sList,
      });
    }
    return out;
  }, [gpsSessions, cityBuckets, cityCoords]);

  // Zoom-aware proximity clustering. Threshold shrinks as the user zooms
  // in so pins that look "on top of each other" at a country view break
  // apart cleanly once you're at city level. At z>=14 we disable
  // clustering entirely so every pin is independently inspectable.
  const [zoom, setZoom] = React.useState(5);
  const [zoomTarget, setZoomTarget] = React.useState<
    { lat: number; lng: number; zoom: number } | null
  >(null);

  type Cluster = {
    key: string;
    pos: { lat: number; lng: number };
    members: SessionPoint[];
    sessions: any[];
    views: number;
    visitors: number;
    timeSpent: number;
  };
  const clusters = React.useMemo<Cluster[]>(() => {
    // ~80px-worth of degrees at the current zoom — empirical, gives a
    // reasonable click target without over-merging cities at metro view.
    const thresholdDeg = zoom >= 14 ? 0 : 80 / Math.pow(2, zoom);
    // Process larger points first so they anchor cluster center/key.
    const sorted = [...points].sort(
      (a, b) => b.sessions.length - a.sessions.length,
    );
    const out: Cluster[] = [];
    for (const item of sorted) {
      const target =
        thresholdDeg > 0
          ? out.find(
              (c) =>
                Math.abs(c.pos.lat - item.pos.lat) < thresholdDeg &&
                Math.abs(c.pos.lng - item.pos.lng) < thresholdDeg,
            )
          : undefined;
      if (target) {
        target.members.push(item);
        target.sessions.push(...item.sessions);
      } else {
        out.push({
          key: item.key,
          pos: item.pos,
          members: [item],
          sessions: [...item.sessions],
          views: 0,
          visitors: 0,
          timeSpent: 0,
        });
      }
    }
    // Compute aggregates after clustering — deduplicates visitors across
    // sessions in the same cluster.
    for (const c of out) {
      const visitorIds = new Set<string>();
      let timeSpent = 0;
      for (const s of c.sessions) {
        const vid = identifiedVisitorKey(s);
        if (vid) visitorIds.add(vid);
        if (typeof s?.total_duration === "number") timeSpent += s.total_duration;
      }
      c.views = c.sessions.length;
      c.visitors = visitorIds.size;
      c.timeSpent = timeSpent;
    }
    return out;
  }, [points, zoom]);

  // The active singleton — null when nothing is open, or when the active
  // point has been clustered with neighbors (zoom-out invalidates it).
  const activeCluster = React.useMemo(() => {
    if (!activeKey) return null;
    return (
      clusters.find(
        (c) => c.members.length === 1 && c.members[0].key === activeKey,
      ) || null
    );
  }, [activeKey, clusters]);

  // Lazy neighborhood fetch when a single GPS pin is opened. Skips for
  // city-fallback pins (the city itself is what we'd show — no point in
  // re-geocoding it).
  React.useEffect(() => {
    if (!apiKey || !activeCluster) {
      setNeighborhood(null);
      return undefined;
    }
    const m = activeCluster.members[0];
    if (m.source !== "gps") {
      setNeighborhood(null);
      return undefined;
    }
    const { lat, lng } = m.pos;
    const cacheKey = neighborhoodCacheKey(lat, lng);
    if (NEIGHBORHOOD_CACHE.has(cacheKey)) {
      setNeighborhood({
        cacheKey,
        value: NEIGHBORHOOD_CACHE.get(cacheKey)!,
        loading: false,
      });
      return undefined;
    }
    setNeighborhood({ cacheKey, value: null, loading: true });
    let cancelled = false;
    (async () => {
      const value = await fetchNeighborhood(lat, lng, apiKey);
      if (!cancelled) {
        setNeighborhood({ cacheKey, value, loading: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeCluster, apiKey]);

  if (!apiKey) {
    return (
      <div className="rounded-xl bg-[#15333D] border border-[#2C4D5A] p-4">
        <div className="flex items-baseline justify-between mb-3 gap-2">
          <h3 className="text-[10px] font-semibold text-[#889DB2] uppercase tracking-widest">
            By Location
          </h3>
        </div>
        <p className="text-[#889DB2] text-sm text-center py-10">
          Google Maps API key not configured.
        </p>
      </div>
    );
  }

  const totalPlotted = clusters.reduce((s, c) => s + c.views, 0);
  const gpsCount = gpsSessions.length;
  const cityCount = Array.from(cityBuckets.values()).reduce(
    (s, arr) => s + arr.length,
    0,
  );

  return (
    <div className="rounded-xl bg-[#15333D] border border-[#2C4D5A] p-4">
      <div className="flex items-baseline justify-between mb-3 gap-2">
        <h3 className="text-[10px] font-semibold text-[#889DB2] uppercase tracking-widest">
          By Location
        </h3>
        <span className="text-[10px] text-[#5A7C8C]">
          {(() => {
            const lead =
              totalPlotted < totalViews
                ? `${totalPlotted.toLocaleString()} / ${totalViews.toLocaleString()} views plotted`
                : `${totalPlotted.toLocaleString()} ${totalPlotted === 1 ? "view" : "views"}`;
            const gpsLabel = gpsCount > 0 ? `${gpsCount} GPS` : "";
            const cityLabel = cityCount > 0 ? `${cityCount} city-only` : "";
            const tail = [gpsLabel, cityLabel].filter(Boolean).join(" · ");
            return tail ? `${lead} · ${tail}` : lead;
          })()}
        </span>
      </div>
      <APIProvider apiKey={apiKey}>
        <div
          className="rounded-lg overflow-hidden border border-[#2C4D5A]"
          style={{ height: 380, width: "100%" }}
        >
          <GoogleMap
            mapId="analytics-v3-locations"
            defaultCenter={{ lat: 20.5937, lng: 78.9629 }}
            defaultZoom={5}
            gestureHandling="greedy"
            streetViewControl={false}
            mapTypeControl={false}
            fullscreenControl
            zoomControl
          >
            <ZoomTracker onZoom={setZoom} />
            <BoundsFitter markers={points} />
            <ClusterZoomController
              target={zoomTarget}
              onConsumed={() => setZoomTarget(null)}
            />
            {clusters.map((c) => {
              const isCluster = c.members.length > 1;
              const label =
                c.views >= 1000
                  ? `${(c.views / 1000).toFixed(1)}k`
                  : c.views.toLocaleString();
              if (isCluster) {
                return (
                  <AdvancedMarker
                    key={c.key}
                    position={c.pos}
                    onClick={() =>
                      setZoomTarget({
                        lat: c.pos.lat,
                        lng: c.pos.lng,
                        zoom: Math.min(zoom + 3, 14),
                      })
                    }
                  >
                    <div
                      title={`${c.views} session${
                        c.views === 1 ? "" : "s"
                      } in this area — click to zoom in`}
                      className="flex items-center justify-center rounded-full shadow-lg border-2 border-white transition-all cursor-pointer bg-[#008564] hover:scale-105"
                      style={{ minWidth: 40, height: 40, padding: "0 10px" }}
                    >
                      <span className="text-white text-[13px] font-bold tabular-nums">
                        {label}
                      </span>
                    </div>
                  </AdvancedMarker>
                );
              }
              const isActive = activeKey === c.key;
              const m = c.members[0];
              // Differentiate visually: GPS pins get a brighter cyan ring;
              // city-fallback pins keep the existing green. Lets curators
              // tell at a glance which pins are precisely located.
              const dotClass =
                m.source === "gps"
                  ? "bg-[#0EA5E9] border-white/95"
                  : "bg-[#008564] border-white/90";
              return (
                <AdvancedMarker
                  key={c.key}
                  position={c.pos}
                  onClick={() =>
                    setActiveKey((prev) => (prev === c.key ? null : c.key))
                  }
                >
                  <div
                    title={
                      m.source === "gps"
                        ? `${m.city || "Unknown"} · 1 session at GPS coords`
                        : `${m.city} · ${c.views} session${
                            c.views === 1 ? "" : "s"
                          } (city-level)`
                    }
                    className={`flex items-center justify-center rounded-full shadow-lg border-2 transition-all cursor-pointer ${dotClass} ${
                      isActive ? "scale-110" : "hover:scale-105"
                    }`}
                    style={{ minWidth: 28, height: 28, padding: "0 8px" }}
                  >
                    <span className="text-white text-[11px] font-bold tabular-nums">
                      {label}
                    </span>
                  </div>
                </AdvancedMarker>
              );
            })}
            <MapZoomController
              activePos={activeCluster ? activeCluster.pos : null}
            />
            {activeCluster &&
              (() => {
                const m = activeCluster.members[0];
                if (m.source === "gps") {
                  const locTail = [m.city, m.region, m.country]
                    .filter(Boolean)
                    .join(", ");
                  const locParts: string[] = [];
                  if (neighborhood?.value) {
                    locParts.push(neighborhood.value);
                  } else if (neighborhood?.loading) {
                    locParts.push("Locating…");
                  }
                  if (locTail) locParts.push(locTail);
                  const locationLabel = locParts.join(" · ");

                  // Multiple views from the SAME spot → aggregate card
                  // ("N views · cumulative time"), not a single session.
                  if (m.sessions.length > 1) {
                    const views = activeCluster.views;
                    const totalTime = activeCluster.timeSpent;
                    const visitors = activeCluster.visitors;
                    const avg =
                      views > 0 ? Math.round(totalTime / views) : 0;
                    const byProject = new Map<string, number>();
                    for (const sx of m.sessions) {
                      const nm =
                        projectNameById.get(String(sx?.project_id)) || "—";
                      byProject.set(nm, (byProject.get(nm) || 0) + 1);
                    }
                    const projectRows = Array.from(
                      byProject.entries(),
                    ).sort((a, b) => b[1] - a[1]);
                    return (
                      <InfoWindow
                        position={m.pos}
                        onCloseClick={() => setActiveKey(null)}
                      >
                        <div className="text-[13px] text-[#1C2C3A] py-1 px-0.5 min-w-[220px]">
                          <div className="font-semibold text-[14px] mb-1 text-[#0F2530]">
                            {locationLabel || "This location"}
                          </div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="inline-flex items-center rounded-md bg-[#0EA5E9] text-white text-[12px] font-bold px-2 py-0.5">
                              {views.toLocaleString()} views
                            </span>
                            <span className="text-[11px] text-[#5A7C8C]">
                              {visitors.toLocaleString()} visitor
                              {visitors === 1 ? "" : "s"}
                            </span>
                          </div>
                          <div className="grid grid-cols-[auto,1fr] gap-y-1 gap-x-3">
                            <span className="text-[#5A7C8C]">Total time</span>
                            <span className="font-semibold text-right">
                              {formatDuration(totalTime)}
                            </span>
                            <span className="text-[#5A7C8C]">Avg / view</span>
                            <span className="font-semibold text-right">
                              {formatDuration(avg)}
                            </span>
                          </div>
                          {projectRows.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-[#E2E8F0]">
                              <div className="text-[10px] uppercase tracking-wide text-[#5A7C8C] mb-1">
                                By project
                              </div>
                              <div className="grid grid-cols-[1fr,auto] gap-y-0.5 gap-x-3">
                                {projectRows.map(([nm, cnt]) => (
                                  <React.Fragment key={nm}>
                                    <span className="truncate">{nm}</span>
                                    <span className="font-semibold text-right">
                                      {cnt} view{cnt === 1 ? "" : "s"}
                                    </span>
                                  </React.Fragment>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </InfoWindow>
                    );
                  }

                  // Single session → detailed card (unchanged).
                  const s = m.sessions[0];
                  const visitor = resolveUserName(s);
                  const role = deriveSessionRole(s);
                  const project =
                    projectNameById.get(String(s?.project_id)) || "—";
                  const statusInfo = SESSION_STATUS[s?.status];
                  return (
                    <InfoWindow
                      position={m.pos}
                      onCloseClick={() => setActiveKey(null)}
                    >
                      <div className="text-[13px] text-[#1C2C3A] py-1 px-0.5 min-w-[240px]">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="font-semibold text-[14px] text-[#0F2530] truncate">
                            {visitor}
                          </div>
                          {statusInfo && (
                            <Pill color={statusInfo.color}>
                              {statusInfo.label}
                            </Pill>
                          )}
                        </div>
                        <div className="mb-2">
                          <Pill color={role.color}>{role.label}</Pill>
                        </div>
                        <div className="text-[11px] text-[#5A7C8C] mb-2">
                          {locationLabel || "Location unknown"}
                        </div>
                        <div className="grid grid-cols-[auto,1fr] gap-y-1 gap-x-3">
                          <span className="text-[#5A7C8C]">Project</span>
                          <span className="font-semibold text-right truncate">
                            {project}
                          </span>
                          <span className="text-[#5A7C8C]">Duration</span>
                          <span className="font-semibold text-right">
                            {formatDuration(s?.total_duration)}
                          </span>
                          <span className="text-[#5A7C8C]">Device</span>
                          <span className="font-semibold text-right capitalize">
                            {s?.device || "—"}
                          </span>
                          <span className="text-[#5A7C8C]">When</span>
                          <span className="font-semibold text-right">
                            {formatSessionDate(s?.timestamp)}
                          </span>
                        </div>
                      </div>
                    </InfoWindow>
                  );
                }
                // source === "city" → aggregate popup (legacy behaviour)
                return (
                  <InfoWindow
                    position={m.pos}
                    onCloseClick={() => setActiveKey(null)}
                  >
                    <div className="text-[13px] text-[#1C2C3A] py-1 px-0.5 min-w-[160px]">
                      <div className="font-semibold text-[14px] mb-2 text-[#0F2530]">
                        {m.city}
                      </div>
                      <div className="text-[11px] text-[#5A7C8C] mb-2">
                        City-level (no GPS)
                      </div>
                      <div className="grid grid-cols-2 gap-y-1 gap-x-3">
                        <span className="text-[#5A7C8C]">Visitors</span>
                        <span className="font-semibold text-right">
                          {activeCluster.visitors.toLocaleString()}
                        </span>
                        <span className="text-[#5A7C8C]">Views</span>
                        <span className="font-semibold text-right">
                          {activeCluster.views.toLocaleString()}
                        </span>
                        <span className="text-[#5A7C8C]">Time</span>
                        <span className="font-semibold text-right">
                          {formatDuration(activeCluster.timeSpent)}
                        </span>
                      </div>
                    </div>
                  </InfoWindow>
                );
              })()}
          </GoogleMap>
        </div>
      </APIProvider>
    </div>
  );
};

const ByCityProjectPanel: React.FC<{ sessions: any[] }> = ({ sessions }) => {
  type CityStat = {
    city: string;
    visitors: number;
    views: number;
    timeSpent: number;
    visitorIds: Set<string>;
  };

  const rows = React.useMemo<CityStat[]>(() => {
    const map = new Map<string, CityStat>();
    for (const s of sessions) {
      const city = String(s?.city || "").trim();
      if (!city) continue;
      const stat =
        map.get(city) ||
        ({ city, visitors: 0, views: 0, timeSpent: 0, visitorIds: new Set() } as CityStat);
      stat.views += 1;
      if (typeof s?.total_duration === "number") stat.timeSpent += s.total_duration;
      {
        const vid = identifiedVisitorKey(s);
        if (vid) stat.visitorIds.add(vid);
      }
      map.set(city, stat);
    }
    return Array.from(map.values())
      .map((s) => ({ ...s, visitors: s.visitorIds.size }))
      .sort((a, b) => b.views - a.views);
  }, [sessions]);

  const emptyRows = [0, 1, 2, 3];

  return (
    <div className="rounded-xl bg-[#15333D] border border-[#2C4D5A] p-4 self-start min-h-[290px]">
      <div className="flex items-baseline justify-between mb-3 gap-2">
        <h3 className="text-[10px] font-semibold text-[#889DB2] uppercase tracking-widest">
          By City
        </h3>
        <span className="text-[10px] text-[#5A7C8C]">
          {sessions.length.toLocaleString()} views
        </span>
      </div>
      <div className="max-h-60 overflow-y-auto">
        <table className="w-full text-[12px] border-separate border-spacing-0">
          <thead className="sticky top-0 bg-[#15333D] z-10">
            <tr className="text-[#889DB2] text-[10px] uppercase tracking-wider">
              <th className="text-left py-2 px-2 font-medium">City</th>
              <th className="text-right py-2 px-2 font-medium">Visitors</th>
              <th className="text-right py-2 px-2 font-medium">Views</th>
              <th className="text-right py-2 px-2 font-medium">Time Spent</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0
              ? emptyRows.map((i) => (
                  <tr key={`empty-${i}`}>
                    <td className="py-2 px-2 border-t border-[#2C4D5A] text-[#5A7C8C]">—</td>
                    <td className="py-2 px-2 border-t border-[#2C4D5A] text-right text-[#5A7C8C]">0</td>
                    <td className="py-2 px-2 border-t border-[#2C4D5A] text-right text-[#5A7C8C]">0</td>
                    <td className="py-2 px-2 border-t border-[#2C4D5A] text-right text-[#5A7C8C] whitespace-nowrap">0s</td>
                  </tr>
                ))
              : rows.map((r) => (
                  <tr key={r.city} className="hover:bg-white/[0.02]">
                    <td className="py-2 px-2 border-t border-[#2C4D5A] text-white">{r.city}</td>
                    <td className="py-2 px-2 border-t border-[#2C4D5A] text-right text-white font-medium">
                      {r.visitors.toLocaleString()}
                    </td>
                    <td className="py-2 px-2 border-t border-[#2C4D5A] text-right text-white font-medium">
                      {r.views.toLocaleString()}
                    </td>
                    <td className="py-2 px-2 border-t border-[#2C4D5A] text-right text-white font-medium whitespace-nowrap">
                      {formatDuration(r.timeSpent)}
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Agent-only: fixed two-row breakdown that mirrors the visual shape of
// ByUserProfilePanel but collapses to just the two categories an agent
// cares about — sessions they personally hosted vs sessions belonging to
// their assigned leads. No expansion, no per-lead drill-down.
// Aggregates sessions by experience visited. Meta Model only — Digital
// Twin sessions don't have human-readable space labels we can rely on,
// so we don't try to track them here. When the panel is rendered for a
// project that has no meta model, the parent passes
// `metaModelConfigured={false}` and we swap the empty rows for an
// explicit "not configured" message.
const ByExperiencePanel: React.FC<{
  sessions: any[];
  metaModelConfigured?: boolean;
}> = ({ sessions, metaModelConfigured = true }) => {
  type ExpStat = {
    name: string;
    visitors: number;
    views: number;
    timeSpent: number;
    visitorIds: Set<string>;
  };
  const rows = React.useMemo<ExpStat[]>(() => {
    const map = new Map<string, ExpStat>();
    for (const s of sessions) {
      const visited = Array.isArray(s?.experiences_visited)
        ? s.experiences_visited
        : null;
      if (!visited || visited.length === 0) continue;
      const times: Record<string, number> =
        s?.experience_times && typeof s.experience_times === "object"
          ? s.experience_times
          : {};
      const visitorId = identifiedVisitorKey(s);
      for (const raw of visited) {
        const name = String(raw || "").trim();
        if (!name) continue;
        const stat =
          map.get(name) ||
          ({
            name,
            visitors: 0,
            views: 0,
            timeSpent: 0,
            visitorIds: new Set(),
          } as ExpStat);
        stat.views += 1;
        const t = times?.[name];
        if (typeof t === "number") stat.timeSpent += t;
        if (visitorId) stat.visitorIds.add(visitorId);
        map.set(name, stat);
      }
    }
    return Array.from(map.values())
      .map((s) => ({ ...s, visitors: s.visitorIds.size }))
      .sort((a, b) => b.views - a.views);
  }, [sessions]);

  const emptyRows = [0, 1, 2, 3];

  return (
    <div className="rounded-xl bg-[#15333D] border border-[#2C4D5A] p-4 self-start min-h-[290px]">
      <div className="flex items-baseline justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-baseline gap-3 flex-wrap min-w-0">
          <h3 className="text-[10px] font-semibold text-[#889DB2] uppercase tracking-widest">
            By Experience
          </h3>
          <span className="text-[10px] text-[#5A7C8C] italic">
            Meta Models only · not Digital Twins
          </span>
        </div>
        <span className="text-[10px] text-[#5A7C8C]">
          {rows.length.toLocaleString()}{" "}
          {rows.length === 1 ? "experience" : "experiences"}
        </span>
      </div>
      <div className="max-h-60 overflow-y-auto">
        <table className="w-full text-[12px] border-separate border-spacing-0">
          <thead className="sticky top-0 bg-[#15333D] z-10">
            <tr className="text-[#889DB2] text-[10px] uppercase tracking-wider">
              <th className="text-left py-2 px-2 font-medium">Experience</th>
              <th className="text-right py-2 px-2 font-medium">Visitors</th>
              <th className="text-right py-2 px-2 font-medium">Views</th>
              <th className="text-right py-2 px-2 font-medium">Time Spent</th>
            </tr>
          </thead>
          <tbody>
            {!metaModelConfigured ? (
              <tr>
                <td
                  colSpan={4}
                  className="py-6 px-2 border-t border-[#2C4D5A] text-center"
                >
                  <div className="text-[12px] font-medium text-[#B6C7DA]">
                    Meta Model Not Configured
                  </div>
                  <div className="text-[10px] text-[#5A7C8C] mt-1">
                    Set up a meta model on this project to start tracking experiences.
                  </div>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              emptyRows.map((i) => (
                <tr key={`empty-${i}`}>
                  <td className="py-2 px-2 border-t border-[#2C4D5A] text-[#5A7C8C]">—</td>
                  <td className="py-2 px-2 border-t border-[#2C4D5A] text-right text-[#5A7C8C]">0</td>
                  <td className="py-2 px-2 border-t border-[#2C4D5A] text-right text-[#5A7C8C]">0</td>
                  <td className="py-2 px-2 border-t border-[#2C4D5A] text-right text-[#5A7C8C] whitespace-nowrap">0s</td>
                </tr>
              ))
            ) : (
              rows.map((r) => (
                <tr key={r.name} className="hover:bg-white/[0.02]">
                  <td className="py-2 px-2 border-t border-[#2C4D5A]">
                    <Pill color="#818cf8">{r.name}</Pill>
                  </td>
                  <td className="py-2 px-2 border-t border-[#2C4D5A] text-right text-white font-medium">
                    {r.visitors.toLocaleString()}
                  </td>
                  <td className="py-2 px-2 border-t border-[#2C4D5A] text-right text-white font-medium">
                    {r.views.toLocaleString()}
                  </td>
                  <td className="py-2 px-2 border-t border-[#2C4D5A] text-right text-white font-medium whitespace-nowrap">
                    {r.timeSpent > 0 ? formatDuration(r.timeSpent) : "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ByAgentActivityPanel: React.FC<{
  sessions: any[];
  agentInternalId: string | null;
  agentLeadIds: Set<string>;
  leadContactById: Map<string, Contact>;
}> = ({ sessions, agentInternalId, agentLeadIds, leadContactById }) => {
  const [leadsOpen, setLeadsOpen] = React.useState(false);

  type RollupRow = {
    key: string;
    label: string;
    color: string;
    visitors: number;
    views: number;
    timeSpent: number;
  };
  type LeadRow = {
    leadId: string;
    name: string;
    mobile: string;
    views: number;
    timeSpent: number;
  };

  // Aggregate the two rollup rows + per-lead breakdown in one pass.
  const { mine, leadRow, leadsBreakdown } = React.useMemo(() => {
    const makeRollup = (key: string, label: string, color: string): RollupRow => ({
      key,
      label,
      color,
      visitors: 0,
      views: 0,
      timeSpent: 0,
    });
    const m = makeRollup("mine", "My Views", "#38BDF8");
    const lr = makeRollup("leads", "Lead", "#34D399");
    const lVisitors = new Set<string>();
    const byLead = new Map<string, LeadRow>();

    for (const s of sessions) {
      const aid = s?.agent_id;
      const isMine = agentInternalId
        ? Array.isArray(aid)
          ? aid.some((x) => String(x) === agentInternalId)
          : String(aid) === agentInternalId
        : false;
      const cid = s?.customer_id ? String(s.customer_id) : "";
      const isLead = cid ? agentLeadIds.has(cid) : false;
      const dur = typeof s?.total_duration === "number" ? s.total_duration : 0;

      if (isMine) {
        m.views += 1;
        m.timeSpent += dur;
      }
      if (isLead) {
        lr.views += 1;
        lr.timeSpent += dur;
        lVisitors.add(String(s?.user_id || s?._id));
        const existing = byLead.get(cid);
        if (existing) {
          existing.views += 1;
          existing.timeSpent += dur;
        } else {
          const contact = leadContactById.get(cid);
          byLead.set(cid, {
            leadId: cid,
            name: contact?.name || "Lead",
            mobile: contact?.mobile || "",
            views: 1,
            timeSpent: dur,
          });
        }
      }
    }
    // "My Views" by definition is the logged-in agent's own sessions —
    // the visitor is always you, so it's tautologically 1 (or 0 if no
    // sessions). Counting distinct user_ids here would only surface data
    // corruption (e.g., sessions with mixed user_id / agent_id pairings).
    m.visitors = m.views > 0 ? 1 : 0;
    lr.visitors = lVisitors.size;
    const breakdown = Array.from(byLead.values()).sort((a, b) => b.views - a.views);
    return { mine: m, leadRow: lr, leadsBreakdown: breakdown };
  }, [sessions, agentInternalId, agentLeadIds, leadContactById]);

  const totalShown = mine.views + leadRow.views;

  const renderRollupRow = (
    r: RollupRow,
    opts: { expandable: boolean; open?: boolean; onToggle?: () => void },
  ) => (
    <tr
      key={r.key}
      onClick={opts.expandable ? opts.onToggle : undefined}
      className={
        opts.expandable ? "cursor-pointer hover:bg-white/[0.02]" : "cursor-default"
      }
    >
      <td className="py-2 px-2 border-t border-[#2C4D5A]">
        <span className="inline-flex items-center gap-2">
          {opts.expandable ? (
            <svg
              className={`w-3 h-3 text-[#889DB2] transition-transform ${
                opts.open ? "rotate-90" : ""
              }`}
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          ) : (
            <span className="w-3 h-3 inline-block" />
          )}
          <Pill color={r.color}>{r.label}</Pill>
        </span>
      </td>
      <td className="py-2 px-2 border-t border-[#2C4D5A] text-right text-white font-medium">
        {r.visitors.toLocaleString()}
      </td>
      <td className="py-2 px-2 border-t border-[#2C4D5A] text-right text-white font-medium">
        {r.views.toLocaleString()}
      </td>
      <td className="py-2 px-2 border-t border-[#2C4D5A] text-right text-white font-medium whitespace-nowrap">
        {formatDuration(r.timeSpent)}
      </td>
      <td className="py-2 px-2 border-t border-[#2C4D5A] text-right text-white font-medium whitespace-nowrap">
        {r.views > 0
          ? formatDuration(Math.round(r.timeSpent / r.views))
          : "—"}
      </td>
    </tr>
  );

  return (
    <div className="rounded-xl bg-[#15333D] border border-[#2C4D5A] p-4 h-full flex flex-col">
      <div className="flex items-baseline justify-between mb-3 gap-2">
        <h3 className="text-[10px] font-semibold text-[#889DB2] uppercase tracking-widest">
          By User Profile
        </h3>
        <span className="text-[10px] text-[#5A7C8C]">
          {totalShown.toLocaleString()} views
        </span>
      </div>
      <table className="w-full text-[12px] border-separate border-spacing-0">
        <thead>
          <tr className="text-[#889DB2] text-[10px] uppercase tracking-wider">
            <th className="text-left py-2 px-2 font-medium">Category</th>
            <th className="text-right py-2 px-2 font-medium">Visitors</th>
            <th className="text-right py-2 px-2 font-medium">Views</th>
            <th className="text-right py-2 px-2 font-medium">Time Spent</th>
            <th className="text-right py-2 px-2 font-medium">Avg</th>
          </tr>
        </thead>
        <tbody>
          {renderRollupRow(mine, { expandable: false })}
          {renderRollupRow(leadRow, {
            expandable: true,
            open: leadsOpen,
            onToggle: () => setLeadsOpen((v) => !v),
          })}
          {leadsOpen && leadsBreakdown.length === 0 && (
            <tr className="bg-[#0E2530]/40">
              <td
                colSpan={5}
                className="py-3 pl-10 pr-2 border-t border-[#2C4D5A]/60 text-[11px] text-[#5A7C8C] italic"
              >
                No lead activity yet
              </td>
            </tr>
          )}
          {leadsOpen &&
            leadsBreakdown.slice(0, USER_EXPAND_LIMIT).map((lead) => (
              <tr key={lead.leadId} className="bg-[#0E2530]/40">
                <td className="py-2 pl-10 pr-2 border-t border-[#2C4D5A]/60">
                  <div className="flex flex-col">
                    <span className="text-[#E5E7EB] truncate max-w-[220px]">
                      {lead.name}
                    </span>
                    {lead.mobile && (
                      <span className="text-[10px] text-[#5A7C8C]">{lead.mobile}</span>
                    )}
                  </div>
                </td>
                <td className="border-t border-[#2C4D5A]/60" />
                <td className="py-2 px-2 border-t border-[#2C4D5A]/60 text-right text-[#E5E7EB]">
                  {lead.views.toLocaleString()}
                </td>
                <td className="py-2 px-2 border-t border-[#2C4D5A]/60 text-right text-[#E5E7EB] whitespace-nowrap">
                  {formatDuration(lead.timeSpent)}
                </td>
                <td className="py-2 px-2 border-t border-[#2C4D5A]/60 text-right text-[#E5E7EB] whitespace-nowrap">
                  {lead.views > 0
                    ? formatDuration(Math.round(lead.timeSpent / lead.views))
                    : "—"}
                </td>
              </tr>
            ))}
          {leadsOpen && leadsBreakdown.length > USER_EXPAND_LIMIT && (
            <tr className="bg-[#0E2530]/40">
              <td
                colSpan={5}
                className="py-2 pl-10 pr-2 border-t border-[#2C4D5A]/60 text-[11px] text-[#889DB2]"
              >
                + {(leadsBreakdown.length - USER_EXPAND_LIMIT).toLocaleString()} more
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const KpiCard: React.FC<KpiCardProps> = ({
  icon,
  iconBg,
  accent,
  value,
  label,
  share,
  subtext,
  emptyState,
  trend,
}) => {
  const base =
    "rounded-lg p-3 bg-gradient-to-b from-[#1F3D48] to-[#16323C] border border-[#3D5468] select-none transition-all duration-150 hover:-translate-y-[1px] hover:border-[var(--card-accent)] hover:shadow-[0_6px_18px_rgba(0,0,0,0.3)]";
  const styleVars = { "--card-accent": accent } as React.CSSProperties;

  if (emptyState) {
    return (
      <div className={base} style={styleVars}>
        <div
          className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 mb-2.5 opacity-50"
          style={{ background: iconBg }}
        >
          {icon}
        </div>
        <div className="text-[13px] font-semibold text-white leading-tight">
          {emptyState.title}
        </div>
        <div className="text-[10px] text-[#B6C7DA] font-medium mt-1.5 leading-snug">
          {emptyState.subtitle}
        </div>
      </div>
    );
  }

  return (
    <div className={base} style={styleVars}>
      <div
        className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 mb-2.5"
        style={{ background: iconBg }}
      >
        {icon}
      </div>
      <div className="flex items-center gap-2 leading-none">
        <div className="text-xl font-bold text-white">{value}</div>
        {trend === "up" && (
          <span
            title="Active — at least one recent view"
            className="relative inline-flex w-2 h-2"
          >
            <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full w-2 h-2 bg-emerald-400" />
          </span>
        )}
        {trend === "down" && (
          <span
            title="Inactive — no recent views"
            className="relative inline-flex w-2 h-2"
          >
            <span className="absolute inline-flex w-full h-full rounded-full bg-rose-500 opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full w-2 h-2 bg-rose-500" />
          </span>
        )}
      </div>
      <div className="text-[11px] text-[#B6C7DA] font-semibold tracking-[0.2px] mt-1.5">{label}</div>
      {share && (
        <div className="mt-1.5">
          <div className="flex justify-between text-[9px] mb-1">
            <span className="text-gray-600">{share.label ?? "Share of total"}</span>
            <span className="text-gray-400 font-semibold">{share.percent}%</span>
          </div>
          <div className="h-1 rounded-full overflow-hidden bg-[#1a2a38]">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${share.percent}%`, background: share.color }}
            />
          </div>
        </div>
      )}
      {subtext && (
        <div className="text-[10px] mt-1" style={{ color: subtext.color }}>
          {subtext.text}
        </div>
      )}
    </div>
  );
};

// ── Inline SVG icons ───────────────────────────────────────────────────
const IconClick = (color: string) => (
  <svg className={`w-4 h-4 ${color}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.042 21.672L13.684 16.6m0 0l-2.51 2.225.569-9.47 5.227 7.917-3.286-.672zM12 2.25V4.5m5.834.166l-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243l-1.59-1.59" />
  </svg>
);
const IconEye = (color: string) => (
  <svg className={`w-4 h-4 ${color}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);
const IconMobile = (color: string) => (
  <svg className={`w-4 h-4 ${color}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 8.25h3" />
  </svg>
);
const IconDesktop = (color: string) => (
  <svg className={`w-4 h-4 ${color}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" />
  </svg>
);
const IconCube = (color: string) => (
  <svg className={`w-4 h-4 ${color}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
  </svg>
);
const IconVR = (color: string) => (
  <svg className={`w-4 h-4 ${color}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 7.5l-2.25-1.313M21 7.5v2.25m0-2.25l-2.25 1.313M3 7.5l2.25-1.313M3 7.5l2.25 1.313M3 7.5v2.25m9 3l2.25-1.313M12 12.75l-2.25-1.313M12 12.75V15m0 6.75l2.25-1.313M12 21.75V19.5m0 2.25l-2.25-1.313m0-16.875L12 2.25l2.25 1.313M21 14.25v2.25l-9 5.25-9-5.25v-2.25m18 0l-9 5.25m-9-5.25l9 5.25" />
  </svg>
);
const IconBuilding = (color: string) => (
  <svg className={`w-4 h-4 ${color}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955a1.5 1.5 0 012.121 0L21.75 12M4.5 9.75v10.125a1.125 1.125 0 001.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125a1.125 1.125 0 001.125-1.125V9.75M8.25 21h8.25" />
  </svg>
);
const IconBolt = (color: string) => (
  <svg className={`w-4 h-4 ${color}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
  </svg>
);
const IconClock = (color: string) => (
  <svg className={`w-4 h-4 ${color}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
const IconFolder = (color: string) => (
  <svg className={`w-4 h-4 ${color}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
  </svg>
);
const IconUsers = (color: string) => (
  <svg className={`w-4 h-4 ${color}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
  </svg>
);
const IconUserPlus = (color: string) => (
  <svg className={`w-4 h-4 ${color}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766z" />
  </svg>
);
const IconUserMinus = (color: string) => (
  <svg className={`w-4 h-4 ${color}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M22 10.5h-6m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766z" />
  </svg>
);

// ── Sessions table ────────────────────────────────────────────────────
const formatSessionDate = (ts: string): string => {
  if (!ts) return "—";
  try {
    // Backend sends naive timestamps (no TZ marker) representing UTC time.
    // Append "Z" so JS parses as UTC, then format in IST (Asia/Kolkata).
    const hasTz = /Z|[+-]\d{2}:?\d{2}$/.test(ts);
    const iso = hasTz ? ts : ts.replace(" ", "T") + "Z";
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: "Asia/Kolkata",
    });
  } catch {
    return ts;
  }
};

const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return remM ? `${h}h ${remM}m` : `${h}h`;
};

const deriveSessionRole = (s: any): { label: string; color: string } => {
  if (s?.aba_id?.length) return { label: "Business Admin", color: "#C4B5FD" };
  if (s?.asa_id?.length) return { label: "System Admin", color: "#FB7185" };
  if (s?.agent_id?.length) return { label: "Agent", color: "#38BDF8" };
  if (s?.customer_id?.length) return { label: "Lead", color: "#34D399" };
  return { label: "Raw Visitor", color: "#9CA3AF" };
};

const deriveMetaAsset = (s: any): { label: string; color: string } | null => {
  if (s?.meta_model_id) return { label: "Meta Model", color: "#818cf8" };
  if (s?.matterport_id) return { label: "Digital Twin", color: "#e879f9" };
  return null;
};

const SESSION_STATUS: Record<string, { color: string; label: string }> = {
  completed: { color: "#34D399", label: "Completed" },
  active: { color: "#38BDF8", label: "Active" },
  abandoned: { color: "#FB7185", label: "Abandoned" },
};

const Pill: React.FC<{ color: string; children: React.ReactNode }> = ({
  color,
  children,
}) => (
  <span
    className="inline-block text-[10px] font-semibold tracking-[0.05em] uppercase px-1.5 py-[1px] rounded whitespace-nowrap"
    style={{ color, backgroundColor: `${color}26` }}
  >
    {children}
  </span>
);

const SessionsTable: React.FC<{
  sessions: any[];
  projectNameById: Map<string, string>;
  businessAdminById: Map<string, string>;
  systemAdminById: Map<string, string>;
  agentNameById: Map<string, string>;
  matterportNameBySid: Map<string, string>;
  metaModelNameById: Map<string, string>;
  loading: boolean;
  page: number;
  pageSize: number;
  total: number | null;
  onPrev: () => void;
  onNext: () => void;
  onDownload: () => void;
  csvBusy: boolean;
}> = ({
  sessions,
  projectNameById,
  businessAdminById,
  systemAdminById,
  agentNameById,
  matterportNameBySid,
  metaModelNameById,
  loading,
  page,
  pageSize,
  total,
  onPrev,
  onNext,
  onDownload,
  csvBusy,
}) => {
  const firstRow = page * pageSize + 1;
  const lastRow = page * pageSize + sessions.length;
  const hasNext = total != null ? lastRow < total : sessions.length === pageSize;
  const hasPrev = page > 0;
  const resolveUserName = (s: any): string => {
    if (s?.aba_id?.length) {
      const name = businessAdminById.get(String(s.aba_id[0]));
      if (name) return name;
    }
    if (s?.asa_id?.length) {
      const name = systemAdminById.get(String(s.asa_id[0]));
      if (name) return name;
    }
    if (s?.agent_id?.length) {
      const name = agentNameById.get(String(s.agent_id[0]));
      if (name) return name;
    }
    return s?.user_id || "Anonymous";
  };
  return (
    <section className="mt-3 rounded-xl bg-[#15333D] border border-[#2C4D5A] p-4 max-w-screen-2xl">
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="flex items-baseline gap-2">
          <h3 className="text-sm font-semibold text-white">Recent Sessions</h3>
          <span className="text-[11px] text-[#889DB2]">
            {loading
              ? "Loading…"
              : total != null
              ? `${total.toLocaleString()} total`
              : `${sessions.length} on this page`}
          </span>
        </div>
        <button
          type="button"
          onClick={onDownload}
          disabled={csvBusy || sessions.length === 0}
          className="text-[11px] font-medium px-3 py-1.5 rounded-md bg-[#008564]/15 text-[#34D399] border border-[#008564]/40 hover:bg-[#008564]/25 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
          </svg>
          {csvBusy ? "Preparing…" : "Download CSV"}
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px] border-separate border-spacing-0">
          <thead>
            <tr className="text-[#889DB2] text-[10px] uppercase tracking-wider">
              <th className="text-left py-2 px-2 font-medium">Date</th>
              <th className="text-left py-2 px-2 font-medium">User Profile</th>
              <th className="text-left py-2 px-2 font-medium">Project</th>
              <th className="text-left py-2 px-2 font-medium">City</th>
              <th className="text-left py-2 px-2 font-medium">Device</th>
              <th className="text-left py-2 px-2 font-medium">VR</th>
              <th className="text-left py-2 px-2 font-medium">Meta Asset</th>
              <th className="text-left py-2 px-2 font-medium">Duration</th>
              <th className="text-left py-2 px-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {!loading && sessions.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center py-8 text-[#889DB2]">
                  No sessions yet.
                </td>
              </tr>
            ) : (
              sessions.map((s) => {
                const role = deriveSessionRole(s);
                const meta = deriveMetaAsset(s);
                const status =
                  SESSION_STATUS[s.status] || {
                    color: "#9CA3AF",
                    label: s.status || "Unknown",
                  };
                return (
                  <tr
                    key={s._id || s.session_id}
                    className="border-t border-[#2C4D5A] hover:bg-white/[0.02]"
                  >
                    <td className="py-2.5 px-2 text-white whitespace-nowrap border-t border-[#2C4D5A]">
                      {formatSessionDate(s.timestamp)}
                    </td>
                    <td className="py-2.5 px-2 border-t border-[#2C4D5A]">
                      <div className="flex flex-col gap-1 items-start">
                        <span className="text-white truncate max-w-[180px]">
                          {resolveUserName(s)}
                        </span>
                        <Pill color={role.color}>{role.label}</Pill>
                      </div>
                    </td>
                    <td className="py-2.5 px-2 text-[#E5E7EB] truncate max-w-[160px] border-t border-[#2C4D5A]">
                      {projectNameById.get(s.project_id) || "—"}
                    </td>
                    <td className="py-2.5 px-2 text-[#E5E7EB] border-t border-[#2C4D5A]">
                      {s.city || <span className="text-[#889DB2]">—</span>}
                    </td>
                    <td className="py-2.5 px-2 text-[#E5E7EB] capitalize border-t border-[#2C4D5A]">
                      {s.device || "—"}
                    </td>
                    <td className="py-2.5 px-2 border-t border-[#2C4D5A]">
                      {s.used_vr ? (
                        <Pill color="#34D399">Yes</Pill>
                      ) : (
                        <span className="text-[#889DB2]">—</span>
                      )}
                    </td>
                    <td className="py-2.5 px-2 border-t border-[#2C4D5A]">
                      {meta ? (
                        (() => {
                          const assetName = s.meta_model_id
                            ? metaModelNameById.get(String(s.meta_model_id))
                            : s.matterport_id
                            ? matterportNameBySid.get(String(s.matterport_id))
                            : undefined;
                          return (
                            <div className="flex flex-col gap-1 items-start">
                              {assetName && (
                                <span className="text-white truncate max-w-[160px]">
                                  {assetName}
                                </span>
                              )}
                              <Pill color={meta.color}>{meta.label}</Pill>
                            </div>
                          );
                        })()
                      ) : (
                        <span className="text-[#889DB2]">—</span>
                      )}
                    </td>
                    <td className="py-2.5 px-2 text-[#E5E7EB] whitespace-nowrap border-t border-[#2C4D5A]">
                      {formatDuration(s.total_duration)}
                    </td>
                    <td className="py-2.5 px-2 border-t border-[#2C4D5A]">
                      <Pill color={status.color}>{status.label}</Pill>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {(hasPrev || hasNext) && (
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#2C4D5A]">
          <span className="text-[11px] text-[#889DB2]">
            {sessions.length > 0
              ? `Showing ${firstRow.toLocaleString()}–${lastRow.toLocaleString()}${
                  total != null ? ` of ${total.toLocaleString()}` : ""
                }`
              : ""}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onPrev}
              disabled={!hasPrev || loading}
              className="text-[11px] font-medium px-3 py-1.5 rounded-md bg-[#2E4051] hover:bg-[#3A4E62] text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ← Prev
            </button>
            <span className="text-[11px] text-[#889DB2] tabular-nums">
              Page {page + 1}
              {total != null
                ? ` of ${Math.max(1, Math.ceil(total / pageSize))}`
                : ""}
            </span>
            <button
              type="button"
              onClick={onNext}
              disabled={!hasNext || loading}
              className="text-[11px] font-medium px-3 py-1.5 rounded-md bg-[#2E4051] hover:bg-[#3A4E62] text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

// Small icon-only button next to the date picker. Spins while a fetch
// is in flight, disabled to prevent rapid double-tap re-fetches.
const RefreshButton: React.FC<{ busy: boolean; onClick: () => void }> = ({
  busy,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy}
    title={busy ? "Refreshing…" : "Refresh data"}
    aria-label="Refresh data"
    className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#1F3D48] border border-[#3D5468] text-[#B6C7DA] hover:bg-[#243F4C] hover:border-[#5A7088] hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
  >
    <svg
      className={`w-4 h-4 ${busy ? "animate-spin" : ""}`}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
      />
    </svg>
  </button>
);

/* ── Share Analytics: "Shared by" role rollup ─────────────────────────
   ABA/ASA see the whole agency's share-link performance, sliceable by role
   tier (Everyone / You / System Admins / Agents / Customers). Agents and
   customers see only their own ("You") — no selector.

   DEMO DATA for now. Live wiring joins the real agency endpoints:
   GET /share-links?scope=agency (code→sharer) + /share-links/clicks?scope=agency
   + GET /realestate/views/user/roles (email→role) + vr_sessions by
   referrer_share_code. Swapped in once the layout's locked. */
type ShareRole = "you" | "system_admin" | "agent" | "customer";
interface SharePerson {
  name: string;
  role: ShareRole;
  clicks: number;
  visitors: number;
  sessions: number;
  leads: number;
}
const SHARE_PEOPLE_DEMO: SharePerson[] = (
  [
    ["You (Sivaram)", "you", 47, 35, 18, 6],
    ["Arjun Rao", "system_admin", 38, 29, 14, 4],
    ["Priya Nair", "system_admin", 22, 18, 9, 2],
    ["Zaid Khan", "system_admin", 31, 24, 12, 3],
    ["Ravi Menon", "agent", 54, 41, 22, 7],
    ["Sneha Patil", "agent", 29, 23, 10, 3],
    ["Imran Sheikh", "agent", 18, 14, 6, 1],
    ["Divya Iyer", "agent", 41, 33, 17, 5],
    ["Karan Joshi", "agent", 12, 9, 4, 1],
    ["Meera Reddy", "agent", 36, 28, 13, 4],
    ["Aditya Verma", "agent", 25, 19, 9, 2],
    ["Fatima Ali", "agent", 33, 26, 12, 3],
    ["Rahul Das", "customer", 9, 7, 3, 1],
    ["Anita Kapoor", "customer", 14, 11, 5, 2],
    ["Vivek Shah", "customer", 6, 5, 2, 0],
    ["Pooja Gupta", "customer", 11, 9, 4, 1],
    ["Sameer Khan", "customer", 8, 6, 3, 0],
    ["Nisha Pillai", "customer", 16, 13, 6, 2],
    ["Rohit Sinha", "customer", 4, 3, 1, 0],
    ["Tara Bose", "customer", 13, 10, 5, 1],
    ["Gaurav M.", "customer", 7, 6, 2, 1],
  ] as [string, ShareRole, number, number, number, number][]
).map(([name, role, clicks, visitors, sessions, leads]) => ({
  name,
  role,
  clicks,
  visitors,
  sessions,
  leads,
}));

// Individual attributed leads (drill-down under the Leads Generated card).
// Per-role counts match the leads totals in SHARE_PEOPLE_DEMO so the card
// count always equals the rows shown. Live data: re_customer rows with
// referrer_share_code, joined to share_code → sharer + channel.
interface ShareLead {
  leadId: string;
  customerId: string;
  name: string;
  phone: string;
  project: string;
  sharedBy: string;
  role: ShareRole;
  channel: string;
  daysAgo: number;
  // Live rows carry the real session timestamp (ISO/UTC); demo rows use daysAgo.
  dateIso?: string;
}
// Role badge for the "Attributed to" cell (name + role, like the session
// table's user profile in Platform Analytics).
// Colors mirror deriveSessionRole so the leads "Attributed to" pills match the
// Sessions table "User Profile" pills exactly. "you" rows are the logged-in
// admin's own shares, shown with their real role (Business Admin) — no "You".
const ROLE_BADGE: Record<ShareRole, { label: string; color: string }> = {
  you: { label: "Business Admin", color: "#C4B5FD" },
  system_admin: { label: "System Admin", color: "#FB7185" },
  agent: { label: "Agent", color: "#38BDF8" },
  customer: { label: "Customer", color: "#34D399" },
};

// Map a backend role payload (shape not strictly typed) to our ShareRole
// buckets. Handles "agency_business_admin" / "agency_system_admin" / "agent"
// / "customer" whether returned as a string, {role}, or an array.
const parseBackendRole = (rr: any): ShareRole | undefined => {
  if (!rr) return undefined;
  let raw: any = rr.role ?? rr.role_name ?? rr.user_role ?? rr.roleName ?? rr.type;
  if (!raw && Array.isArray(rr.roles)) raw = rr.roles[0];
  if (!raw && Array.isArray(rr)) raw = rr[0];
  if (raw && typeof raw === "object") raw = raw.role ?? raw.name ?? raw.type;
  const s = String(raw ?? "").toLowerCase();
  if (!s) return undefined;
  if (s.includes("business")) return "you";
  if (s.includes("system")) return "system_admin";
  if (s.includes("agent")) return "agent";
  if (s.includes("customer") || s.includes("lead")) return "customer";
  return undefined;
};
const SHARE_LEADS_DEMO: ShareLead[] = (() => {
  const poolFor = (r: ShareRole) => {
    const names = SHARE_PEOPLE_DEMO.filter((p) => p.role === r).map((p) => p.name);
    return names.length ? names : ["—"];
  };
  const leadNames = [
    "Riya Sharma", "Karan Mehta", "Aisha Khan", "Dev Patel", "Sara Iyer",
    "Vikram Rao", "Neha Gupta", "Arjun Nair", "Pooja Reddy", "Rohan Das",
    "Ananya Bose", "Kabir Singh", "Maya Pillai", "Yash Malhotra",
  ];
  const projects = ["Triton", "Serenity", "Kohinoor", "Hallmark Altus", "Villa Feliz"];
  const channels = ["WhatsApp", "Copy link", "QR code", "Email", "LinkedIn"];
  const counts: [ShareRole, number][] = [
    ["you", 6],
    ["system_admin", 9],
    ["agent", 26],
    ["customer", 8],
  ];
  const out: ShareLead[] = [];
  let g = 0;
  for (const [role, count] of counts) {
    const pool = poolFor(role);
    for (let i = 0; i < count; i += 1) {
      out.push({
        leadId: `led_${(0xa100 + g).toString(16)}`,
        customerId: `cus_${(0x6a0f0000 + g * 137).toString(16)}`,
        name: leadNames[g % leadNames.length],
        phone: `+91 98${String(10000000 + g * 137).slice(-8)}`,
        project: projects[g % projects.length],
        sharedBy: pool[i % pool.length],
        role,
        channel: channels[g % channels.length],
        daysAgo: (g % 14) + 1,
      });
      g += 1;
    }
  }
  return out;
})();

type ShareScopeKey = "everyone" | ShareRole;

const ShareAnalytics: React.FC<{
  selectedProjectId: string;
  agencySlug: string;
  projectName?: string;
  onOpenProjectPage: () => void;
  dateRange: DateRangeKey;
  refreshTick: number;
  active: boolean;
  onLoadingChange?: (loading: boolean) => void;
  agencySessions: any[];
  businessAdminById: Map<string, string>;
  systemAdminById: Map<string, string>;
  agentNameById: Map<string, string>;
  // Sharer name keyed by lower-cased email. The id-keyed maps above only hit
  // when the backend stamps a matching Mongo id on the share link (it often
  // doesn't), so without this we fall back to the email local-part — e.g. a
  // sharer on sales@… renders as "Sales" instead of their real name.
  nameByEmail: Map<string, string>;
  roleByEmailDir: Map<string, ShareRole>;
  customerContactById: Map<string, Contact>;
  projectNameById: Map<string, string>;
  ownCustomerEmails: Set<string>;
  myAgentEmails: Set<string>;
  myAgentCustomerEmails: Set<string>;
}> = ({
  selectedProjectId,
  agencySlug,
  projectName,
  onOpenProjectPage,
  dateRange,
  refreshTick,
  active,
  onLoadingChange,
  agencySessions,
  businessAdminById,
  systemAdminById,
  agentNameById,
  nameByEmail,
  roleByEmailDir,
  customerContactById,
  projectNameById,
  ownCustomerEmails,
  myAgentEmails,
  myAgentCustomerEmails,
}) => {
  // Viewer role drives which "Shared by" scopes are visible (locked taxonomy):
  //   ABA   → Everyone · You · System Admins · Agents · Customers
  //   ASA   → Everyone · You · Agents · Customers          (no SA layer)
  //   Agent → Everyone · You · Customers (own customers only)
  //   Customer → own only (no chips)
  const viewerRole: "aba" | "asa" | "agent" | "customer" =
    UserService.hasRole("agency_business_admin")
      ? "aba"
      : UserService.hasRole("agency_system_admin")
        ? "asa"
        : UserService.hasRole("agent")
          ? "agent"
          : "customer";
  const showScopeChips = viewerRole !== "customer";
  const [scope, setScope] = useState<ShareScopeKey>(showScopeChips ? "everyone" : "you");

  // Role-color dot for each "Shared by" chip — same taxonomy as the By User
  // Profile rows / sessions pills, without recoloring the whole chip (which
  // would fight the selected state). "You" = the viewer's own role color;
  // "Everyone" = neutral gray.
  const chipDotColor = (key: ShareScopeKey): string => {
    switch (key) {
      case "system_admin":
        return "#FB7185";
      case "agent":
        return "#38BDF8";
      case "customer":
        return "#34D399";
      case "you":
        return viewerRole === "aba"
          ? "#C4B5FD"
          : viewerRole === "asa"
            ? "#FB7185"
            : viewerRole === "agent"
              ? "#38BDF8"
              : "#34D399";
      default:
        return "#9CA3AF";
    }
  };
  const [leadsPage, setLeadsPage] = useState(0);
  // Reset the leads table to page 1 whenever the scope changes.
  useEffect(() => {
    setLeadsPage(0);
  }, [scope]);

  // ── Live agency data ──────────────────────────────────────────────────
  // Two reads: agency-wide share links (code → sharer + per-link clicks) and
  // the caller's own my-stats (to flag which codes are "you"). Views, leads,
  // and roles come from data already in memory (agencySessions + the role
  // directories), so no extra round-trips. Falls back to demo on error.
  const [rawLinks, setRawLinks] = useState<any[] | null>(null);
  const [rawClicks, setRawClicks] = useState<any[]>([]);
  const [roleByEmail, setRoleByEmail] = useState<Record<string, ShareRole>>({});
  const [myCodes, setMyCodes] = useState<Set<string>>(new Set());
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveError, setLiveError] = useState(false);
  // Fetch lazily: only when the Share tab is active, and only once per
  // refreshTick. The component stays mounted (parent just hides it), so the
  // data persists across tab switches — no refetch on toggle. Matches how
  // Platform's data (held in the parent) stays stable when switching sections.
  const fetchedTickRef = React.useRef<number>(-1);

  useEffect(() => {
    if (!active) return undefined;
    if (fetchedTickRef.current === refreshTick) return undefined; // already loaded
    fetchedTickRef.current = refreshTick;
    let alive = true;
    let completed = false;
    setLiveLoading(true);
    (async () => {
      try {
        const agencyRes: any = await getAgencyShareLinks();
        const links: any[] = Array.isArray(agencyRes)
          ? agencyRes
          : agencyRes?.links || agencyRes?.shares || agencyRes?.data || [];

        // My own share codes → flags "you" rows.
        let myRes: any = null;
        try {
          myRes = await getMyShareLinkStats();
        } catch {
          myRes = null;
        }
        const mine: any[] = Array.isArray(myRes) ? myRes : myRes?.shares || [];
        const codes = new Set<string>();
        for (const m of mine) {
          const c = m?.share_code || m?.code;
          if (c) codes.add(String(c));
        }

        // Full agency click log (for Clicks / unique Visitors). The endpoint
        // caps each page at 200 docs and paginates via `page`, so we loop until
        // a short page to pull the COMPLETE log — otherwise clicks/visitors
        // silently under-count past 200 (the cap was masking ~10 of 210 here).
        let clicks: any[] = [];
        try {
          const CLICK_PAGE_SIZE = 200;
          const MAX_PAGES = 500; // backstop (= 100k clicks) vs a misbehaving endpoint
          let page = 1;
          for (; page <= MAX_PAGES; page++) {
            const res: any = await getAgencyShareLinkClicks(page, CLICK_PAGE_SIZE);
            const batch: any[] = Array.isArray(res)
              ? res
              : res?.clicks || res?.data || [];
            clicks = clicks.concat(batch);
            if (batch.length < CLICK_PAGE_SIZE) break; // last page reached
          }
          if (page > MAX_PAGES) {
            // Never silently truncate — surface it so we know to switch to a
            // backend aggregate count instead of paging the whole log.
            // eslint-disable-next-line no-console
            console.warn(
              `[ShareAnalytics] click log exceeded ${MAX_PAGES} pages (${MAX_PAGES * CLICK_PAGE_SIZE}+ clicks) — count may be truncated.`,
            );
          }
        } catch {
          clicks = [];
        }

        // Resolve each distinct sharer's role (email → role), in parallel.
        const emails = Array.from(
          new Set(links.map((l) => l?.shared_by_email).filter(Boolean)),
        ) as string[];
        // A user with no role in this agency (roles endpoint returns []) is not
        // a member — their links are excluded downstream (guards against the
        // agency share-links endpoint leaking cross-agency links).
        const roleMap: Record<string, ShareRole> = {};
        await Promise.all(
          emails.map(async (em) => {
            try {
              const rr: any = await getUserRoleInAgency(em, agencySlug);
              const parsed = parseBackendRole(rr);
              if (parsed) roleMap[em] = parsed;
            } catch {
              /* not a member of this agency / lookup failed — skip */
            }
          }),
        );

        if (!alive) return;
        setRawLinks(links);
        setRawClicks(clicks);
        setRoleByEmail(roleMap);
        setMyCodes(codes);
        setLiveError(false);
        completed = true;
      } catch (e) {
        if (!alive) return;
        // eslint-disable-next-line no-console
        console.warn("[ShareAnalytics] live fetch failed — using demo data:", e);
        setRawLinks(null);
        setLiveError(true);
        completed = true;
      }
      if (alive) setLiveLoading(false);
    })();
    return () => {
      alive = false;
      // If we were toggled away before the fetch finished, allow a refetch on
      // re-activation (otherwise the cached tick would block it and leave it empty).
      if (!completed) fetchedTickRef.current = -1;
    };
  }, [active, refreshTick]);

  // Report loading up so the parent's top refresh-spinner spins during the
  // share fetch (same spinner Platform uses) — consistent UX, no second spinner.
  useEffect(() => {
    onLoadingChange?.(liveLoading);
  }, [liveLoading, onLoadingChange]);

  const isLive = !liveError && Array.isArray(rawLinks);

  // ── Live model (null until the agency links resolve) ──────────────────
  const liveModel = React.useMemo(() => {
    if (!isLive || !rawLinks) return null;
    const { startDate, endDate } = computeDateRange(dateRange);
    const startMs = startDate
      ? new Date(startDate + "T00:00:00Z").getTime()
      : -Infinity;
    const endMs = endDate
      ? new Date(endDate + "T23:59:59.999Z").getTime()
      : Infinity;
    const inDate = (ts: any) => {
      if (!ts) return !(startDate || endDate);
      const raw = String(ts);
      const hasTz = /Z|[+-]\d{2}:?\d{2}$/.test(raw);
      const iso = hasTz ? raw : raw.replace(" ", "T") + "Z";
      const t = new Date(iso).getTime();
      return t >= startMs && t <= endMs;
    };
    const toIso = (ts: any) => {
      const raw = ts ? String(ts) : "";
      if (!raw) return "";
      const hasTz = /Z|[+-]\d{2}:?\d{2}$/.test(raw);
      return hasTz ? raw : raw.replace(" ", "T") + "Z";
    };
    // Sharer name: prefer an in-memory directory hit (works if the backend
    // ever sends a Mongo id), else derive from the email local-part.
    const emailToName = (em: string) => {
      const local = String(em || "").split("@")[0] || "";
      return local
        ? local.charAt(0).toUpperCase() + local.slice(1)
        : em || "Unknown";
    };

    interface NLink {
      code: string;
      sharerId: string;
      email: string;
      projectId: string;
      name: string;
      role: ShareRole;
      isYou: boolean;
    }
    const codeMap = new Map<string, NLink>();
    for (const row of rawLinks) {
      const code = String(row?.share_code ?? row?.code ?? "");
      if (!code) continue;
      const email = String(row?.shared_by_email ?? "");
      const sharerId = String(row?.shared_by_user_id ?? "");
      const isYou = myCodes.has(code);
      // Authoritative agency directory (BA/SA/agent lists) wins over the
      // ambiguous /user/roles endpoint, which mis-roles a multi-role person
      // (e.g. a System Admin who is also a customer record -> "Customer").
      const resolvedRole = roleByEmailDir.get(email.toLowerCase()) || roleByEmail[email];
      // Exclude links whose sharer is NOT a member of THIS agency. The roles
      // endpoint returns no role ([]) for outside users; the agency share-links
      // endpoint currently leaks cross-agency links (backend RBAC gap), so we
      // filter them out here. The caller's own links ("you") are always kept.
      if (!isYou && !resolvedRole) continue;
      // Use the sharer's TRUE role for the badge + role-scoped chips. Do NOT
      // collapse a System Admin's / agent's OWN share to "you" (which badges as
      // "Business Admin"). The "You" scope chip is driven by the isYou boolean
      // (see matchScope), not by role === "you", so the two are independent.
      const selfRole: ShareRole =
        viewerRole === "asa"
          ? "system_admin"
          : viewerRole === "agent"
            ? "agent"
            : viewerRole === "customer"
              ? "customer"
              : "you";
      const role: ShareRole = (resolvedRole as ShareRole) || (isYou ? selfRole : "you");
      // Viewer-scoped visibility (locked taxonomy). Filtering the codeMap here
      // scopes EVERYTHING downstream (cards, clicks, sessions, leads, chips),
      // since they all resolve via codeMap. role "you" w/o isYou = the agency's
      // ABA (parseBackendRole maps business_admin→"you"); excluded for ASA/agent.
      const visibleToViewer =
        viewerRole === "aba"
          ? true
          : viewerRole === "asa"
            ? isYou ||
              (role === "agent" && myAgentEmails.has(email.toLowerCase())) ||
              (role === "customer" &&
                myAgentCustomerEmails.has(email.toLowerCase()))
            : viewerRole === "agent"
              ? isYou ||
                (role === "customer" && ownCustomerEmails.has(email.toLowerCase()))
              : isYou; // customer
      if (!visibleToViewer) continue;
      // Prefer the email-keyed directory (email is reliably stamped on the
      // share link); fall back to the id-keyed maps, then the email local-part.
      const known =
        nameByEmail.get(email.toLowerCase()) ||
        businessAdminById.get(sharerId) ||
        systemAdminById.get(sharerId) ||
        agentNameById.get(sharerId);
      codeMap.set(code, {
        code,
        sharerId: sharerId || email || code,
        email,
        projectId: String(row?.project_id ?? ""),
        name: known || emailToName(email),
        role,
        isYou,
      });
    }

    const matchScope = (role: ShareRole, isYou: boolean) =>
      scope === "everyone" ? true : scope === "you" ? isYou : role === scope;

    // Clicks / unique visitors from the raw agency click log (bots excluded,
    // attributed to a sharer/role via the link's share_code).
    const scopedClicks = rawClicks.filter((c) => {
      if (c?.is_bot) return false;
      // Click records stamp the time as `created_at` (the `at` alias isn't
      // emitted by GET /share-links/clicks). Falling back to `created_at` is
      // what makes clicks honor the date range — otherwise `c.at` is undefined
      // and inDate() drops EVERY click the moment any range other than
      // "Lifetime" is selected (sessions kept working because they use
      // `timestamp`, which is why Visitors/Views moved while Clicks stayed flat).
      if (!inDate(c?.at ?? c?.created_at)) return false;
      const l = codeMap.get(String(c?.share_code));
      if (!l) return false; // only clicks on THIS agency's links (codeMap)
      if (selectedProjectId && l.projectId !== selectedProjectId) return false;
      if (scope === "everyone") return true;
      return matchScope(l.role, l.isYou);
    });
    const clicks = scopedClicks.length;
    // Unique visitors = distinct (ip + user_agent). IP alone collapses
    // different people behind one NAT (office / household) into a single
    // visitor; adding the user-agent separates distinct devices on that IP.
    // Falls back to IP alone when a click has no user_agent — so no regression
    // if the backend ever omits it. IPv6 is normalized to its /64 prefix
    // (see normalizeIpForDedup) so mobile privacy-address rotation on one
    // network doesn't inflate the count.
    const visitors = new Set(
      scopedClicks
        .map((c) => {
          const ip = normalizeIpForDedup(c?.ip);
          if (!ip) return "";
          const ua = String(c?.user_agent || c?.userAgent || "").trim();
          return ua ? `${ip}|${ua}` : ip;
        })
        .filter(Boolean),
    ).size;

    const allLinks = Array.from(codeMap.values()).filter(
      (l) => !selectedProjectId || l.projectId === selectedProjectId,
    );

    // Views / meta / DT / time from in-memory share sessions.
    const shareSessions = agencySessions.filter((s) => {
      if (!s?.referrer_share_code) return false;
      if (!inDate(s?.timestamp)) return false;
      if (selectedProjectId && String(s?.project_id) !== selectedProjectId)
        return false;
      const l = codeMap.get(String(s.referrer_share_code));
      if (!l) return false; // only sessions referred by THIS agency's links
      if (scope === "everyone") return true;
      return matchScope(l.role, l.isYou);
    });
    const sessions = shareSessions.length;
    // "Tours" = distinct visits, bundled by the click that started them. Falls
    // back to each session's own id until the backend stamps click_id — so
    // pre-backend Tours == Views (each view is its own visit), and post-backend
    // they collapse per click (7 sessions in one click → 1 tour).
    const tours = new Set(
      shareSessions.map((s) => String(s?.click_id || s?._id || s?.session_id || "")),
    ).size;
    const metaModelViews = shareSessions.filter((s) => s?.meta_model_id).length;
    const digitalTwinViews = shareSessions.filter((s) => s?.matterport_id).length;
    let durationSec = 0;
    for (const s of shareSessions)
      if (typeof s?.total_duration === "number") durationSec += s.total_duration;
    const timeSpentMin = Math.round(durationSec / 60);

    // Leads = share sessions with a customer, deduped by customer.
    const seen = new Set<string>();
    const leadsInScope: ShareLead[] = [];
    for (const s of shareSessions) {
      const cid = s?.customer_id ? String(s.customer_id) : "";
      if (!cid || seen.has(cid)) continue;
      seen.add(cid);
      const l = codeMap.get(String(s.referrer_share_code));
      const contact = customerContactById.get(cid);
      leadsInScope.push({
        leadId: String(s?._id || s?.session_id || cid),
        customerId: cid,
        name: contact?.name || "Lead",
        phone: contact?.mobile || "—",
        project: projectNameById.get(String(s?.project_id)) || "—",
        sharedBy: l?.name || "Unknown",
        role: l?.role || "you",
        channel: s?.src || "—",
        daysAgo: 0,
        dateIso: toIso(s?.timestamp),
      });
    }

    // Scope tab counts = distinct sharers per role (project-filtered).
    const distinct = (pred: (l: NLink) => boolean) =>
      new Set(allLinks.filter(pred).map((l) => l.sharerId)).size;
    const peopleCount = new Set(allLinks.map((l) => l.sharerId)).size;
    // Chips shown depend on the viewer's role (codeMap is already viewer-scoped,
    // so counts are within what they can see). Everyone + You always; ABA adds
    // System Admins + Agents + Customers; ASA adds Agents + Customers; Agent
    // adds Customers (own).
    const allScopeTabs: { key: ShareScopeKey; label: string; count: number }[] = [
      { key: "everyone", label: "Everyone", count: peopleCount },
      { key: "you", label: "You", count: distinct((l) => l.isYou) },
      { key: "system_admin", label: "System Admins", count: distinct((l) => l.role === "system_admin") },
      { key: "agent", label: "Agents", count: distinct((l) => l.role === "agent") },
      { key: "customer", label: "Customers", count: distinct((l) => l.role === "customer") },
    ];
    const scopeTabs = allScopeTabs.filter((t) => {
      if (t.key === "everyone" || t.key === "you") return true;
      if (viewerRole === "aba") return true;
      if (viewerRole === "asa") return t.key === "agent" || t.key === "customer";
      if (viewerRole === "agent") return t.key === "customer";
      return false;
    });

    // Whether a share LINK exists for the current project+scope (independent of
    // any clicks/sessions). Drives the empty-state: "not shared" vs "shared but
    // no activity yet" are different things.
    const linksInScope = allLinks.filter((l) =>
      matchScope(l.role, l.isYou),
    ).length;

    return {
      agg: { clicks, visitors, sessions, tours, leads: leadsInScope.length },
      metaModelViews,
      digitalTwinViews,
      timeSpentMin,
      leadsInScope,
      scopeTabs,
      peopleCount,
      linksInScope,
    };
  }, [
    isLive,
    rawLinks,
    rawClicks,
    roleByEmail,
    myCodes,
    dateRange,
    selectedProjectId,
    scope,
    viewerRole,
    ownCustomerEmails,
    myAgentEmails,
    myAgentCustomerEmails,
    agencySessions,
    businessAdminById,
    systemAdminById,
    agentNameById,
    nameByEmail,
    roleByEmailDir,
    customerContactById,
    projectNameById,
  ]);

  // ── Demo fallback model ───────────────────────────────────────────────
  const people = SHARE_PEOPLE_DEMO;
  const countOf = (r: ShareRole) => people.filter((p) => p.role === r).length;
  const demoScopeTabsAll: { key: ShareScopeKey; label: string; count: number }[] = [
    { key: "everyone", label: "Everyone", count: people.length },
    { key: "you", label: "You (Sivaram)", count: countOf("you") },
    { key: "system_admin", label: "System Admins", count: countOf("system_admin") },
    { key: "agent", label: "Agents", count: countOf("agent") },
    { key: "customer", label: "Customers", count: countOf("customer") },
  ];
  const demoScopeTabs = demoScopeTabsAll.filter((t) => {
    if (t.key === "everyone" || t.key === "you") return true;
    if (viewerRole === "aba") return true;
    if (viewerRole === "asa") return t.key === "agent" || t.key === "customer";
    if (viewerRole === "agent") return t.key === "customer";
    return false;
  });
  const demoInScope =
    scope === "everyone" ? people : people.filter((p) => p.role === scope);
  const demoAgg = demoInScope.reduce(
    (a, p) => ({
      clicks: a.clicks + p.clicks,
      visitors: a.visitors + p.visitors,
      sessions: a.sessions + p.sessions,
      // Demo has no click bundling — treat each session as its own tour.
      tours: a.tours + p.sessions,
      leads: a.leads + p.leads,
    }),
    { clicks: 0, visitors: 0, sessions: 0, tours: 0, leads: 0 },
  );
  const demoLeads =
    scope === "everyone"
      ? SHARE_LEADS_DEMO
      : SHARE_LEADS_DEMO.filter((l) => l.role === scope);

  // ── Active model: live when ready; demo ONLY on real error; zeros while the
  // first fetch is in flight (Platform pattern: 0s + spinner → values populate).
  const useDemo = !liveModel && liveError;
  const zeroScopeTabsAll: { key: ShareScopeKey; label: string; count: number }[] = [
    { key: "everyone", label: "Everyone", count: 0 },
    { key: "you", label: "You", count: 0 },
    { key: "system_admin", label: "System Admins", count: 0 },
    { key: "agent", label: "Agents", count: 0 },
    { key: "customer", label: "Customers", count: 0 },
  ];
  const zeroScopeTabs = zeroScopeTabsAll.filter((t) => {
    if (t.key === "everyone" || t.key === "you") return true;
    if (viewerRole === "aba") return true;
    if (viewerRole === "asa") return t.key === "agent" || t.key === "customer";
    if (viewerRole === "agent") return t.key === "customer";
    return false;
  });
  const agg = liveModel
    ? liveModel.agg
    : useDemo
      ? demoAgg
      : { clicks: 0, visitors: 0, sessions: 0, tours: 0, leads: 0 };
  const scopeTabs = liveModel
    ? liveModel.scopeTabs
    : useDemo
      ? demoScopeTabs
      : zeroScopeTabs;
  const leadsInScope: ShareLead[] = liveModel
    ? liveModel.leadsInScope
    : useDemo
      ? demoLeads
      : [];
  const peopleCount = liveModel
    ? liveModel.peopleCount
    : useDemo
      ? people.length
      : 0;
  const fmtDuration = (m: number) =>
    m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
  const timeSpentMin = liveModel
    ? liveModel.timeSpentMin
    : useDemo
      ? Math.round(agg.sessions * 4.6)
      : 0;
  const metaModelViews = liveModel
    ? liveModel.metaModelViews
    : useDemo
      ? Math.round(agg.sessions * 0.62)
      : 0;
  const digitalTwinViews = liveModel
    ? liveModel.digitalTwinViews
    : useDemo
      ? agg.sessions - Math.round(agg.sessions * 0.62)
      : 0;

  // Conversion rates surfaced directly on the relevant cards (no funnel):
  // Tours as a % of clicks (engagement rate — how many clicks led to a real
  // visit). Views deliberately has NO "% of clicks" since views can exceed
  // clicks (multiple experiences per visit), which would read as >100%.
  const toursPerClick =
    agg.clicks > 0 ? Math.round((agg.tours / agg.clicks) * 100) : 0;
  // Leads as a % of tours (visits) — leads per visit reads as a clean funnel
  // step (Clicks → Visitors → Tours → Leads), unlike leads-per-view which is
  // skewed by visitors opening many experiences.
  const leadsPerTour =
    agg.tours > 0 ? Math.round((agg.leads / agg.tours) * 100) : 0;
  // Meta Model / Digital Twin as a share of total Views (matches Platform).
  const metaPct =
    agg.sessions > 0 ? Math.round((metaModelViews / agg.sessions) * 100) : 0;
  const dtPct =
    agg.sessions > 0 ? Math.round((digitalTwinViews / agg.sessions) * 100) : 0;

  const LEADS_PAGE_SIZE = 10;
  const leadsPageCount = Math.max(
    1,
    Math.ceil(leadsInScope.length / LEADS_PAGE_SIZE),
  );
  const pagedLeads = leadsInScope.slice(
    leadsPage * LEADS_PAGE_SIZE,
    leadsPage * LEADS_PAGE_SIZE + LEADS_PAGE_SIZE,
  );

  // Scope-aware empty copy: for "you"/"everyone" the current user can act →
  // show the Share CTA; for another role's scope the gap is theirs to fill, so
  // change the message and drop the CTA (you can't share on their behalf).
  const scopeLabel =
    scope === "system_admin"
      ? "system admin"
      : scope === "agent"
        ? "agent"
        : scope === "customer"
          ? "customer"
          : "";
  const scopeLabelPlural =
    scope === "system_admin"
      ? "system admins"
      : scope === "agent"
        ? "agents"
        : scope === "customer"
          ? "customers"
          : "";
  const ownEmptyScope = scope === "you" || scope === "everyone";
  const emptyProj = projectName || "this project";
  const emptyTitle =
    scope === "you"
      ? `You haven't shared ${emptyProj} yet`
      : scope === "everyone"
        ? `${projectName || "This project"} hasn't been shared by anyone yet`
        : `None of the ${scopeLabelPlural} have shared ${emptyProj} yet`;
  const emptySub = ownEmptyScope
    ? "Open the project page and hit Share to mint a link — clicks, sessions and leads will then show up here."
    : `Once a ${scopeLabel} shares it, their clicks, sessions and leads will show up here.`;

  const noScopeData =
    agg.clicks === 0 &&
    agg.visitors === 0 &&
    agg.sessions === 0 &&
    agg.leads === 0;
  // Does a share LINK exist for this project + scope? (independent of any
  // clicks/sessions). This — not the numbers — decides "not shared" vs
  // "shared but no activity yet".
  const hasSharesInScope = liveModel ? liveModel.linksInScope > 0 : false;
  // First load: live data still fetching (no model yet, no error). Cards show
  // zeros + the top refresh spinner — never the empty-state or demo.
  const showLoading = liveLoading && !liveModel && !liveError;
  // Empty prompt = project filtered and genuinely NOT shared in this scope (no
  // link). Demo keeps showing empty for any selected project (not project-aware).
  const showEmpty =
    !showLoading && !!selectedProjectId && (useDemo || !hasSharesInScope);
  // Shared, but nothing's happened yet (link exists, zero clicks/sessions).
  const showNoActivity =
    !!liveModel && !!selectedProjectId && hasSharesInScope && noScopeData;

  return (
    <div className="flex flex-col gap-3">
      {/* Demo-fallback banner — only when the live fetch genuinely FAILED */}
      {useDemo && (
        <div className="flex items-center gap-2 text-[10px] text-amber-200/90 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-1.5">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
          Demo data — live agency numbers unavailable (share-link endpoints not reachable).
        </div>
      )}

      {/* "Shared by" role scope — managers only; agents/customers see own */}
      {showScopeChips && (
        <div className="rounded-xl bg-[#15333D] border border-[#2C4D5A] p-3">
          <div className="flex items-baseline justify-between mb-2.5">
            <span className="text-[10px] font-semibold text-[#889DB2] uppercase tracking-widest">
              Shared by
            </span>
            <span className="text-[10px] text-[#5A7C8C]">
              {peopleCount} people in scope
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {scopeTabs.map((t) => {
              const active = scope === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setScope(t.key)}
                  className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium border transition-colors ${
                    active
                      ? "bg-[#008564]/15 border-[#34D399] text-[#34D399]"
                      : "border-[#2C4D5A] text-[#889DB2] hover:text-white hover:border-[#5A7C8C]"
                  }`}
                >
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: chipDotColor(t.key) }}
                  />
                  {t.label}
                  <span
                    className={`text-[10px] min-w-[20px] h-[20px] inline-flex items-center justify-center px-1.5 rounded-full font-semibold ${
                      active
                        ? "bg-[#34D399] text-[#0C2420]"
                        : "bg-[#1F3D48] text-[#889DB2]"
                    }`}
                  >
                    {t.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Project filtered but unshared (in this scope) → empty prompt;
          otherwise the KPI cards. The "Shared by" control above stays visible
          so a manager can switch scope (a project may be shared by an SA or
          agent even if not by them, or by nobody). */}
      {showEmpty ? (
        <div className="rounded-xl bg-[#15333D] border border-[#2C4D5A] p-8 flex flex-col items-center text-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#1F3D48] flex items-center justify-center">
            {IconUserPlus("text-[#5A7C8C]")}
          </div>
          <div className="text-white font-semibold text-[15px]">{emptyTitle}</div>
          <div className="text-[13px] text-[#889DB2] max-w-md">{emptySub}</div>
          {ownEmptyScope && (
            <button
              type="button"
              onClick={onOpenProjectPage}
              className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-[#34D399] hover:text-white border border-[#34D399]/40 hover:border-[#34D399] rounded-lg px-4 py-2 transition-colors"
            >
              Open project page →
            </button>
          )}
        </div>
      ) : (
      <>
      {showNoActivity && (
        <div className="flex items-center gap-2 text-[11px] text-[#889DB2] bg-[#15333D] border border-[#2C4D5A] rounded-lg px-3 py-2">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#34D399] flex-shrink-0" />
          {projectName || "This project"} is shared{scope !== "everyone" ? " in this scope" : ""} — no clicks or sessions yet.
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        <KpiCard icon={IconClick("text-blue-400")} iconBg="rgba(74,158,255,0.15)" accent="#60a5fa" value={agg.clicks.toLocaleString()} label="Clicks" subtext={{ text: "Times the link was opened", color: "#5A7C8C" }} />
        <KpiCard icon={IconUsers("text-lime-400")} iconBg="rgba(163,230,53,0.15)" accent="#a3e635" value={agg.visitors.toLocaleString()} label="Visitors" subtext={{ text: "Unique link openers", color: "#5A7C8C" }} />
        <KpiCard icon={IconVR("text-teal-400")} iconBg="rgba(45,212,191,0.15)" accent="#2dd4bf" value={agg.tours.toLocaleString()} label="Tours" share={{ percent: toursPerClick, color: "#2dd4bf", label: "of clicks" }} subtext={{ text: "Clicks that led to a view", color: "#5A7C8C" }} />
        <KpiCard icon={IconUserPlus("text-amber-400")} iconBg="rgba(251,191,36,0.15)" accent="#fbbf24" value={agg.leads.toLocaleString()} label="Leads Generated" share={{ percent: leadsPerTour, color: "#fbbf24", label: "of tours" }} subtext={{ text: "Contacts captured from visits", color: "#5A7C8C" }} />
        <KpiCard icon={IconEye("text-emerald-400")} iconBg="rgba(52,211,153,0.15)" accent="#34d399" value={agg.sessions.toLocaleString()} label="Views" subtext={{ text: "Total experiences opened", color: "#5A7C8C" }} />
        <KpiCard icon={IconCube("text-indigo-400")} iconBg="rgba(129,140,248,0.15)" accent="#818cf8" value={metaModelViews.toLocaleString()} label="Meta Model Views" share={{ percent: metaPct, color: "#818cf8" }} subtext={{ text: "Opens of the 3D meta model", color: "#5A7C8C" }} />
        <KpiCard icon={IconBuilding("text-fuchsia-400")} iconBg="rgba(232,121,249,0.15)" accent="#e879f9" value={digitalTwinViews.toLocaleString()} label="Digital Twin Views" share={{ percent: dtPct, color: "#e879f9" }} subtext={{ text: "Opens of the Matterport tour", color: "#5A7C8C" }} />
        <KpiCard icon={IconClock("text-cyan-400")} iconBg="rgba(34,211,238,0.15)" accent="#22d3ee" value={fmtDuration(timeSpentMin)} label="Time Spent" subtext={{ text: "Total time across all views", color: "#5A7C8C" }} />
      </div>

      {/* Attributed Leads — the actual leads behind the count, for the
          selected scope. Row count matches the Leads Generated card. */}
      <div className="rounded-xl bg-[#15333D] border border-[#2C4D5A] p-4">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="text-[10px] font-semibold text-[#889DB2] uppercase tracking-widest">
            Attributed Leads
          </h3>
          <span className="text-[10px] text-[#5A7C8C]">{leadsInScope.length} leads</span>
        </div>
        {leadsInScope.length === 0 ? (
          <p className="text-[#889DB2] text-sm text-center py-6">
            No attributed leads in this scope yet.
          </p>
        ) : (
          <div className="overflow-x-auto analytics-scroll">
            <table className="w-full text-[12px] border-separate border-spacing-0">
              <thead>
                <tr className="text-[#889DB2] text-[10px] uppercase tracking-wider">
                  <th className="text-left py-2 px-2 font-medium">Date</th>
                  <th className="text-left py-2 px-2 font-medium">Lead Name</th>
                  <th className="text-left py-2 px-2 font-medium">Phone Number</th>
                  <th className="text-left py-2 px-2 font-medium">Project</th>
                  <th className="text-left py-2 px-2 font-medium">Attributed to</th>
                  <th className="text-left py-2 px-2 font-medium">Channel</th>
                </tr>
              </thead>
              <tbody>
                {pagedLeads.map((l) => (
                  <tr
                    key={l.leadId}
                    className="border-t border-[#2C4D5A] hover:bg-white/[0.02]"
                  >
                    <td className="py-2.5 px-2 text-white whitespace-nowrap border-t border-[#2C4D5A]">
                      {formatSessionDate(
                        l.dateIso ||
                          new Date(Date.now() - l.daysAgo * 86400000).toISOString()
                      )}
                    </td>
                    <td className="py-2.5 px-2 text-[#E5E7EB] truncate max-w-[160px] border-t border-[#2C4D5A]">
                      {l.name}
                    </td>
                    <td className="py-2.5 px-2 text-[#E5E7EB] whitespace-nowrap border-t border-[#2C4D5A]">
                      {l.phone}
                    </td>
                    <td className="py-2.5 px-2 text-[#E5E7EB] truncate max-w-[160px] border-t border-[#2C4D5A]">
                      {l.project}
                    </td>
                    <td className="py-2.5 px-2 border-t border-[#2C4D5A]">
                      <div className="flex flex-col gap-1 items-start">
                        <span className="text-white truncate max-w-[180px]">{l.sharedBy}</span>
                        <Pill color={ROLE_BADGE[l.role].color}>{ROLE_BADGE[l.role].label}</Pill>
                      </div>
                    </td>
                    <td className="py-2.5 px-2 text-[#E5E7EB] capitalize border-t border-[#2C4D5A]">
                      {l.channel}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {leadsInScope.length > LEADS_PAGE_SIZE && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#2C4D5A]">
            <span className="text-[11px] text-[#889DB2]">
              Showing {leadsPage * LEADS_PAGE_SIZE + 1}–
              {Math.min((leadsPage + 1) * LEADS_PAGE_SIZE, leadsInScope.length)} of{" "}
              {leadsInScope.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setLeadsPage((p) => Math.max(0, p - 1))}
                disabled={leadsPage === 0}
                className="text-[11px] font-medium px-3 py-1.5 rounded-md bg-[#2E4051] hover:bg-[#3A4E62] text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ← Prev
              </button>
              <span className="text-[11px] text-[#889DB2] tabular-nums">
                Page {leadsPage + 1} of {leadsPageCount}
              </span>
              <button
                type="button"
                onClick={() => setLeadsPage((p) => Math.min(leadsPageCount - 1, p + 1))}
                disabled={leadsPage >= leadsPageCount - 1}
                className="text-[11px] font-medium px-3 py-1.5 rounded-md bg-[#2E4051] hover:bg-[#3A4E62] text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
};

/* The four analytics sections, and the copy that names each one. Kept in
   one place because the rail, the breadcrumb and the section header all
   need to agree, and three nested ternaries across 900 lines is how they
   stop agreeing. */
export type AnalyticsSection = "platform" | "share" | "guided" | "embed";

export const SECTION_COPY: Record<AnalyticsSection, { title: string; blurb: string; sub: string }> = {
  platform: {
    title: "Platform Analytics",
    blurb: "Sessions and visitors generated within the OpenSky platform.",
    sub: "Views · sessions · leads",
  },
  share: {
    title: "Share Analytics",
    blurb: "Clicks, sessions, leads and attribution from your shared project links.",
    sub: "Link clicks · attribution",
  },
  guided: {
    title: "Guided Tour Analytics",
    blurb: "Live tours hosted, who attended, how long they ran and what came of them.",
    sub: "Tours · attendance · leads",
  },
  embed: {
    title: "Embed Analytics",
    blurb: "Walkthroughs opened from your own website, by site and by page.",
    sub: "Sites · loads · sessions",
  },
};

/* ── Section awaiting its design ──────────────────────────────────────
   Deliberately not a mock. A placeholder chart with invented numbers is
   read as data by whoever walks past the screen, and this page is shown
   to agencies. It names what the section will hold and what it needs, so
   an empty tab is informative rather than broken. */
const SectionPending: React.FC<{ title: string; blurb: string; needs: string[] }> = ({
  title, blurb, needs,
}) => (
  <section className="mt-3 rounded-xl bg-[#15333D] border border-[#2C4D5A] p-6 max-w-screen-2xl">
    <h3 className="text-white text-base font-semibold">{title}</h3>
    <p className="text-[13px] text-[#889DB2] mt-1 max-w-2xl">{blurb}</p>
    <div className="mt-4 border-t border-[#2C4D5A] pt-4">
      <p className="text-[11px] uppercase tracking-wide text-[#5A7C8C]">Waiting on</p>
      <ul className="mt-2 space-y-1.5">
        {needs.map((n) => (
          <li key={n} className="flex items-start gap-2 text-[13px] text-[#889DB2]">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#5A7C8C] flex-shrink-0" />
            <span>{n}</span>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

/* ── Embed Analytics ───────────────────────────────────────────────────
   Walkthroughs opened from a builder's own website. The counting lives in
   utils/embedAnalytics (pure, tested); this renders it. Sessions and customers
   come from the parent, which already holds them for Platform and Share; the
   only fetch here is the embed keys, one call per project, so embeds nobody
   has opened yet still show — and so a row can name its tour. */
const EmbedAnalytics: React.FC<{
  active: boolean;
  refreshTick: number;
  dateRange: DateRangeKey;
  selectedProjectId: string;
  projects: any[];
  sessions: any[];
  customers: any[];
  projectNameById: Map<string, string>;
  matterportNameBySid: Map<string, string>;
  metaModelNameById: Map<string, string>;
  /** The parent couldn't load its own data — we never get to ask for keys. */
  projectsFailed?: boolean;
}> = ({
  active, refreshTick, dateRange, selectedProjectId, projects, sessions, customers,
  projectNameById, matterportNameBySid, metaModelNameById, projectsFailed,
}) => {
  const [keys, setKeys] = useState<EmbedKeyInfo[]>([]);
  /* site_ids still on an allowlist, across every project. The settings endpoint omits
     soft-removed entries, so a placement whose site_id is missing here is bound to a
     website that was deleted — dead to visitors, whatever its own fields say. */
  const [liveSiteIds, setLiveSiteIds] = useState<Set<string>>(new Set());
  const [keysLoaded, setKeysLoaded] = useState(false);
  const [failedProjects, setFailedProjects] = useState(0);
  const [attempt, setAttempt] = useState(0);      // bumped by Retry
  const fetchedRef = React.useRef<string>("");

  useEffect(() => {
    if (!active || !projects.length) return undefined;
    const sig = `${refreshTick}:${attempt}:${projects.length}`;
    if (fetchedRef.current === sig) return undefined;
    fetchedRef.current = sig;
    setKeysLoaded(false);
    (async () => {
      const all: EmbedKeyInfo[] = [];
      const liveSites = new Set<string>();
      let failed = 0;
      await Promise.all(
        projects.map(async (p: any) => {
          if (!p?._id) return;
          try {
            // Reporting, not management: ask for revoked placements too, so a row that
            // exists only because of past sessions or leads can say WHY it is dead.
            const r: any = await listEmbedKeys(p._id, { includeRevoked: true });
            const hostById = new Map<string, string>(
              (r?.embed_settings?.allowed_domains || []).map((d: any) => [d.id, d.host]),
            );
            // Soft-removed domains never come back from this endpoint, so the ids present
            // here are exactly the websites still able to show anything.
            hostById.forEach((_h, id) => liveSites.add(String(id)));
            for (const k of r?.embed_keys || []) {
              all.push({ ...k, project_id: k.project_id || p._id, site_host: k.site_host || hostById.get(k.site_id) || null });
            }
          } catch (e: any) {
            // A 404 means this project has no embed settings yet — genuinely nothing to
            // show. Anything else means we DON'T KNOW, and the difference has to survive
            // all the way to the screen: an unasked question must never render as a zero.
            if (e?.response?.status !== 404) failed += 1;
          }
        }),
      );
      // `fetchedRef` is the generation token: only the newest fetch may write. It used to
      // be a separate `alive` flag cleared on cleanup, and `projects` is a fresh array on
      // every render — so a re-render mid-fetch cancelled the run in flight, and the re-run
      // that replaced it returned immediately on the unchanged signature. Nothing ever
      // called setKeysLoaded, and the section sat on "Loading…" for good.
      if (fetchedRef.current !== sig) return;
      setKeys(all);
      setLiveSiteIds(liveSites);
      setFailedProjects(failed);
      setKeysLoaded(true);
    })();
    return undefined;
  }, [active, refreshTick, attempt, projects]);

  const loadState = embedSectionState({
    active,
    projectsFailed: !!projectsFailed,
    projectCount: projects.length,
    failedCount: failedProjects,
    done: keysLoaded,
  });
  const ready = loadState === "ready";
  const retry = () => {
    setFailedProjects(0);
    setAttempt((a) => a + 1);
  };

  const summary = React.useMemo(() => {
    const { startDate, endDate } = computeDateRange(dateRange);
    return summarizeEmbeds({
      sessions,
      customers,
      keys,
      liveSiteIds,
      startMs: startDate ? new Date(startDate + "T00:00:00Z").getTime() : -Infinity,
      endMs: endDate ? new Date(endDate + "T23:59:59.999Z").getTime() : Infinity,
      projectId: selectedProjectId || undefined,
    });
  }, [sessions, customers, keys, liveSiteIds, dateRange, selectedProjectId]);

  const tourName = (kind: string, id: string) =>
    (kind === "meta_model" ? metaModelNameById.get(id) : matterportNameBySid.get(id)) || "";
  const when = (t: number | null) =>
    t == null ? "—" : new Date(t).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  const td = "py-2.5 px-2 text-[#E5E7EB] border-t border-[#2C4D5A]";
  const muted = <span className="text-[#889DB2]">—</span>;
  const hasMeta = summary.rows.some((r) => r.experienceKind === "meta_model");

  return (
    <>
      {/* A number here is a claim about the customer's business. Until the data is
          actually in, these show a dash: "0 leads" and "we couldn't ask" must never
          look the same. */}
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-screen-2xl">
        <KpiCard icon={IconBuilding("text-violet-300")} iconBg="rgba(196,181,253,0.15)" accent="#c4b5fd"
          value={ready ? summary.websites.toLocaleString() : "—"} label="Websites" />
        <KpiCard icon={IconEye("text-blue-400")} iconBg="rgba(74,158,255,0.15)" accent="#60a5fa"
          value={ready ? summary.opens.toLocaleString() : "—"} label="Tour Opens" />
        <KpiCard icon={IconUsers("text-lime-400")} iconBg="rgba(163,230,53,0.15)" accent="#a3e635"
          value={ready ? summary.visitors.toLocaleString() : "—"} label="Visitors" />
        <KpiCard icon={IconUserPlus("text-emerald-400")} iconBg="rgba(52,211,153,0.15)" accent="#34d399"
          value={ready ? summary.leads.toLocaleString() : "—"} label="Leads"
          subtext={ready && summary.visitors > 0
            ? { text: `${Math.round((summary.leads / summary.visitors) * 100)}% of visitors`, color: "#6EE7B7" }
            : undefined} />
      </div>

      {loadState === "error" && (
        <section className="mt-3 rounded-xl bg-[#3B2430] border border-[#7F1D1D] p-4 max-w-screen-2xl">
          <h3 className="text-sm font-semibold text-white">Couldn&apos;t load your embed data</h3>
          <p className="text-[13px] text-[#E5B4B4] mt-1">
            These numbers are unknown right now, not zero. Nothing has been lost — the walkthroughs
            and any leads they captured are unaffected.
          </p>
          <button
            type="button"
            onClick={retry}
            className="mt-3 text-[12px] font-medium px-3 py-1.5 rounded-md bg-[#008564]/20 text-[#34D399] border border-[#008564]/50 hover:bg-[#008564]/30 transition-colors"
          >
            Try again
          </button>
        </section>
      )}

      <section className="mt-3 rounded-xl bg-[#15333D] border border-[#2C4D5A] p-4 max-w-screen-2xl">
        <div className="flex items-baseline gap-2 mb-3">
          <h3 className="text-sm font-semibold text-white">By website</h3>
          <span className="text-[11px] text-[#889DB2]">
            {loadState === "loading" ? "Loading…"
              : loadState === "error" ? "Couldn't load"
                : `${summary.rows.length} embed${summary.rows.length === 1 ? "" : "s"}`}
          </span>
        </div>
        {!ready ? (
          <p className="text-[13px] text-[#889DB2]">
            {loadState === "loading" ? "Loading your embeds…" : "We couldn't read your embeds just now."}
          </p>
        ) : summary.rows.length === 0 ? (
          <p className="text-[13px] text-[#889DB2]">
            No website embeds yet. Publish a tour from a project&apos;s page to put it on your own site.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px] border-separate border-spacing-0">
              <thead>
                <tr className="text-[#889DB2] text-[10px] uppercase tracking-wider">
                  <th className="text-left py-2 px-2 font-medium">Website</th>
                  <th className="text-left py-2 px-2 font-medium">Tour</th>
                  <th className="text-left py-2 px-2 font-medium">Project</th>
                  <th className="text-right py-2 px-2 font-medium">Opens</th>
                  <th className="text-right py-2 px-2 font-medium">Visitors</th>
                  <th className="text-right py-2 px-2 font-medium">Avg time</th>
                  <th className="text-right py-2 px-2 font-medium">Leads</th>
                  <th className="text-left py-2 px-2 font-medium">Last opened</th>
                </tr>
              </thead>
              <tbody>
                {summary.rows.map((r) => (
                  <tr key={r.key}>
                    <td className={td}>
                      <div className="flex items-center gap-2">
                        <span className="text-white">{r.domain || "—"}</span>
                        {/* Why it isn't showing, in the words that match what staff can do
                            about it: an unpublished embed is one tick away, a removed website
                            needs re-adding, a revoked key is gone for good. */}
                        {r.state === "unpublished" && <Pill color="#9CA3AF">Unpublished</Pill>}
                        {r.state === "site_removed" && <Pill color="#FB7185">Website removed</Pill>}
                        {r.state === "revoked" && <Pill color="#FB7185">Revoked</Pill>}
                        {/* History with no placement behind it. Its numbers still count —
                            that is why the row is here — but it is not running. */}
                        {r.state === "ended" && <Pill color="#9CA3AF">Ended</Pill>}
                      </div>
                    </td>
                    <td className={td}>
                      <div className="flex flex-col gap-1 items-start">
                        <span className="text-white truncate max-w-[180px]">
                          {r.label || tourName(r.experienceKind, r.experienceId) || r.experienceId || "—"}
                        </span>
                        <Pill color={r.experienceKind === "meta_model" ? "#C4B5FD" : "#38BDF8"}>
                          {r.experienceKind === "meta_model" ? "Meta Model" : "Digital Twin"}
                        </Pill>
                      </div>
                    </td>
                    <td className={td}>{projectNameById.get(r.projectId) || muted}</td>
                    <td className={`${td} text-right`}>
                      {r.experienceKind === "meta_model" && r.opens === 0 ? muted : r.opens.toLocaleString()}
                    </td>
                    <td className={`${td} text-right`}>
                      {r.experienceKind === "meta_model" && r.opens === 0 ? muted : r.visitors.toLocaleString()}
                    </td>
                    <td className={`${td} text-right`}>
                      {r.opens ? formatDuration(Math.round(r.totalSeconds / r.opens)) : muted}
                    </td>
                    <td className={`${td} text-right`}>{r.leads.toLocaleString()}</td>
                    <td className={td}>{when(r.lastOpenedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {ready && failedProjects > 0 && (
          <p className="mt-3 text-[11px] text-[#E5B4B4]">
            {failedProjects} of {projects.length} projects couldn&apos;t be read, so these totals may be
            low.{" "}
            <button type="button" onClick={retry} className="underline hover:text-white">Try again</button>
          </p>
        )}
        {hasMeta && (
          <p className="mt-3 text-[11px] text-[#5A7C8C]">
            Meta-model embeds count leads only for now. Opens and visitors need the 3D engine to report
            which embed it was opened from.
          </p>
        )}
      </section>

      <section className="mt-3 rounded-xl bg-[#15333D] border border-[#2C4D5A] p-4 max-w-screen-2xl">
        <div className="flex items-baseline gap-2 mb-3">
          <h3 className="text-sm font-semibold text-white">Leads from your website</h3>
          <span className="text-[11px] text-[#889DB2]">
            {ready ? `${summary.recentLeads.length.toLocaleString()} total` : "—"}
          </span>
        </div>
        {!ready ? (
          <p className="text-[13px] text-[#889DB2]">
            {loadState === "loading" ? "Loading…" : "Unknown right now — this isn't a count of zero."}
          </p>
        ) : summary.recentLeads.length === 0 ? (
          <p className="text-[13px] text-[#889DB2]">No leads from embeds in this period.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px] border-separate border-spacing-0">
              <thead>
                <tr className="text-[#889DB2] text-[10px] uppercase tracking-wider">
                  <th className="text-left py-2 px-2 font-medium">Date</th>
                  <th className="text-left py-2 px-2 font-medium">Name</th>
                  <th className="text-left py-2 px-2 font-medium">Phone</th>
                  <th className="text-left py-2 px-2 font-medium">Website</th>
                  <th className="text-left py-2 px-2 font-medium">Project</th>
                </tr>
              </thead>
              <tbody>
                {summary.recentLeads.slice(0, 50).map((l) => (
                  <tr key={l.id || `${l.mobile}-${l.createdAt}`}>
                    <td className={td}>{when(l.createdAt)}</td>
                    <td className={`${td} text-white`}>{l.name}</td>
                    <td className={td}>{l.mobile || muted}</td>
                    <td className={td}>{l.domain || muted}</td>
                    <td className={td}>{projectNameById.get(l.projectId) || muted}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
};

/* ── Collapsible left rail for the analytics sections ──
   Standard SaaS sidebar: two rows (Platform / Share). Collapses to an
   icon-only rail via the chevron; labels hide but the active highlight
   stays. Matches the AnalyticsV3 teal palette. Mobile: stacks full-width
   above the content (flex-col on the parent). */
const AnalyticsSideNav: React.FC<{
  section: AnalyticsSection;
  onSection: (s: AnalyticsSection) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  /* Filter controls live here as children so they keep direct access to
     the page's state/handlers. Hidden when the rail is collapsed. */
  children?: React.ReactNode;
}> = ({ section, onSection, collapsed, onToggleCollapse, children }) => {
  const allItems: Array<{
    key: AnalyticsSection;
    label: string;
    sub: string;
    icon: JSX.Element;
  }> = [
    {
      key: "platform",
      label: "Platform Analytics",
      sub: "Views · sessions · leads",
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
        </svg>
      ),
    },
    {
      key: "share",
      label: "Share Analytics",
      sub: "Link clicks · attribution",
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
        </svg>
      ),
    },
    {
      key: "guided",
      label: SECTION_COPY.guided.title,
      sub: SECTION_COPY.guided.sub,
      /* A frame with a play tab — a hosted session, not a broadcast. */
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      key: "embed",
      label: SECTION_COPY.embed.title,
      sub: SECTION_COPY.embed.sub,
      /* Angle brackets — the snippet someone pastes into their own site. */
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
    },
  ];
  // Customers get Share Analytics only — no Platform Analytics (per the role
  // matrix). All other roles (ABA/ASA/agent) see both sections.
  // isCustomerOnly (not hasRole) because Keycloak roles are additive: an agent
  // who was a lead first still carries the `customer` realm role, and a bare
  // hasRole check would strip Platform Analytics from a legitimate agent.
  /* A customer sees Share ONLY. Guided Tour and Embed are agency-side
     views — how the agency is performing, not what one buyer did. An
     allow-list rather than excluding Platform by name, so the next
     section added is private by default rather than public by accident. */
  /* Guided tours are held out of production by featureFlags.guidedTourEnabled, which hides the
     Guided Tour button and the room routes. THIS section escaped that gate: the nav item, and
     the "coming soon" panel behind it, still rendered in prod — naming a feature to a paying
     client that they cannot reach anywhere in the product. Same flag, so the section comes
     back on the day guided tours ship, with nothing further to remember. */
  const shipped = guidedTourEnabled()
    ? allItems
    : allItems.filter((i) => i.key !== "guided");
  const items = UserService.isCustomerOnly()
    ? shipped.filter((i) => i.key === "share")
    : shipped;

  return (
    <aside
      className={`analytics-scroll w-full flex-shrink-0 rounded-xl bg-[#15333D] border border-[#2C4D5A] p-2 transition-all duration-200 md:h-full md:min-h-0 md:overflow-y-auto ${
        collapsed ? "md:w-[58px]" : "md:w-64"
      }`}
    >
      {/* collapse toggle (desktop affordance) */}
      <button
        type="button"
        onClick={onToggleCollapse}
        title={collapsed ? "Expand" : "Collapse"}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        className="hidden md:flex w-full items-center justify-end text-[#889DB2] hover:text-white px-2 py-1.5 mb-1"
      >
        <svg
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
          className={`transition-transform ${collapsed ? "rotate-180" : ""}`}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
        </svg>
      </button>

      <nav className="flex flex-row md:flex-col gap-1">
        {items.map((it) => {
          const active = section === it.key;
          return (
            <button
              key={it.key}
              type="button"
              onClick={() => onSection(it.key)}
              title={collapsed ? it.label : undefined}
              className={`group flex flex-1 md:flex-initial items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                active
                  ? "bg-[#008564]/15 text-white"
                  : "text-[#889DB2] hover:bg-white/5 hover:text-white"
              } ${collapsed ? "md:justify-center md:px-0" : ""}`}
            >
              <span className={`flex-shrink-0 ${active ? "text-[#34D399]" : ""}`}>{it.icon}</span>
              <span className={`min-w-0 flex-1 ${collapsed ? "md:hidden" : ""}`}>
                <span className="block text-sm font-medium truncate">{it.label}</span>
                <span className="block text-[10px] text-[#5A7C8C] truncate">{it.sub}</span>
              </span>
              {active && (
                <span
                  className={`w-1.5 h-1.5 rounded-full bg-[#34D399] flex-shrink-0 ${
                    collapsed ? "md:hidden" : ""
                  }`}
                />
              )}
            </button>
          );
        })}
      </nav>

      {!collapsed && children && (
        <div className="mt-3 pt-3 border-t border-[#2C4D5A] flex flex-col gap-3">
          {children}
        </div>
      )}
    </aside>
  );
};

const AnalyticsV3: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();

  const [hasMetaModel, setHasMetaModel] = useState(false);
  const [hasDigitalTwin, setHasDigitalTwin] = useState(false);
  const [allProjects, setAllProjects] = useState<any[]>([]);
  /* The project fetch failing is not the same as an agency having no projects, and the
     sections that count things need to know which it was. */
  const [projectsFailed, setProjectsFailed] = useState(false);
  // Single source of truth — all sessions fetched once for the agency.
  // Filtering by date range happens client-side because the backend's
  // start_date/end_date query params are not honoured (same as v2).
  const [allAgencySessions, setAllAgencySessions] = useState<any[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  // Share Analytics reports its own loading up so the shared top refresh-spinner
  // reflects whichever section is active.
  const [shareLoading, setShareLoading] = useState(false);
  // Incrementing this re-runs the session + customer fetches without a
  // full page reload. Bumped by the refresh button in the header.
  const [refreshTick, setRefreshTick] = useState(0);
  const [sessionsPage, setSessionsPage] = useState(0);
  const [dateRange, setDateRange] = useState<DateRangeKey>("lifetime");
  const [agencyId, setAgencyId] = useState<string | undefined>(undefined);
  const [csvBusy, setCsvBusy] = useState(false);
  const [assignedLeads, setAssignedLeads] = useState<number | null>(null);
  const [unassignedLeads, setUnassignedLeads] = useState<number | null>(null);
  const [businessAdminById, setBusinessAdminById] = useState<Map<string, string>>(new Map());
  const [systemAdminById, setSystemAdminById] = useState<Map<string, string>>(new Map());
  const [agentNameById, setAgentNameById] = useState<Map<string, string>>(new Map());
  // Sharer-name directory keyed by lower-cased email (built from the BA / SA /
  // agent lists). Email is what the share link reliably carries, so this is the
  // resolution that actually fixes "Sales" → the real sharer name.
  const [nameByEmail, setNameByEmail] = useState<Map<string, string>>(new Map());
  // Authoritative sharer-role-by-email (from the BA/SA/agent lists). The source
  // of truth for share-link attribution — fixes a System Admin being badged
  // "Business Admin" (own share) or "Customer" (the /user/roles endpoint).
  const [roleByEmailDir, setRoleByEmailDir] = useState<Map<string, ShareRole>>(new Map());
  const [matterportNameBySid, setMatterportNameBySid] = useState<Map<string, string>>(new Map());
  const [metaModelNameById, setMetaModelNameById] = useState<Map<string, string>>(new Map());
  const [projectsWithMetaModel, setProjectsWithMetaModel] = useState<Set<string>>(new Set());
  const [matterportsByProject, setMatterportsByProject] = useState<Map<string, Array<{ sid: string; name: string }>>>(new Map());
  const [metaModelsByProject, setMetaModelsByProject] = useState<Map<string, Array<{ id: string; name: string }>>>(new Map());
  // Maps internal user id -> { name, mobile } for each role tier. Used to
  // hydrate expanded user rows in the By User Profile panel.
  const [businessAdminContactById, setBusinessAdminContactById] = useState<Map<string, Contact>>(new Map());
  const [systemAdminContactById, setSystemAdminContactById] = useState<Map<string, Contact>>(new Map());
  const [agentContactById, setAgentContactById] = useState<Map<string, Contact>>(new Map());
  const [customerContactById, setCustomerContactById] = useState<Map<string, Contact>>(new Map());
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [selectedAsset, setSelectedAsset] = useState<string>("all"); // "all" | "mp:SID" | "mm:ID"
  // Left-rail section + collapse state. "platform" = the existing
  // views (sessions/leads); "share" = share-link analytics (Phase 1:
  // the logged-in user's own per-share funnel via SharesPanel).
  const [section, setSection] = useState<AnalyticsSection>(
    UserService.isCustomerOnly() ? "share" : "platform",
  );
  // Collapse state is remembered across sessions (the one real friction with
  // the current toggle was that it reset on every refresh).
  const [navCollapsed, setNavCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("analyticsNavCollapsed") === "true";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("analyticsNavCollapsed", String(navCollapsed));
    } catch {
      /* localStorage unavailable — ignore */
    }
  }, [navCollapsed]);

  // Agent-only state: the logged-in agent's internal id (needed to scope
  // sessions to "mine") and the raw customer list (needed to figure out
  // which customers are this agent's leads).
  const isAgent = UserService.hasRole("agent");
  // ASA-only (not ABA/super). ASA Platform scope = own sessions + their agents'
  // + those agents' customers' sessions. Resolves via `assigned_asa_id`
  // (pending backend) → strictly "self only" until the field is live.
  const isASA =
    UserService.hasRole("agency_system_admin") &&
    !UserService.hasRole("agency_business_admin") &&
    !UserService.hasRole("super_admin");
  const [agentInternalId, setAgentInternalId] = useState<string | null>(null);
  const [selfAsaId, setSelfAsaId] = useState<string | null>(null);
  // The logged-in ABA's own id — powers "My Views" in the By User Profile panel.
  const [selfAbaId, setSelfAbaId] = useState<string | null>(null);
  const [allCustomers, setAllCustomers] = useState<any[]>([]);
  const [allAgents, setAllAgents] = useState<any[]>([]);

  // Platform scope filter (mirrors Share). Narrows the WHOLE Platform dashboard
  // (KPIs, map, session metrics, By User Profile) to a role tier or the
  // viewer's own sessions. Uses the same `roleKey` the By User Profile panel
  // uses, so the chip counts and the panel always reconcile.
  const [platformScope, setPlatformScope] = useState<
    "everyone" | "you" | "system_admin" | "agent" | "customer"
  >("everyone");
  // Individual-agent drill-down, only meaningful when the Agents chip is active.
  // "" = all agents; a specific id filters to that agent's OWN sessions.
  const [selectedAgentId, setSelectedAgentId] = useState<string>("");
  const isMyPlatformSession = React.useCallback(
    (s: any): boolean => {
      const match = (v: any, id: string | null) =>
        !id
          ? false
          : Array.isArray(v)
            ? v.some((x: any) => String(x) === id)
            : String(v) === id;
      if (isAgent) return match(s?.agent_id, agentInternalId);
      if (isASA) return match(s?.asa_id, selfAsaId);
      return match(s?.aba_id, selfAbaId); // ABA / super
    },
    [isAgent, isASA, agentInternalId, selfAsaId, selfAbaId],
  );
  // Agent reuses `selectedProjectId` + `selectedAsset` (empty / "all" = no
  // filter), so the option-building and asset-filter logic is shared with
  // the admin Project tab.

  const projectNameById = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const p of allProjects) {
      if (p?._id) {
        m.set(
          p._id,
          p.project_name || p.name || `Project ${String(p._id).slice(-4)}`,
        );
      }
    }
    return m;
  }, [allProjects]);

  useEffect(() => {
    let cancelled = false;
    const email = UserService.getUseremail();

    // Resolve the agency ID from whichever role the user has, then fetch
    // all projects under that agency and check whether at least one has
    // a matterport (digital twin) or meta_model id.
    const resolveAgencyId = async (): Promise<string | undefined> => {
      try {
        if (UserService.hasRole("agency_business_admin")) {
          const res = await getClientManagerByEmailId(email);
          const bid = res?.agency_business_admin?._id ?? res?.agency_business_admin?.id;
          if (bid && !cancelled) setSelfAbaId(String(bid));
          return res?.agency_business_admin?.agency_id;
        }
        if (UserService.hasRole("agency_system_admin")) {
          const res = await getClientAdminByEmailId(email);
          const sid = res?.agency_system_admin?._id ?? res?.agency_system_admin?.id;
          if (sid && !cancelled) setSelfAsaId(String(sid));
          return res?.agency_system_admin?.agency_id;
        }
        if (UserService.hasRole("agent")) {
          const res = await getAgentProfile(email);
          const aid = res?.agent?._id ?? res?.agent?.id;
          if (aid && !cancelled) setAgentInternalId(String(aid));
          return res?.agent?.agency?.id;
        }
      } catch (e) {
        console.error("AnalyticsV3: failed to resolve agency", e);
      }
      return undefined;
    };

    const fetchUserMaps = (agencyId: string) => {
      // Accumulate email -> name across all three role lists (each carries
      // email_id + first/last name). Merged via functional update so the three
      // async list fetches don't clobber each other.
      const mergeDirectory = (list: any[], role: ShareRole) => {
        setNameByEmail((prev) => {
          const m = new Map(prev);
          for (const a of list) {
            const em = String(a?.email_id || a?.email || a?.username || "").toLowerCase();
            const name = joinName(a?.first_name, a?.last_name) || a?.name;
            if (em && name) m.set(em, String(name));
          }
          return m;
        });
        // Authoritative role-by-email: who is a BA / SA / agent in THIS agency.
        // This is the truth for share-link attribution — the /user/roles
        // endpoint mis-resolves a multi-role person (e.g. a System Admin who is
        // also a customer record) and would badge them "Customer".
        setRoleByEmailDir((prev) => {
          const m = new Map(prev);
          for (const a of list) {
            const em = String(a?.email_id || a?.email || a?.username || "").toLowerCase();
            if (em) m.set(em, role);
          }
          return m;
        });
      };

      // Business Admins — internal _id -> { name, mobile }
      getBusinessAdminList(agencyId).then((res: any) => {
        if (cancelled) return;
        const list: any[] = Array.isArray(res?.agency_business_admin_list)
          ? res.agency_business_admin_list
          : [];
        const nameMap = new Map<string, string>();
        const contactMap = new Map<string, Contact>();
        for (const a of list) {
          const id = a?._id ?? a?.id ?? a?.agency_business_admin_id;
          const name = joinName(a?.first_name, a?.last_name) || a?.name;
          if (id && name) {
            nameMap.set(String(id), String(name));
            contactMap.set(String(id), { name: String(name), mobile: pickMobile(a) });
          }
        }
        setBusinessAdminById(nameMap);
        setBusinessAdminContactById(contactMap);
        mergeDirectory(list, "you"); // ShareRole "you" badges as Business Admin
      });

      // System Admins
      getSystemAdminList(agencyId).then((res: any) => {
        if (cancelled) return;
        const list: any[] = Array.isArray(res?.agency_system_admin_list)
          ? res.agency_system_admin_list
          : [];
        const nameMap = new Map<string, string>();
        const contactMap = new Map<string, Contact>();
        for (const a of list) {
          const id = a?._id ?? a?.id ?? a?.agency_system_admin_id;
          const name = joinName(a?.first_name, a?.last_name) || a?.name;
          if (id && name) {
            nameMap.set(String(id), String(name));
            contactMap.set(String(id), { name: String(name), mobile: pickMobile(a) });
          }
        }
        setSystemAdminById(nameMap);
        setSystemAdminContactById(contactMap);
        mergeDirectory(list, "system_admin");
      });

      // Agents
      getAllAgent(0, 10000, agencyId).then((res: any) => {
        if (cancelled) return;
        const list: any[] = Array.isArray(res?.re_agent_list)
          ? res.re_agent_list
          : Array.isArray(res)
          ? res
          : [];
        setAllAgents(list);
        const nameMap = new Map<string, string>();
        const contactMap = new Map<string, Contact>();
        for (const a of list) {
          const id = a?._id ?? a?.id;
          const name = joinName(a?.first_name, a?.last_name) || a?.name;
          if (id && name) {
            nameMap.set(String(id), String(name));
            contactMap.set(String(id), { name: String(name), mobile: pickMobile(a) });
          }
        }
        setAgentNameById(nameMap);
        setAgentContactById(contactMap);
        mergeDirectory(list, "agent");
      });
    };

    const checkProjectCapabilities = async () => {
      const resolvedAgencyId = await resolveAgencyId();
      if (!resolvedAgencyId || cancelled) return;
      setAgencyId(resolvedAgencyId);

      // Fire-and-forget side fetches in parallel with the capability check.
      // Sessions are handled by a separate effect that re-runs on
      // dateRange / pagination changes.
      fetchUserMaps(resolvedAgencyId);

      try {
        const projects: any[] = (await getFullProjectData(resolvedAgencyId)) || [];
        if (cancelled) return;
        setAllProjects(projects);
        setProjectsFailed(false);

        // Resolve matterport names — for each project, fetch its matterports
        // and build a map keyed by matterport SID -> human-readable name.
        // Used by the Sessions table to show the matterport name above the
        // MATTERPORT pill.
        Promise.all(
          projects
            .filter((p) => p?._id)
            .map((p) =>
              getMatterports(p._id)
                .then((res: any) => res?.matterports || [])
                .catch(() => []),
            ),
        ).then((results) => {
          if (cancelled) return;
          const nameBySid = new Map<string, string>();
          const byProject = new Map<string, Array<{ sid: string; name: string }>>();
          for (const list of results) {
            for (const mp of list) {
              if (mp?.matterport_id && mp?.name) {
                const sid = String(mp.matterport_id);
                const name = String(mp.name);
                nameBySid.set(sid, name);
                if (mp?.project_id) {
                  const pid = String(mp.project_id);
                  if (!byProject.has(pid)) byProject.set(pid, []);
                  byProject.get(pid)!.push({ sid, name });
                }
              }
            }
          }
          setMatterportNameBySid(nameBySid);
          setMatterportsByProject(byProject);
        });

        // Digital Twin: any project with a non-empty `matterport_ids` array.
        const dt = projects.some(
          (p) => Array.isArray(p?.matterport_ids) && p.matterport_ids.length > 0,
        );
        setHasDigitalTwin(dt);

        // Meta Models — fetch per project in parallel. Builds the
        // `meta_model._id -> name` map for the sessions table, the
        // agency-wide "has any meta model" flag, and a
        // `Set<projectId>` for per-project capability checks.
        const filteredProjects = projects.filter((p) => p?._id);
        Promise.all(
          filteredProjects.map((p) =>
            getMetaModels(p._id, 0, 100)
              .then((res: any) => ({
                projectId: String(p._id),
                list: Array.isArray(res?.meta_models)
                  ? res.meta_models
                  : Array.isArray(res?.re_meta_model_list)
                  ? res.re_meta_model_list
                  : [],
              }))
              .catch(() => ({ projectId: String(p._id), list: [] })),
          ),
        ).then((results) => {
          if (cancelled) return;
          const nameMap = new Map<string, string>();
          const projectSet = new Set<string>();
          const byProject = new Map<string, Array<{ id: string; name: string }>>();
          for (const { projectId, list } of results) {
            if (list.length > 0) projectSet.add(projectId);
            const bucket: Array<{ id: string; name: string }> = [];
            for (const mm of list) {
              if (mm?._id && mm?.name) {
                const id = String(mm._id);
                const name = String(mm.name);
                nameMap.set(id, name);
                bucket.push({ id, name });
              }
            }
            if (bucket.length > 0) byProject.set(projectId, bucket);
          }
          setMetaModelNameById(nameMap);
          setProjectsWithMetaModel(projectSet);
          setMetaModelsByProject(byProject);
          setHasMetaModel(projectSet.size > 0);
        });
      } catch (e) {
        console.error("AnalyticsV3: failed to fetch projects", e);
        if (!cancelled) setProjectsFailed(true);
      }
    };

    checkProjectCapabilities();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch all sessions for the agency once (no date params — backend
  // doesn't honour them). Date filtering happens client-side.
  React.useEffect(() => {
    if (!agencyId) return undefined;
    let cancelled = false;
    setSessionsLoading(true);
    getVrSessionList(agencyId, 0, 10000)
      .then((res: any) => {
        if (cancelled) return;
        const list = Array.isArray(res?.re_session_list)
          ? res.re_session_list
          : Array.isArray(res?.vr_session_list)
          ? res.vr_session_list
          : Array.isArray(res?.sessions)
          ? res.sessions
          : Array.isArray(res)
          ? res
          : [];
        const sorted = [...list].sort((a, b) =>
          String(b?.timestamp || "").localeCompare(String(a?.timestamp || "")),
        );
        setAllAgencySessions(sorted);
      })
      .catch((e) => console.error("AnalyticsV3: failed to fetch sessions", e))
      .finally(() => {
        if (!cancelled) setSessionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [agencyId, refreshTick]);

  // Date range change → reset to page 0 so we don't sit on a page that
  // no longer exists after the filter narrows results.
  React.useEffect(() => {
    setSessionsPage(0);
  }, [dateRange]);

  // Customer _ids belonging to this agent's book of business. Used to
  // expand "my sessions" beyond just sessions where the agent was the
  // attached host — agents care about anything their leads do.
  const agentLeadIds = React.useMemo<Set<string>>(() => {
    if (!isAgent || !agentInternalId) return new Set();
    const ids = new Set<string>();
    for (const c of allCustomers) {
      const aid = c?.agent_id;
      const matches = Array.isArray(aid)
        ? aid.some((x) => String(x) === agentInternalId)
        : String(aid) === agentInternalId;
      if (matches) {
        const cid = c?._id ?? c?.id;
        if (cid) ids.add(String(cid));
      }
    }
    return ids;
  }, [isAgent, agentInternalId, allCustomers]);

  // Emails of THIS agent's own customers (customer.agent_id contains the
  // agent's id). Used by Share Analytics to scope the agent's "Customers" chip
  // to their own customers — matched against the share link's shared_by_email.
  const ownCustomerEmails = React.useMemo<Set<string>>(() => {
    const emails = new Set<string>();
    if (!isAgent || !agentInternalId) return emails;
    for (const c of allCustomers) {
      const aid = c?.agent_id;
      const mine = Array.isArray(aid)
        ? aid.some((x) => String(x) === agentInternalId)
        : String(aid) === agentInternalId;
      if (!mine) continue;
      const em = c?.email || c?.email_id || c?.username || "";
      if (em) emails.add(String(em).toLowerCase());
    }
    return emails;
  }, [isAgent, agentInternalId, allCustomers]);

  // ── ASA downline (mirrors the agent's own-book derivation) ──────────────
  // Agents assigned to THIS ASA (assigned_asa_id === self). Pending backend →
  // empty until the field is live, so the ASA's analytics resolve to self only.
  const myAgentIds = React.useMemo<Set<string>>(() => {
    const ids = new Set<string>();
    if (!isASA || !selfAsaId) return ids;
    for (const a of allAgents) {
      const raw = a?.assigned_asa_id ?? a?.asa_id;
      const id = raw ? String(Array.isArray(raw) ? raw[0] : raw) : "";
      if (id && id === selfAsaId) {
        const aid = a?._id ?? a?.id;
        if (aid) ids.add(String(aid));
      }
    }
    return ids;
  }, [isASA, selfAsaId, allAgents]);

  // Customer _ids belonging to this ASA's agents (customer.agent_id ∈ myAgentIds).
  const myAgentCustomerIds = React.useMemo<Set<string>>(() => {
    const ids = new Set<string>();
    if (!isASA) return ids;
    for (const c of allCustomers) {
      const aid = c?.agent_id;
      const arr = Array.isArray(aid) ? aid : aid ? [aid] : [];
      if (arr.some((x: any) => myAgentIds.has(String(x)))) {
        const cid = c?._id ?? c?.id;
        if (cid) ids.add(String(cid));
      }
    }
    return ids;
  }, [isASA, myAgentIds, allCustomers]);

  // Emails for Share scoping: this ASA's agents, and those agents' customers —
  // matched against share links' shared_by_email (lower-cased).
  const myAgentEmails = React.useMemo<Set<string>>(() => {
    const emails = new Set<string>();
    if (!isASA || !selfAsaId) return emails;
    for (const a of allAgents) {
      const raw = a?.assigned_asa_id ?? a?.asa_id;
      const id = raw ? String(Array.isArray(raw) ? raw[0] : raw) : "";
      if (id && id === selfAsaId) {
        const em = a?.email_id || a?.email || a?.username || "";
        if (em) emails.add(String(em).toLowerCase());
      }
    }
    return emails;
  }, [isASA, selfAsaId, allAgents]);

  const myAgentCustomerEmails = React.useMemo<Set<string>>(() => {
    const emails = new Set<string>();
    if (!isASA) return emails;
    for (const c of allCustomers) {
      const aid = c?.agent_id;
      const arr = Array.isArray(aid) ? aid : aid ? [aid] : [];
      if (arr.some((x: any) => myAgentIds.has(String(x)))) {
        const em = c?.email || c?.email_id || c?.username || "";
        if (em) emails.add(String(em).toLowerCase());
      }
    }
    return emails;
  }, [isASA, myAgentIds, allCustomers]);

  // Client-side date filter against the parsed UTC timestamp. For agent
  // logins we additionally narrow to the agent's union scope (sessions
  // they hosted OR sessions belonging to one of their leads) and apply
  // the top-of-page project dropdown if set.
  const platformBaseSessions = React.useMemo(() => {
    const { startDate, endDate } = computeDateRange(dateRange);
    const startMs = startDate
      ? new Date(startDate + "T00:00:00Z").getTime()
      : -Infinity;
    const endMs = endDate
      ? new Date(endDate + "T23:59:59.999Z").getTime()
      : Infinity;
    let out = allAgencySessions.filter((s) => {
      if (!s?.timestamp) return startDate || endDate ? false : true;
      const raw = String(s.timestamp);
      const hasTz = /Z|[+-]\d{2}:?\d{2}$/.test(raw);
      const iso = hasTz ? raw : raw.replace(" ", "T") + "Z";
      const t = new Date(iso).getTime();
      return t >= startMs && t <= endMs;
    });
    // Platform Analytics = organic/direct only. Sessions that arrived via a
    // shared link (referrer_share_code set) are counted in Share Analytics
    // instead, so exclude them here to avoid double-counting across sections.
    out = out.filter((s) => !s?.referrer_share_code);
    // Same for website embeds: those opens belong to Embed Analytics.
    out = out.filter((s) => !s?.embed_key);
    if (isAgent) {
      out = out.filter((s) => {
        const aid = s?.agent_id;
        const isMine = Array.isArray(aid)
          ? aid.some((x) => String(x) === agentInternalId)
          : String(aid) === agentInternalId;
        const isMyLead = s?.customer_id
          ? agentLeadIds.has(String(s.customer_id))
          : false;
        return isMine || isMyLead;
      });
    }
    // ASA scope = own sessions (asa_id == self) + their agents' sessions
    // (agent_id ∈ myAgentIds) + those agents' customers' sessions.
    if (isASA) {
      out = out.filter((s) => {
        const asaRaw = s?.asa_id;
        const isMine = Array.isArray(asaRaw)
          ? asaRaw.some((x) => String(x) === selfAsaId)
          : String(asaRaw) === selfAsaId;
        const agRaw = s?.agent_id;
        const byMyAgent = Array.isArray(agRaw)
          ? agRaw.some((x) => myAgentIds.has(String(x)))
          : myAgentIds.has(String(agRaw));
        const byMyAgentCustomer = s?.customer_id
          ? myAgentCustomerIds.has(String(s.customer_id))
          : false;
        return isMine || byMyAgent || byMyAgentCustomer;
      });
    }
    if (selectedProjectId) {
      out = out.filter((s) => String(s?.project_id) === selectedProjectId);
      if (selectedAsset.startsWith("mp:")) {
        const sid = selectedAsset.slice(3);
        out = out.filter((s) => String(s?.matterport_id) === sid);
      } else if (selectedAsset.startsWith("mm:")) {
        const id = selectedAsset.slice(3);
        out = out.filter((s) => String(s?.meta_model_id) === id);
      }
    }
    // Raw visitors (anonymous — roleKey "raw_visitor") are excluded entirely
    // from Platform Analytics: only identified engagement counts, and it keeps
    // the scope chips reconciling (Everyone == You + System Admin + Agents +
    // Leads, with no untracked remainder).
    out = out.filter((s) => roleKey(s) !== "raw_visitor");
    return out;
  }, [
    allAgencySessions,
    dateRange,
    isAgent,
    agentInternalId,
    agentLeadIds,
    isASA,
    selfAsaId,
    myAgentIds,
    myAgentCustomerIds,
    selectedProjectId,
    selectedAsset,
  ]);

  // Apply the scope chip on top of the base set. "everyone" = no filter; "you"
  // = the viewer's own sessions; otherwise match the session's roleKey. This is
  // the set every Platform consumer reads (KPIs, map, panels, sessions table),
  // so selecting a chip scopes the entire dashboard at once.
  const filteredSessions = React.useMemo(() => {
    if (platformScope === "everyone") return platformBaseSessions;
    if (platformScope === "you")
      return platformBaseSessions.filter((s) => isMyPlatformSession(s));
    if (platformScope === "agent" && selectedAgentId) {
      // Drill into ONE agent — Option A: that agent's own sessions only.
      return platformBaseSessions.filter((s) => {
        const aid = s?.agent_id;
        return Array.isArray(aid)
          ? aid.some((x: any) => String(x) === selectedAgentId)
          : String(aid) === selectedAgentId;
      });
    }
    return platformBaseSessions.filter((s) => roleKey(s) === platformScope);
  }, [platformBaseSessions, platformScope, selectedAgentId, isMyPlatformSession]);

  // Chip counts come from the BASE set (pre-scope) so each chip shows its full
  // size regardless of the current selection. Gated by viewer role: ABA sees
  // all 5; ASA drops System Admin (they ARE that tier — their own is "You");
  // Agent sees only Everyone / You / Leads.
  const platformScopeChips = React.useMemo(() => {
    const base = platformBaseSessions;
    // Count = UNIQUE users per bucket (not views) — matches the Share chips and
    // the By User Profile "Visitors" column. Distinct via identifiedVisitorKey.
    const distinctUsers = (arr: any[]) => {
      const set = new Set<string>();
      for (const s of arr) {
        const k = identifiedVisitorKey(s);
        if (k) set.add(k);
      }
      return set.size;
    };
    const ofRole = (r: string) => base.filter((s) => roleKey(s) === r);
    const all = [
      { key: "everyone" as const, label: "Everyone", count: distinctUsers(base) },
      {
        key: "you" as const,
        label: "You",
        count: distinctUsers(base.filter((s) => isMyPlatformSession(s))),
      },
      { key: "system_admin" as const, label: "System Admin", count: distinctUsers(ofRole("system_admin")) },
      { key: "agent" as const, label: "Agents", count: distinctUsers(ofRole("agent")) },
      { key: "customer" as const, label: "Leads", count: distinctUsers(ofRole("customer")) },
    ];
    return all.filter((c) => {
      if (c.key === "system_admin") return !isASA && !isAgent; // ABA / super only
      if (c.key === "agent") return !isAgent; // ABA / ASA (agent has no peers here)
      return true; // everyone, you, customer always
    });
  }, [platformBaseSessions, isMyPlatformSession, isASA, isAgent]);

  // If the active chip disappears (viewer/data change), fall back to Everyone.
  React.useEffect(() => {
    if (!platformScopeChips.some((c) => c.key === platformScope)) {
      setPlatformScope("everyone");
    }
  }, [platformScopeChips, platformScope]);

  // Agents the viewer can drill into — ONLY agents that actually have sessions
  // in view (matches the By User Profile agent drill-down, which is built from
  // sessions, so zero-view agents don't appear). Scoped: ASA → own agents.
  const agentFilterOptions = React.useMemo(() => {
    const viewsByAgent = new Map<string, number>();
    for (const s of platformBaseSessions) {
      if (roleKey(s) !== "agent") continue;
      const aid = Array.isArray(s?.agent_id) ? s.agent_id[0] : s?.agent_id;
      if (aid == null) continue;
      const id = String(aid);
      if (isASA && !myAgentIds.has(id)) continue;
      viewsByAgent.set(id, (viewsByAgent.get(id) || 0) + 1);
    }
    return Array.from(viewsByAgent.entries())
      .map(([id, count]) => ({
        id,
        name: agentNameById.get(id) || `Agent ${id.slice(-4)}`,
        count,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [platformBaseSessions, agentNameById, isASA, myAgentIds]);

  // Clear the individual-agent selection whenever we leave the Agents chip.
  React.useEffect(() => {
    if (platformScope !== "agent") setSelectedAgentId("");
  }, [platformScope]);

  // "My Pipeline" health snapshot — replaces the agency-wide 6-card row
  // for agents. Active = the agent's lead has ≥1 session in the last
  // 15 days. Inactive is derived as (totalLeads - active15d) in render
  // so the buckets always sum to total. Walks unfiltered
  // allAgencySessions for wall-clock 15d, but respects the agent's
  // project + meta-asset selectors so the strip stays consistent with
  // the Session Metrics row above.
  const agentPipeline = React.useMemo(() => {
    if (!isAgent) {
      return { totalLeads: 0, active15d: 0, avgSessionsPerLead: 0 };
    }
    const now = Date.now();
    const day = 24 * 60 * 60 * 1000;
    const lastSeenByLead = new Map<string, number>();
    const sessionCountByLead = new Map<string, number>();
    const assetMpId = selectedAsset.startsWith("mp:")
      ? selectedAsset.slice(3)
      : null;
    const assetMmId = selectedAsset.startsWith("mm:")
      ? selectedAsset.slice(3)
      : null;
    for (const s of allAgencySessions) {
      const cid = s?.customer_id ? String(s.customer_id) : "";
      if (!cid || !agentLeadIds.has(cid)) continue;
      if (selectedProjectId && String(s?.project_id) !== selectedProjectId) continue;
      if (assetMpId && String(s?.matterport_id) !== assetMpId) continue;
      if (assetMmId && String(s?.meta_model_id) !== assetMmId) continue;
      sessionCountByLead.set(cid, (sessionCountByLead.get(cid) || 0) + 1);
      const raw = s?.timestamp ? String(s.timestamp) : "";
      if (raw) {
        const hasTz = /Z|[+-]\d{2}:?\d{2}$/.test(raw);
        const iso = hasTz ? raw : raw.replace(" ", "T") + "Z";
        const t = new Date(iso).getTime();
        if (!Number.isNaN(t)) {
          const prev = lastSeenByLead.get(cid) || 0;
          if (t > prev) lastSeenByLead.set(cid, t);
        }
      }
    }
    let active15d = 0;
    for (const leadId of agentLeadIds) {
      const last = lastSeenByLead.get(leadId);
      if (last && now - last <= 15 * day) active15d++;
    }
    const totalSessions = Array.from(sessionCountByLead.values()).reduce(
      (a, b) => a + b,
      0,
    );
    const avgSessionsPerLead =
      agentLeadIds.size > 0
        ? Math.round((totalSessions / agentLeadIds.size) * 10) / 10
        : 0;
    return {
      totalLeads: agentLeadIds.size,
      active15d,
      avgSessionsPerLead,
    };
  }, [
    isAgent,
    agentLeadIds,
    allAgencySessions,
    selectedProjectId,
    selectedAsset,
  ]);

  // Binary agent activity. Active = ≥1 session in the last 15 days.
  // Inactive is derived in render as (total agents - active), giving
  // mathematically complete buckets that sum to total. Walks unfiltered
  // allAgencySessions so the window stays wall-clock, independent of
  // the date picker.
  const agentActivity = React.useMemo(() => {
    if (isAgent) return { active15d: 0 };
    const now = Date.now();
    const day = 24 * 60 * 60 * 1000;
    const lastSeenByAgent = new Map<string, number>();
    for (const s of allAgencySessions) {
      const aid = s?.agent_id;
      if (!aid) continue;
      const ids = Array.isArray(aid) ? aid : [aid];
      const raw = s?.timestamp ? String(s.timestamp) : "";
      if (!raw) continue;
      const hasTz = /Z|[+-]\d{2}:?\d{2}$/.test(raw);
      const iso = hasTz ? raw : raw.replace(" ", "T") + "Z";
      const t = new Date(iso).getTime();
      if (Number.isNaN(t)) continue;
      for (const id of ids) {
        if (!id) continue;
        const sid = String(id);
        const prev = lastSeenByAgent.get(sid) || 0;
        if (t > prev) lastSeenByAgent.set(sid, t);
      }
    }
    // ASA Agency Snapshot is scoped to THEIR agents only.
    const idsForScope: Iterable<string> = isASA
      ? myAgentIds
      : agentNameById.keys();
    let active15d = 0;
    for (const id of idsForScope) {
      const last = lastSeenByAgent.get(String(id));
      if (last && now - last <= 15 * day) active15d++;
    }
    return { active15d };
  }, [isAgent, isASA, myAgentIds, allAgencySessions, agentNameById]);

  // Total agents shown in the Agency Snapshot — an ASA sees only their own.
  const snapAgentTotal = isASA ? myAgentIds.size : agentNameById.size;
  // Snapshot leads — an ASA sees only their agents' leads (all of which are,
  // by definition, assigned to one of their agents).
  const snapLeadAssigned = isASA
    ? myAgentCustomerIds.size
    : assignedLeads ?? 0;
  const snapLeadUnassigned = isASA ? 0 : unassignedLeads ?? 0;
  const snapLeadTotal = snapLeadAssigned + snapLeadUnassigned;

  // Agency-wide lead pipeline health for the admin Agency Snapshot row.
  // Walks unfiltered `allAgencySessions` so the 7d/30d windows always
  // reflect real wall-clock time — independent of the date range picker.
  const agencyPipeline = React.useMemo(() => {
    if (isAgent) {
      return { active15d: 0, rawVisitors: 0, rawVisitorViews: 0 };
    }
    const now = Date.now();
    const day = 24 * 60 * 60 * 1000;
    // ASA Agency Snapshot leads are scoped to their agents' customers only.
    const customerIds = new Set<string>();
    for (const c of allCustomers) {
      const id = String(c?._id ?? c?.id ?? "");
      if (!id) continue;
      if (isASA && !myAgentCustomerIds.has(id)) continue;
      customerIds.add(id);
    }
    const lastSeenByLead = new Map<string, number>();
    const rawVisitorIds = new Set<string>();
    let rawVisitorViews = 0;
    for (const s of allAgencySessions) {
      const cid = s?.customer_id ? String(s.customer_id) : "";
      if (cid && customerIds.has(cid)) {
        const raw = s?.timestamp ? String(s.timestamp) : "";
        if (raw) {
          const hasTz = /Z|[+-]\d{2}:?\d{2}$/.test(raw);
          const iso = hasTz ? raw : raw.replace(" ", "T") + "Z";
          const t = new Date(iso).getTime();
          if (!Number.isNaN(t)) {
            const prev = lastSeenByLead.get(cid) || 0;
            if (t > prev) lastSeenByLead.set(cid, t);
          }
        }
      }
      const hasRole =
        s?.aba_id?.length ||
        s?.asa_id?.length ||
        s?.agent_id?.length ||
        s?.customer_id?.length;
      if (!hasRole) {
        rawVisitorViews += 1;
        const vid = String(s?.user_id || s?._id || "");
        if (vid) rawVisitorIds.add(vid);
      }
    }
    let active15d = 0;
    for (const cid of customerIds) {
      const last = lastSeenByLead.get(cid);
      if (last && now - last <= 15 * day) active15d++;
    }
    return {
      active15d,
      rawVisitors: rawVisitorIds.size,
      rawVisitorViews,
    };
  }, [isAgent, isASA, myAgentCustomerIds, allCustomers, allAgencySessions]);

  // Slice for the current table page.
  const sessionsPageItems = React.useMemo(() => {
    const start = sessionsPage * SESSIONS_PAGE_SIZE;
    return filteredSessions.slice(start, start + SESSIONS_PAGE_SIZE);
  }, [filteredSessions, sessionsPage]);

  const sessionsTotal = filteredSessions.length;

  // Customers — agency-wide, not date-filtered (leads are static infra).
  React.useEffect(() => {
    if (!agencyId) return undefined;
    let cancelled = false;
    getCustomerList(0, 10000, agencyId)
      .then((res: any) => {
        if (cancelled) return;
        const list: any[] = Array.isArray(res?.re_customer_list)
          ? res.re_customer_list
          : Array.isArray(res?.customers)
          ? res.customers
          : Array.isArray(res)
          ? res
          : [];
        let assigned = 0;
        let unassigned = 0;
        const contactMap = new Map<string, Contact>();
        for (const c of list) {
          const aid = c?.agent_id;
          const hasAgent = Array.isArray(aid)
            ? aid.some((id) => typeof id === "string" && id.trim() !== "")
            : typeof aid === "string"
            ? aid.trim() !== ""
            : aid != null;
          if (hasAgent) assigned++;
          else unassigned++;
          const id = c?._id ?? c?.id;
          const name = joinName(c?.first_name, c?.last_name) || c?.name;
          if (id && name) {
            contactMap.set(String(id), { name: String(name), mobile: pickMobile(c) });
          }
        }
        setAssignedLeads(assigned);
        setUnassignedLeads(unassigned);
        setCustomerContactById(contactMap);
        setAllCustomers(list);
      })
      .catch(() => {
        if (cancelled) return;
        setAssignedLeads(null);
        setUnassignedLeads(null);
      });
    return () => {
      cancelled = true;
    };
  }, [agencyId, refreshTick]);

  // Aggregate the Session Metric values from the date-filtered set.
  const summary = React.useMemo(() => {
    let totalDuration = 0;
    let mobileViews = 0;
    let desktopViews = 0;
    let vrViews = 0;
    let metaModelViews = 0;
    let digitalTwinViews = 0;
    const visitorIds = new Set<string>();
    for (const s of filteredSessions) {
      if (s?.device === "mobile") mobileViews++;
      else if (s?.device === "desktop") desktopViews++;
      if (s?.used_vr === true) vrViews++;
      if (s?.meta_model_id) metaModelViews++;
      if (s?.matterport_id) digitalTwinViews++;
      if (typeof s?.total_duration === "number")
        totalDuration += s.total_duration;
      {
        const vid = identifiedVisitorKey(s);
        if (vid) visitorIds.add(vid);
      }
    }
    const totalViews = filteredSessions.length;
    const avgDuration =
      totalViews > 0 ? Math.round(totalDuration / totalViews) : 0;
    return {
      totalViews,
      visitors: visitorIds.size,
      mobileViews,
      desktopViews,
      vrViews,
      metaModelViews,
      digitalTwinViews,
      totalDuration,
      avgDuration,
    };
  }, [filteredSessions]);

  const pct = (part: number, total: number) =>
    total > 0 ? Math.round((part / total) * 100) : 0;

  // Reset asset filter when project changes.
  React.useEffect(() => {
    setSelectedAsset("all");
  }, [selectedProjectId]);

  // Single-project agencies: skip the redundant "All Projects" default
  // and land directly on the only project's view. Runs once after
  // projects load. We don't override a user's later choice — they can
  // still flip back to "All Projects" (which == the same data here)
  // via the dropdown.
  const autoPickedRef = React.useRef(false);
  React.useEffect(() => {
    if (autoPickedRef.current) return;
    if (allProjects.length === 1 && !selectedProjectId) {
      setSelectedProjectId(String(allProjects[0]?._id || ""));
      autoPickedRef.current = true;
    }
  }, [allProjects, selectedProjectId]);


  // Project-scoped engagement counts. Tracks which agents/leads have
  // had a session ON THIS PROJECT recently. We track both 7d and 30d
  // windows so the render can derive Dormant as (total - active30d)
  // against an agency-wide total — that way "Dormant Agents (30d+)"
  // correctly includes agents who've simply never touched the project.
  // Walks unfiltered allAgencySessions so the windows stay wall-clock.
  const projectSnapshot = React.useMemo(() => {
    if (!selectedProjectId) {
      return {
        agentActive15d: 0,
        leadActive15d: 0,
        rawVisitors: 0,
        rawVisitorViews: 0,
      };
    }
    const now = Date.now();
    const day = 24 * 60 * 60 * 1000;
    const agentLastSeen = new Map<string, number>();
    const leadLastSeen = new Map<string, number>();
    const rawVisitorIds = new Set<string>();
    let rawVisitorViews = 0;
    // Honor the meta-asset filter so the snapshot row matches what's
    // visible in the Session Metrics row above. "All" = no asset filter.
    const assetMpId = selectedAsset.startsWith("mp:")
      ? selectedAsset.slice(3)
      : null;
    const assetMmId = selectedAsset.startsWith("mm:")
      ? selectedAsset.slice(3)
      : null;
    for (const s of allAgencySessions) {
      if (String(s?.project_id) !== selectedProjectId) continue;
      if (assetMpId && String(s?.matterport_id) !== assetMpId) continue;
      if (assetMmId && String(s?.meta_model_id) !== assetMmId) continue;
      const raw = s?.timestamp ? String(s.timestamp) : "";
      const hasTz = raw && /Z|[+-]\d{2}:?\d{2}$/.test(raw);
      const iso = raw ? (hasTz ? raw : raw.replace(" ", "T") + "Z") : "";
      const t = iso ? new Date(iso).getTime() : NaN;
      if (Number.isNaN(t)) continue;
      const aid = s?.agent_id;
      const aids = Array.isArray(aid) ? aid : aid ? [aid] : [];
      for (const id of aids) {
        if (!id) continue;
        const sid = String(id);
        const prev = agentLastSeen.get(sid) || 0;
        if (t > prev) agentLastSeen.set(sid, t);
      }
      const cid = s?.customer_id ? String(s.customer_id) : "";
      if (cid) {
        const prev = leadLastSeen.get(cid) || 0;
        if (t > prev) leadLastSeen.set(cid, t);
      }
      const hasRole =
        s?.aba_id?.length ||
        s?.asa_id?.length ||
        s?.agent_id?.length ||
        s?.customer_id?.length;
      if (!hasRole) {
        rawVisitorViews += 1;
        const vid = String(s?.user_id || s?._id || "");
        if (vid) rawVisitorIds.add(vid);
      }
    }
    let agentActive15d = 0;
    for (const [, last] of agentLastSeen) {
      if (now - last <= 15 * day) agentActive15d++;
    }
    let leadActive15d = 0;
    for (const [, last] of leadLastSeen) {
      if (now - last <= 15 * day) leadActive15d++;
    }
    return {
      agentActive15d,
      leadActive15d,
      rawVisitors: rawVisitorIds.size,
      rawVisitorViews,
    };
  }, [selectedProjectId, selectedAsset, allAgencySessions]);

  const selectedProject = React.useMemo(
    () => allProjects.find((p) => p?._id === selectedProjectId),
    [allProjects, selectedProjectId],
  );
  const selectedProjectName =
    selectedProject?.project_name ||
    selectedProject?.name ||
    (selectedProjectId
      ? `Project ${String(selectedProjectId).slice(-4)}`
      : "");
  const selectedHasMatterport =
    Array.isArray(selectedProject?.matterport_ids) &&
    selectedProject.matterport_ids.length > 0;
  const selectedHasMetaModel = projectsWithMetaModel.has(selectedProjectId);

  // Asset-share bars are only meaningful when BOTH Meta Model and Digital
  // Twin are configured — otherwise the lone configured side trivially
  // reads 100%, which is no signal. Project-picked: use that project's
  // capability; else agency-wide flags.
  const showAssetShare = selectedProjectId
    ? selectedHasMetaModel && selectedHasMatterport
    : hasMetaModel && hasDigitalTwin;


  const formatHoursAndMinutes = (seconds: number): string => {
    if (seconds <= 0) return "0";
    if (seconds < 60) return `${seconds}s`;
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h`;
    return `${m}m`;
  };

  const formatTimeDetail = (seconds: number): string => {
    if (seconds <= 0) return "no time tracked";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m combined`;
    if (m > 0) return `${m}m total`;
    return `${seconds}s total`;
  };

  const handleDownloadCsv = async () => {
    if (!agencyId || csvBusy) return;
    setCsvBusy(true);
    try {
      // No extra fetch — filteredSessions is already in memory and
      // reflects the current date filter.
      const list = filteredSessions;
      const resolveUserNameLocal = (s: any): string => {
        if (s?.aba_id?.length) {
          const n = businessAdminById.get(String(s.aba_id[0]));
          if (n) return n;
        }
        if (s?.asa_id?.length) {
          const n = systemAdminById.get(String(s.asa_id[0]));
          if (n) return n;
        }
        if (s?.agent_id?.length) {
          const n = agentNameById.get(String(s.agent_id[0]));
          if (n) return n;
        }
        return s?.user_id || "";
      };
      const esc = (v: any) => {
        const str = String(v ?? "");
        return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
      };
      const cols: { label: string; get: (s: any) => any }[] = [
        { label: "Date (IST)", get: (s) => formatSessionDate(s.timestamp) },
        { label: "User Profile", get: (s) => resolveUserNameLocal(s) },
        { label: "Role", get: (s) => deriveSessionRole(s).label },
        { label: "Project", get: (s) => projectNameById.get(s.project_id) || "" },
        { label: "City", get: (s) => s.city || "" },
        { label: "Device", get: (s) => s.device || "" },
        { label: "VR Used", get: (s) => (s.used_vr ? "Yes" : "No") },
        { label: "Meta Asset", get: (s) => deriveMetaAsset(s)?.label || "" },
        {
          label: "Meta Asset Name",
          get: (s) =>
            s.meta_model_id
              ? metaModelNameById.get(String(s.meta_model_id)) || ""
              : s.matterport_id
              ? matterportNameBySid.get(String(s.matterport_id)) || ""
              : "",
        },
        { label: "Duration (sec)", get: (s) => s.total_duration ?? "" },
        { label: "Status", get: (s) => s.status || "" },
      ];
      const csv = [
        cols.map((c) => esc(c.label)).join(","),
        ...list.map((s: any) => cols.map((c) => esc(c.get(s))).join(",")),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sessions-${dateRange}-${new Date()
        .toISOString()
        .split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("CSV download failed", e);
    } finally {
      setCsvBusy(false);
    }
  };

  return (
    <div className="font-inter box-border min-h-screen md:min-h-0 md:h-[calc(100vh-4rem)] bg-gradient-to-b from-[#1C2C3A] to-[#1E4F52] p-3 sm:p-4 md:p-6 md:flex md:flex-col md:overflow-hidden">
      <style
        dangerouslySetInnerHTML={{
          __html: `
            .analytics-scroll { scrollbar-color: #2C4D5A transparent; }
            .analytics-scroll::-webkit-scrollbar { width: 14px; height: 14px; }
            .analytics-scroll::-webkit-scrollbar-track { background: transparent; transition: background 0.15s ease; }
            .analytics-scroll::-webkit-scrollbar-thumb { background: #2C4D5A; border-radius: 7px; border: 3px solid transparent; background-clip: padding-box; transition: background 0.15s ease; }
            .analytics-scroll:hover::-webkit-scrollbar-track { background: rgba(255,255,255,0.10); }
            .analytics-scroll:hover::-webkit-scrollbar-thumb { background: #3D5468; background-clip: padding-box; }
            .analytics-scroll::-webkit-scrollbar-thumb:hover { background: #4A6075; background-clip: padding-box; }
          `,
        }}
      />
      {/* Breadcrumb */}
      <div className="flex items-center gap-1 text-sm mb-4 md:flex-shrink-0">
        <button
          type="button"
          onClick={() => navigate(slug ? `/c/${slug}` : "/")}
          className="text-[#889DB2] hover:underline"
        >
          Home
        </button>
        <span className="text-[#889DB2] mx-1">›</span>
        <span className="text-white font-medium">
          {SECTION_COPY[section].title}
        </span>
      </div>

      {/* Shared filter — controls both branches below. Picking a project
          switches the snapshot/metrics view from Agency to Project; the
          meta-asset selector only appears once a project is picked. */}
      <div className="max-w-screen-2xl flex flex-col md:flex-row gap-3 sm:gap-4 md:items-stretch md:flex-1 md:min-h-0 md:overflow-hidden">
        <AnalyticsSideNav
          section={section}
          onSection={setSection}
          collapsed={navCollapsed}
          onToggleCollapse={() => setNavCollapsed((c) => !c)}
        />

        {/* Section content. Platform = the existing render path (KPIs,
            breakdowns, map, sessions). Share = Phase-1 "My View". The rail
            above holds the section nav + GLOBAL filters (project, date) and
            SECTION-SPECIFIC filters (asset = platform-only). */}
        <div className="flex-1 min-w-0 md:min-h-0 md:flex md:flex-col">
          {/* Section header — title + one-line description. Stays fixed above
              the filters; the data region below gets the scroll. */}
          <div className="mb-3 md:flex-shrink-0 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h2 className="text-lg sm:text-xl font-semibold text-white flex-shrink-0">
              {SECTION_COPY[section].title}
            </h2>
            <p className="text-xs sm:text-[13px] text-[#889DB2]">
              {SECTION_COPY[section].blurb}
            </p>
          </div>
          {/* Scrollable region — filters + data scroll together; only the
              header above stays fixed (desktop). */}
          <div className="md:flex-1 md:min-h-0 md:overflow-y-auto analytics-scroll">
          {/* Global filters — time range + project; both drive Platform and Share. */}
          <div className="rounded-xl bg-[#15333D] border border-[#2C4D5A] p-3 sm:p-4 mb-4 flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
            <div className="order-2">
              <span className="text-[10px] font-semibold text-[#889DB2] uppercase tracking-widest block mb-2">
                Time range
              </span>
              <div className="flex items-center gap-2">
                <DateRangePicker value={dateRange} onChange={setDateRange} />
                <RefreshButton
                  /* Only Platform and Share fetch anything today; the two
                     newer sections have nothing in flight to report. */
                  busy={
                    section === "share" ? shareLoading
                      : section === "platform" ? sessionsLoading
                        : false
                  }
                  onClick={() => setRefreshTick((t) => t + 1)}
                />
              </div>
            </div>
            <div className="w-full sm:w-72 order-1">
              <span className="text-[10px] font-semibold text-[#889DB2] uppercase tracking-widest block mb-2">
                Filter by Project
              </span>
              <Listbox
                value={selectedProjectId}
                onChange={(v: string) => setSelectedProjectId(v)}
              >
                <div className="relative">
                  <Listbox.Button className="w-full bg-[#1F3D48] border border-[#3D5468] text-sm rounded-lg px-3.5 py-2.5 pr-9 focus:outline-none focus:ring-2 focus:ring-[#008564]/30 focus:border-[#008564]/60 cursor-pointer hover:bg-[#243F4C] hover:border-[#5A7088] transition-all text-left flex items-center gap-2">
                    <span className="flex-1 truncate text-white">
                      {selectedProjectId
                        ? projectNameById.get(selectedProjectId) ||
                          `Project ${selectedProjectId.slice(-4)}`
                        : "All Projects"}
                    </span>
                    <svg
                      className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#889DB2]"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </Listbox.Button>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-lg bg-[#15333D] border border-[#2C4D5A] py-1 shadow-2xl focus:outline-none">
                      <Listbox.Option
                        value=""
                        className={({ active, selected }) =>
                          `cursor-pointer select-none px-3.5 py-2 text-sm flex items-center gap-2 transition-colors ${
                            active ? "bg-white/5" : ""
                          } ${selected ? "bg-[#008564]/10" : ""}`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span
                              className={`flex-1 truncate ${
                                selected ? "text-white font-medium" : "text-[#E5E7EB]"
                              }`}
                            >
                              All Projects
                            </span>
                            {selected && (
                              <svg
                                className="w-3.5 h-3.5 text-[#34D399] flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2.5}
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </>
                        )}
                      </Listbox.Option>
                      {allProjects.map((p) => (
                        <Listbox.Option
                          key={p?._id}
                          value={p?._id}
                          className={({ active, selected }) =>
                            `cursor-pointer select-none px-3.5 py-2 text-sm flex items-center gap-2 transition-colors ${
                              active ? "bg-white/5" : ""
                            } ${selected ? "bg-[#008564]/10" : ""}`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={`flex-1 truncate ${
                                  selected ? "text-white font-medium" : "text-[#E5E7EB]"
                                }`}
                              >
                                {p?.project_name ||
                                  p?.name ||
                                  `Project ${String(p?._id || "").slice(-4)}`}
                              </span>
                              {selected && (
                                <svg
                                  className="w-3.5 h-3.5 text-[#34D399] flex-shrink-0"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth={2.5}
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
            </div>
          </div>
          {section === "platform" && (
            <>
        {/* Scope by user profile — narrows the whole dashboard to a role tier
            (or the viewer's own sessions). Mirrors the Share scope chips. */}
        {platformScopeChips.length > 1 && (
          <div className="rounded-xl bg-[#15333D] border border-[#2C4D5A] p-3 sm:p-4 mb-3">
            <div className="flex items-baseline justify-between mb-3 gap-2 flex-wrap">
              <h3 className="text-[10px] font-semibold text-[#889DB2] uppercase tracking-widest">
                Filter by user profile
              </h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {platformScopeChips.map((t) => {
                const active = platformScope === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setPlatformScope(t.key)}
                    className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium border transition-colors ${
                      active
                        ? "bg-[#008564]/15 border-[#34D399] text-[#34D399]"
                        : "border-[#2C4D5A] text-[#889DB2] hover:text-white hover:border-[#5A7C8C]"
                    }`}
                  >
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: platformChipDot(t.key) }}
                    />
                    {t.label}
                    <span
                      className={`text-[10px] min-w-[20px] h-[20px] inline-flex items-center justify-center px-1.5 rounded-full font-semibold ${
                        active
                          ? "bg-[#34D399] text-[#0C2420]"
                          : "bg-[#1F3D48] text-[#889DB2]"
                      }`}
                    >
                      {t.count.toLocaleString()}
                    </span>
                  </button>
                );
              })}
            </div>
            {/* Individual-agent drill-down — only when the Agents chip is
                active. Filters the whole dashboard to that agent's OWN
                sessions. Styled to match the "Filter by Project" Listbox. */}
            {platformScope === "agent" && agentFilterOptions.length > 0 && (
              <div className="mt-3 w-full sm:w-72">
                <span className="text-[10px] font-semibold text-[#889DB2] uppercase tracking-widest block mb-2">
                  Filter by Agent
                </span>
                <Listbox
                  value={selectedAgentId}
                  onChange={(v: string) => setSelectedAgentId(v)}
                >
                  <div className="relative">
                    <Listbox.Button className="w-full bg-[#1F3D48] border border-[#3D5468] text-sm rounded-lg px-3.5 py-2.5 pr-9 focus:outline-none focus:ring-2 focus:ring-[#008564]/30 focus:border-[#008564]/60 cursor-pointer hover:bg-[#243F4C] hover:border-[#5A7088] transition-all text-left flex items-center gap-2">
                      <span className="flex-1 truncate text-white">
                        {selectedAgentId
                          ? agentFilterOptions.find(
                              (a) => a.id === selectedAgentId,
                            )?.name || "Agent"
                          : "All agents"}
                      </span>
                      <svg
                        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#889DB2]"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </Listbox.Button>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-lg bg-[#15333D] border border-[#2C4D5A] py-1 shadow-2xl focus:outline-none">
                        <Listbox.Option
                          value=""
                          className={({ active, selected }) =>
                            `cursor-pointer select-none px-3.5 py-2 text-sm flex items-center gap-2 transition-colors ${
                              active ? "bg-white/5" : ""
                            } ${selected ? "bg-[#008564]/10" : ""}`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={`flex-1 truncate ${
                                  selected
                                    ? "text-white font-medium"
                                    : "text-[#E5E7EB]"
                                }`}
                              >
                                All agents
                              </span>
                              {selected && (
                                <svg
                                  className="w-3.5 h-3.5 text-[#34D399] flex-shrink-0"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth={2.5}
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </>
                          )}
                        </Listbox.Option>
                        {agentFilterOptions.map((a) => (
                          <Listbox.Option
                            key={a.id}
                            value={a.id}
                            className={({ active, selected }) =>
                              `cursor-pointer select-none px-3.5 py-2 text-sm flex items-center gap-2 transition-colors ${
                                active ? "bg-white/5" : ""
                              } ${selected ? "bg-[#008564]/10" : ""}`
                            }
                          >
                            {({ selected }) => (
                              <>
                                <span
                                  className={`flex-1 truncate ${
                                    selected
                                      ? "text-white font-medium"
                                      : "text-[#E5E7EB]"
                                  }`}
                                >
                                  {a.name}
                                </span>
                                {selected && (
                                  <svg
                                    className="w-3.5 h-3.5 text-[#34D399] flex-shrink-0"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={2.5}
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                </Listbox>
              </div>
            )}
          </div>
        )}
        {/* Row 1: Session Metrics — date-range aware */}
        <div className="rounded-xl bg-[#15333D] border border-[#2C4D5A] p-3 sm:p-4">
          <div className="flex items-baseline justify-between mb-3 gap-2 flex-wrap">
            <h3 className="text-[10px] font-semibold text-[#889DB2] uppercase tracking-widest">
              Session Metrics
            </h3>
            <span className="text-[10px] text-[#5A7C8C] truncate max-w-[60%]">
              {!isAgent && selectedProjectId && (
                <>{selectedProjectName} · </>
              )}
              {DATE_RANGE_OPTIONS.find((o) => o.key === dateRange)?.label}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-2">
            <KpiCard
              icon={IconEye("text-blue-400")}
              iconBg="rgba(74,158,255,0.15)"
              accent="#60a5fa"
              value={summary.totalViews.toLocaleString()}
              label="Total Views"
            />
            <KpiCard
              icon={IconUsers("text-lime-400")}
              iconBg="rgba(163,230,53,0.15)"
              accent="#a3e635"
              value={summary.visitors.toLocaleString()}
              label="Visitors"
            />
            <KpiCard
              icon={IconMobile("text-sky-400")}
              iconBg="rgba(56,189,248,0.15)"
              accent="#38bdf8"
              value={summary.mobileViews.toLocaleString()}
              label="Mobile Views"
              share={{
                percent: pct(summary.mobileViews, summary.totalViews),
                color: "#38bdf8",
              }}
            />
            <KpiCard
              icon={IconDesktop("text-violet-400")}
              iconBg="rgba(167,139,250,0.15)"
              accent="#a78bfa"
              value={summary.desktopViews.toLocaleString()}
              label="Desktop Views"
              share={{
                percent: pct(summary.desktopViews, summary.totalViews),
                color: "#a78bfa",
              }}
            />
            <KpiCard
              icon={IconVR("text-emerald-400")}
              iconBg="rgba(52,211,153,0.15)"
              accent="#34d399"
              value={summary.vrViews.toLocaleString()}
              label="VR Views"
              subtext={{
                text: "Subset · entered VR mode",
                color: "#889DB2",
              }}
            />

            {/* Metamodel Views — project-specific check when a project is
                picked (admin or agent), otherwise agency-wide. Empty-state
                copy is project-specific only in admin-with-project mode. */}
            {(selectedProjectId ? selectedHasMetaModel : hasMetaModel) ? (
              <KpiCard
                icon={IconCube("text-indigo-400")}
                iconBg="rgba(129,140,248,0.15)"
                accent="#818cf8"
                value={summary.metaModelViews.toLocaleString()}
                label="Metamodel Views"
                share={
                  showAssetShare
                    ? {
                        percent: pct(summary.metaModelViews, summary.totalViews),
                        color: "#818cf8",
                      }
                    : undefined
                }
              />
            ) : (
              <KpiCard
                icon={IconCube("text-indigo-400")}
                iconBg="rgba(129,140,248,0.15)"
                accent="#818cf8"
                label="Metamodel Views"
                emptyState={{
                  title:
                    !isAgent && selectedProjectId
                      ? "Meta Model not set up"
                      : "Meta Model Not Configured",
                  subtitle:
                    !isAgent && selectedProjectId
                      ? "This project has no Meta Model yet."
                      : "Set up a meta model to start tracking.",
                }}
              />
            )}

            {/* Digital Twin Views — same project-specific check. */}
            {(selectedProjectId ? selectedHasMatterport : hasDigitalTwin) ? (
              <KpiCard
                icon={IconBuilding("text-fuchsia-400")}
                iconBg="rgba(232,121,249,0.15)"
                accent="#e879f9"
                value={summary.digitalTwinViews.toLocaleString()}
                label="Digital Twin Views"
                share={
                  showAssetShare
                    ? {
                        percent: pct(summary.digitalTwinViews, summary.totalViews),
                        color: "#e879f9",
                      }
                    : undefined
                }
              />
            ) : (
              <KpiCard
                icon={IconBuilding("text-fuchsia-400")}
                iconBg="rgba(232,121,249,0.15)"
                accent="#e879f9"
                label="Digital Twin Views"
                emptyState={{
                  title:
                    !isAgent && selectedProjectId
                      ? "Digital Twin not set up"
                      : "Digital Twin Not Configured",
                  subtitle:
                    !isAgent && selectedProjectId
                      ? "This project has no Matterport linked."
                      : "Set up a digital twin to start tracking.",
                }}
              />
            )}

            <KpiCard
              icon={IconBolt("text-cyan-400")}
              iconBg="rgba(34,211,238,0.15)"
              accent="#22d3ee"
              value={formatHoursAndMinutes(summary.totalDuration)}
              label="Total Time Spent"
              subtext={{
                text: formatTimeDetail(summary.totalDuration),
                color: "#22d3ee99",
              }}
            />
            <KpiCard
              icon={IconClock("text-amber-400")}
              iconBg="rgba(251,191,36,0.15)"
              accent="#fbbf24"
              value={formatDuration(summary.avgDuration)}
              label="Avg Duration"
            />
          </div>
        </div>

        {/* Row 2: Snapshot — three-way swap.
            Admin + project → Project Snapshot.
            Agent → My Pipeline.
            Admin no project → Agency Snapshot. */}
        {!isAgent && selectedProjectId ? (
          <div className="mt-3 rounded-xl bg-[#15333D] border border-[#2C4D5A] p-3 sm:p-4">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <div className="flex items-center gap-4 flex-wrap">
                <h3 className="text-[10px] font-semibold text-[#889DB2] uppercase tracking-widest">
                  Project Snapshot
                </h3>
                <div className="flex items-center gap-3 text-[10px] text-[#889DB2]">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="relative inline-flex w-2 h-2">
                      <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                      <span className="relative inline-flex rounded-full w-2 h-2 bg-emerald-400" />
                    </span>
                    <span>Active = at least 1 view in last 15d</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="relative inline-flex w-2 h-2">
                      <span className="absolute inline-flex w-full h-full rounded-full bg-rose-500 opacity-75 animate-ping" />
                      <span className="relative inline-flex rounded-full w-2 h-2 bg-rose-500" />
                    </span>
                    <span>Inactive = no view in last 15d</span>
                  </span>
                </div>
              </div>
              <span className="text-[10px] text-[#5A7C8C]">All-time</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
              <KpiCard
                icon={IconFolder("text-orange-400")}
                iconBg="rgba(251,146,60,0.15)"
                accent="#fb923c"
                value={
                  <span className="text-sm font-bold text-white block truncate">
                    {selectedProjectName || "Project"}
                  </span>
                }
                label="This Project"
                subtext={{
                  color: "#889DB2",
                  text: (
                    <div className="flex flex-col gap-1 mt-1.5 text-[10px]">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#818cf8] flex-shrink-0" />
                        <span className="text-white font-semibold tabular-nums">
                          {metaModelsByProject.get(selectedProjectId)?.length || 0}
                        </span>
                        <span className="text-[#889DB2]">Meta Models</span>
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#e879f9] flex-shrink-0" />
                        <span className="text-white font-semibold tabular-nums">
                          {matterportsByProject.get(selectedProjectId)?.length || 0}
                        </span>
                        <span className="text-[#889DB2]">Digital Twins</span>
                      </span>
                    </div>
                  ),
                }}
              />
              {(() => {
                const totalAgents = agentNameById.size;
                const totalLeads =
                  (assignedLeads ?? 0) + (unassignedLeads ?? 0);
                const agentInactive = Math.max(
                  0,
                  totalAgents - projectSnapshot.agentActive15d,
                );
                const leadInactive = Math.max(
                  0,
                  totalLeads - projectSnapshot.leadActive15d,
                );
                return (
                  <>
                    <KpiCard
                      icon={IconUsers("text-pink-400")}
                      iconBg="rgba(244,114,182,0.15)"
                      accent="#f472b6"
                      value={totalAgents.toLocaleString()}
                      label="Total Agents"
                    />
                    <KpiCard
                      icon={IconBolt("text-emerald-400")}
                      iconBg="rgba(52,211,153,0.15)"
                      accent="#34d399"
                      value={projectSnapshot.agentActive15d.toLocaleString()}
                      label="Active Agents (15d)"
                      trend="up"
                      share={{
                        percent: pct(projectSnapshot.agentActive15d, totalAgents),
                        color: "#34d399",
                        label: "Of all agents",
                      }}
                    />
                    <KpiCard
                      icon={IconUserMinus("text-rose-400")}
                      iconBg="rgba(251,113,133,0.15)"
                      accent="#fb7185"
                      value={agentInactive.toLocaleString()}
                      label="Inactive Agents (15d+)"
                      trend="down"
                      share={{
                        percent: pct(agentInactive, totalAgents),
                        color: "#fb7185",
                        label: "Of all agents",
                      }}
                    />
                    <KpiCard
                      icon={IconUserPlus("text-teal-400")}
                      iconBg="rgba(45,212,191,0.15)"
                      accent="#2dd4bf"
                      value={totalLeads.toLocaleString()}
                      label="Total Leads"
                      share={{
                        percent: pct(assignedLeads ?? 0, totalLeads),
                        color: "#2dd4bf",
                        label: `${assignedLeads ?? 0} assigned · ${unassignedLeads ?? 0} unassigned`,
                      }}
                    />
                    <KpiCard
                      icon={IconBolt("text-emerald-400")}
                      iconBg="rgba(52,211,153,0.15)"
                      accent="#34d399"
                      value={projectSnapshot.leadActive15d.toLocaleString()}
                      label="Active Leads (15d)"
                      trend="up"
                      share={{
                        percent: pct(projectSnapshot.leadActive15d, totalLeads),
                        color: "#34d399",
                        label: "Of all leads",
                      }}
                    />
                    <KpiCard
                      icon={IconUserMinus("text-rose-400")}
                      iconBg="rgba(251,113,133,0.15)"
                      accent="#fb7185"
                      value={leadInactive.toLocaleString()}
                      label="Inactive Leads (15d+)"
                      trend="down"
                      share={{
                        percent: pct(leadInactive, totalLeads),
                        color: "#fb7185",
                        label: "Of all leads",
                      }}
                    />
                    {/* Raw Visitor Views KPI removed — raw/anonymous views are
                        excluded entirely from Platform Analytics. */}
                  </>
                );
              })()}
            </div>
          </div>
        ) : null}
        {/* My Pipeline (agent) removed per founder — kept behind {false} so it
            is trivially restorable. */}
        {false && (
          <div className="mt-3 rounded-xl bg-[#15333D] border border-[#2C4D5A] p-3 sm:p-4">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <div className="flex items-center gap-4 flex-wrap">
                <h3 className="text-[10px] font-semibold text-[#889DB2] uppercase tracking-widest">
                  My Pipeline
                </h3>
                <div className="flex items-center gap-3 text-[10px] text-[#889DB2]">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="relative inline-flex w-2 h-2">
                      <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                      <span className="relative inline-flex rounded-full w-2 h-2 bg-emerald-400" />
                    </span>
                    <span>Active = at least 1 view in last 15d</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="relative inline-flex w-2 h-2">
                      <span className="absolute inline-flex w-full h-full rounded-full bg-rose-500 opacity-75 animate-ping" />
                      <span className="relative inline-flex rounded-full w-2 h-2 bg-rose-500" />
                    </span>
                    <span>Inactive = no view in last 15d</span>
                  </span>
                </div>
              </div>
              <span className="text-[10px] text-[#5A7C8C]">All-time</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <KpiCard
                icon={IconUserPlus("text-teal-400")}
                iconBg="rgba(45,212,191,0.15)"
                accent="#2dd4bf"
                value={agentPipeline.totalLeads.toLocaleString()}
                label="Total Leads"
              />
              <KpiCard
                icon={IconBolt("text-emerald-400")}
                iconBg="rgba(52,211,153,0.15)"
                accent="#34d399"
                value={agentPipeline.active15d.toLocaleString()}
                label="Active Leads (15d)"
                trend="up"
                share={{
                  percent: pct(agentPipeline.active15d, agentPipeline.totalLeads),
                  color: "#34d399",
                  label: "Of all leads",
                }}
              />
              <KpiCard
                icon={IconUserMinus("text-rose-400")}
                iconBg="rgba(251,113,133,0.15)"
                accent="#fb7185"
                value={Math.max(
                  0,
                  agentPipeline.totalLeads - agentPipeline.active15d,
                ).toLocaleString()}
                label="Inactive Leads (15d+)"
                trend="down"
                share={{
                  percent: pct(
                    Math.max(0, agentPipeline.totalLeads - agentPipeline.active15d),
                    agentPipeline.totalLeads,
                  ),
                  color: "#fb7185",
                  label: "Of all leads",
                }}
              />
              <KpiCard
                icon={IconEye("text-blue-400")}
                iconBg="rgba(74,158,255,0.15)"
                accent="#60a5fa"
                value={String(agentPipeline.avgSessionsPerLead)}
                label="Avg sessions / lead"
              />
            </div>
          </div>
        )}
        {/* Agency Snapshot removed per founder — the new Platform scope chips +
            per-agent filter + By User Profile now own the engagement story.
            Kept behind {false} so it's trivially restorable. */}
        {false && (
          <div className="mt-3 rounded-xl bg-[#15333D] border border-[#2C4D5A] p-3 sm:p-4">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <div className="flex items-center gap-4 flex-wrap">
                <h3 className="text-[10px] font-semibold text-[#889DB2] uppercase tracking-widest">
                  Agency Snapshot
                </h3>
                <div className="flex items-center gap-3 text-[10px] text-[#889DB2]">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="relative inline-flex w-2 h-2">
                      <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                      <span className="relative inline-flex rounded-full w-2 h-2 bg-emerald-400" />
                    </span>
                    <span>Active = at least 1 view in last 15d</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="relative inline-flex w-2 h-2">
                      <span className="absolute inline-flex w-full h-full rounded-full bg-rose-500 opacity-75 animate-ping" />
                      <span className="relative inline-flex rounded-full w-2 h-2 bg-rose-500" />
                    </span>
                    <span>Inactive = no view in last 15d</span>
                  </span>
                </div>
              </div>
              <span className="text-[10px] text-[#5A7C8C]">All-time</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
              <KpiCard
                icon={IconFolder("text-orange-400")}
                iconBg="rgba(251,146,60,0.15)"
                accent="#fb923c"
                value={String(allProjects.length)}
                label="Total Projects"
                subtext={{
                  color: "#889DB2",
                  text: (
                    <div className="flex flex-col gap-1 mt-1.5 text-[10px]">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#818cf8] flex-shrink-0" />
                        <span className="text-white font-semibold tabular-nums">
                          {metaModelNameById.size}
                        </span>
                        <span className="text-[#889DB2]">Meta Models</span>
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#e879f9] flex-shrink-0" />
                        <span className="text-white font-semibold tabular-nums">
                          {matterportNameBySid.size}
                        </span>
                        <span className="text-[#889DB2]">Digital Twins</span>
                      </span>
                    </div>
                  ),
                }}
              />
              <KpiCard
                icon={IconUsers("text-pink-400")}
                iconBg="rgba(244,114,182,0.15)"
                accent="#f472b6"
                value={String(snapAgentTotal)}
                label="Total Agents"
              />
              <KpiCard
                icon={IconBolt("text-emerald-400")}
                iconBg="rgba(52,211,153,0.15)"
                accent="#34d399"
                value={agentActivity.active15d.toLocaleString()}
                label="Active Agents (15d)"
                trend="up"
                share={{
                  percent: pct(agentActivity.active15d, snapAgentTotal),
                  color: "#34d399",
                  label: "Of all agents",
                }}
              />
              <KpiCard
                icon={IconUserMinus("text-rose-400")}
                iconBg="rgba(251,113,133,0.15)"
                accent="#fb7185"
                value={Math.max(
                  0,
                  snapAgentTotal - agentActivity.active15d,
                ).toLocaleString()}
                label="Inactive Agents (15d+)"
                trend="down"
                share={{
                  percent: pct(
                    Math.max(0, snapAgentTotal - agentActivity.active15d),
                    snapAgentTotal,
                  ),
                  color: "#fb7185",
                  label: "Of all agents",
                }}
              />
              <KpiCard
                icon={IconUserPlus("text-teal-400")}
                iconBg="rgba(45,212,191,0.15)"
                accent="#2dd4bf"
                value={snapLeadTotal.toLocaleString()}
                label="Total Leads"
              />
              <KpiCard
                icon={IconBolt("text-emerald-400")}
                iconBg="rgba(52,211,153,0.15)"
                accent="#34d399"
                value={agencyPipeline.active15d.toLocaleString()}
                label="Active Leads (15d)"
                trend="up"
                share={{
                  percent: pct(agencyPipeline.active15d, snapLeadTotal),
                  color: "#34d399",
                  label: "Of all leads",
                }}
              />
              <KpiCard
                icon={IconUserMinus("text-rose-400")}
                iconBg="rgba(251,113,133,0.15)"
                accent="#fb7185"
                value={Math.max(
                  0,
                  snapLeadTotal - agencyPipeline.active15d,
                ).toLocaleString()}
                label="Inactive Leads (15d+)"
                trend="down"
                share={{
                  percent: pct(
                    Math.max(0, snapLeadTotal - agencyPipeline.active15d),
                    snapLeadTotal,
                  ),
                  color: "#fb7185",
                  label: "Of all leads",
                }}
              />
              {/* Raw Visitor Views KPI removed — raw/anonymous views are
                  excluded entirely from Platform Analytics. */}
            </div>
          </div>
        )}

        {/* Breakdowns: 3-col row of User Profile (or Lead Activity for
            agents) + City + Experience. */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-3">
          {isAgent ? (
            <ByAgentActivityPanel
              sessions={filteredSessions}
              agentInternalId={agentInternalId}
              agentLeadIds={agentLeadIds}
              leadContactById={customerContactById}
            />
          ) : (
            <ByUserProfilePanel
              sessions={filteredSessions}
              contactsByRole={{
                business_admin: businessAdminContactById,
                system_admin: systemAdminContactById,
                agent: agentContactById,
                customer: customerContactById,
                raw_visitor: new Map(),
              }}
              viewerRole={isASA ? "asa" : "aba"}
              selfId={isASA ? selfAsaId || "" : selfAbaId || ""}
              scope={platformScope}
            />
          )}
          <ByCityProjectPanel sessions={filteredSessions} />
          <ByExperiencePanel
            sessions={filteredSessions}
            // When a project is picked (admin or agent), use that project's
            // capability. Otherwise keep default true and let empty rows
            // show — agency-wide there may be other projects with meta
            // models even if any one is bare.
            metaModelConfigured={
              selectedProjectId ? selectedHasMetaModel : true
            }
          />
        </div>

        {/* Locations map — pins visitor counts per city below the
            breakdown panels. */}
        <div className="mt-3">
          <LocationsMap
            sessions={filteredSessions}
            totalViews={summary.totalViews}
            projectNameById={projectNameById}
            businessAdminById={businessAdminById}
            systemAdminById={systemAdminById}
            agentNameById={agentNameById}
          />
        </div>

        <SessionsTable
          sessions={sessionsPageItems}
          projectNameById={projectNameById}
          businessAdminById={businessAdminById}
          systemAdminById={systemAdminById}
          agentNameById={agentNameById}
          matterportNameBySid={matterportNameBySid}
          metaModelNameById={metaModelNameById}
          loading={sessionsLoading}
          page={sessionsPage}
          pageSize={SESSIONS_PAGE_SIZE}
          total={sessionsTotal}
          onPrev={() => setSessionsPage((p) => Math.max(0, p - 1))}
          onNext={() => setSessionsPage((p) => p + 1)}
          onDownload={handleDownloadCsv}
          csvBusy={csvBusy}
        />
            </>
          )}

          {section === "guided" && (
            <SectionPending
              title={SECTION_COPY.guided.title}
              blurb={SECTION_COPY.guided.blurb}
              needs={[
                "Design — layout and which numbers lead.",
                "An agency-level rooms endpoint. Today the backend can list rooms for one host (views/guided_room/host/{host_id}); there is nothing that aggregates across an agency.",
                "Attendance and duration on the room record — who joined, by role, and how long the tour actually ran.",
              ]}
            />
          )}

          {section === "embed" && (
            <EmbedAnalytics
              active={section === "embed"}
              refreshTick={refreshTick}
              dateRange={dateRange}
              selectedProjectId={selectedProjectId}
              projects={allProjects}
              sessions={allAgencySessions}
              customers={allCustomers}
              projectNameById={projectNameById}
              matterportNameBySid={matterportNameBySid}
              metaModelNameById={metaModelNameById}
              projectsFailed={projectsFailed}
            />
          )}

          {/* Kept mounted (just hidden) when on Platform so its data persists
              across tab switches — no refetch on toggle. Fetches lazily only
              once `active` becomes true (see ShareAnalytics). */}
          <div className={section === "share" ? "" : "hidden"}>
            <ShareAnalytics
              active={section === "share"}
              onLoadingChange={setShareLoading}
              selectedProjectId={selectedProjectId}
              agencySlug={slug || ""}
              onOpenProjectPage={() => {
                // Mirror ProjectList navigation: client-side route (smooth,
                // shows ProjectDetailsV3's EntryLoader) with state so the
                // loader has real text from frame 1. Segment falls back to _id.
                const p: any = allProjects.find(
                  (x: any) => x?._id === selectedProjectId,
                );
                if (!p) return;
                const seg = p.slug || p._id;
                const lm = p?.address?.landmark || "";
                const cy = p?.address?.city || "";
                const locationStr = [lm, cy].filter(Boolean).join(", ");
                navigate(`/c/${slug || ""}/p/${seg}`, {
                  state: {
                    projectId: p._id,
                    projectName: p.project_name || p.name || "",
                    locationStr,
                  },
                });
              }}
              projectName={selectedProjectId ? selectedProjectName : undefined}
              dateRange={dateRange}
              refreshTick={refreshTick}
              agencySessions={allAgencySessions}
              businessAdminById={businessAdminById}
              systemAdminById={systemAdminById}
              agentNameById={agentNameById}
              nameByEmail={nameByEmail}
              roleByEmailDir={roleByEmailDir}
              customerContactById={customerContactById}
              projectNameById={projectNameById}
              ownCustomerEmails={ownCustomerEmails}
              myAgentEmails={myAgentEmails}
              myAgentCustomerEmails={myAgentCustomerEmails}
            />
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsV3;
export { AnalyticsV3 };
